import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { 
  Lock, 
  Unlock, 
  Mail, 
  ArrowRight, 
  TrendingUp, 
  Users, 
  Droplets, 
  Package, 
  Code, 
  Building2,
  DollarSign,
  Target,
  ChevronRight,
  ChevronDown,
  FileText,
  Download,
  CheckCircle2,
  XCircle,
  Award,
  Briefcase,
  MapPin,
  Calendar,
  BarChart3,
  Zap,
  Globe,
  Shield,
  Sparkles,
  Eye,
  Star,
  Heart,
  Percent,
  UserCheck,
  Building,
  Hospital,
  ShieldCheck,
  Lightbulb,
  Rocket,
  AlertTriangle,
  AlertCircle,
  Link2,
  Clock,
  TrendingDown,
  HelpCircle,
  MessageSquare,
  Quote,
  Play,
  ExternalLink,
  Send,
  PieChart,
  Handshake,
  Timer,
  CircleDollarSign,
  Phone,
  Wrench,
  Layers,
  Flag,
  FileCheck,
  Calculator,
  Brain,
  Store,
  Truck,
  Home,
  Landmark,
  Megaphone,
  RefreshCw,
  Search
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { ShareButtons } from "@/components/ShareButtons";
import EnhancedDemoPresentation from "@/components/EnhancedDemoPresentation";
import InvestorPackagePDF, { getInvestorPackageFileName } from "@/components/InvestorPackagePDF";
import { PDFDownloadLink } from "@react-pdf/renderer";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

// George Broughton Image
const GEORGE_IMAGE = "https://customer-assets.emergentagent.com/job_service-widgets/artifacts/n8vnfz06_Gemini_Generated_Image_ntzefxntzefxntze%20%281%29.png";

// Digital Isaac - AI Healthcare Assistant
const DIGITAL_ISAAC = "https://static.prod-images.emergentagent.com/jobs/0bc119bf-ae15-43e9-9f13-14b53e074c94/images/fe24d5e280d09d6a7d0c6d06f6b149605eaa9a0fa1be0b67e18ee25456bf0b6f.png";
const ASKISAAC_LOGO = "/logos/askisaac-b-monogram.svg";
const STERILEMATE_LOGO = "https://customer-assets-cm19k8pv.emergentagent.net/job_831e67a6-d823-415e-9bc6-ce18d6b738ca/artifacts/sxm1cszk_Sterile%20Mate%20Logo%20PNG.webp";
const STERILEMATE_LOGO_WHITE = "https://customer-assets-cm19k8pv.emergentagent.net/job_831e67a6-d823-415e-9bc6-ce18d6b738ca/artifacts/73gmnnia_Sterile%20Mate%20Logo%20White%20%281%29.webp";

// Company Info
const COMPANY_ADDRESS = "396 W Greens Road, Suite 1139, Houston, TX 77067";

// Approved investor emails (in production, this would be server-side)
const APPROVED_EMAILS = [
  "george.broughton@sterilemate.com",
  "investor@sterilemate.com",
  "amit@sterilemate.com",
  "admin@sterilemate.com"
];

// Capabilities PDFs
const capabilitiesDocs = [
  {
    name: "2025 Capabilities Brochure",
    description: "Full company capabilities and services overview",
    url: "https://customer-assets.emergentagent.com/job_service-widgets/artifacts/std19ze8_2025%20Capabilities%20Brochure%201.12.2025.pdf"
  },
  {
    name: "Healthcare Software Capabilities",
    description: "Software solutions and technology partnerships",
    url: "https://customer-assets.emergentagent.com/job_service-widgets/artifacts/yzqcqgjk_2025%20Healthcare%20Software%20Capabilities%20Brochure11.11.2025.pdf"
  },
  {
    name: "Healthcare Water Purification Systems",
    description: "Water purification and solutions division",
    url: "https://customer-assets.emergentagent.com/job_supply-backend/artifacts/iknj4per_Sterile_Mate_Sai_Water_Healthcare_Water_Purification_Systems_2025.pdf"
  }
];

// Case Studies - Enterprise Validation
const caseStudies = [
  {
    name: "Major US Public Health System",
    description: "One of the largest public health systems in the United States - Live beta deployment",
    highlight: "ENTERPRISE VALIDATION",
    contracts: "Live Platform Beta + 3 Water Contracts",
    color: "from-blue-900 to-blue-900",
    featured: true
  },
  {
    name: "National Integrated Healthcare System",
    description: "The nation's largest integrated health care system partnership",
    highlight: "Federal Healthcare",
    contracts: "Water Purification Systems",
    color: "from-blue-900 to-blue-900"
  },
  {
    name: "Regional Federal Medical Center",
    description: "Regional federal healthcare facility",
    highlight: "Federal Healthcare",
    contracts: "Water Purification Systems",
    color: "from-blue-900 to-blue-900"
  },
  {
    name: "Leading Pediatric Institution",
    description: "Leading pediatric healthcare institution",
    highlight: "Pediatric Care",
    contracts: "Healthcare Solutions",
    color: "from-blue-900 to-blue-900"
  },
  {
    name: "State of Texas",
    description: "Government contract award",
    highlight: "Awarded Bid",
    contracts: "1 Reverse Osmosis System",
    color: "from-blue-900 to-blue-900"
  }
];

// Roadmap milestones - SterileMate LLC Journey
const roadmapData = [
  {
    year: "2020",
    title: "SterileMate Founded",
    description: "Founded during COVID-19 crisis, fulfilling urgent hospital supply needs from George's living room",
    revenue: "$0",
    icon: Building2,
    color: "from-blue-900 to-blue-900"
  },
  {
    year: "2021",
    title: "Rapid Growth",
    description: "Contract fulfillment and rapid backorder sourcing for Texas Medical Center hospitals",
    revenue: "$800K",
    icon: TrendingUp,
    color: "from-blue-900 to-blue-900"
  },
  {
    year: "2022",
    title: "Water Purification Division",
    description: "Launched healthcare water purification solutions for Sterile Processing and Dialysis",
    revenue: "$700K",
    icon: Droplets,
    color: "from-blue-900 to-blue-900"
  },
  {
    year: "2023",
    title: "Physical Expansion",
    description: "Opened distribution center, 4 major water purification installations, VA contracts",
    revenue: "$600K",
    icon: Briefcase,
    color: "from-blue-900 to-blue-900"
  },
  {
    year: "2024",
    title: "Major Contracts",
    description: "Major health system projects, VA healthcare expansion, AskIsaac platform development begins",
    revenue: "$1.1M",
    icon: Users,
    color: "from-blue-900 to-blue-900"
  },
  {
    year: "2025",
    title: "Platform Launch",
    description: "AskIsaac platform launches — automating Sterile Mate's 6-year backorder sourcing business (the door-opener into every Texas hospital system) for regional and statewide scale. Major hospital capital projects secured.",
    revenue: "$1.5M+",
    icon: Zap,
    color: "from-blue-900 to-blue-900",
    current: true
  },
  {
    year: "2026",
    title: "HQ Acquisition + Three-Pillar Launch",
    description: "SBA 7(a) acquisition of the executive HQ. Phase I: B2B headquarters with showroom, training center, and logistics hub. Expanded backorder sourcing goes fully automated via AskIsaac, water-purification and healthcare-construction sales teams scale. $10K/mo marketing engine activated.",
    revenue: "$2.5M–$3.2M",
    icon: Landmark,
    color: "from-blue-900 to-blue-900",
    forecast: true
  },
  {
    year: "2027",
    title: "Plan C Y1 — SBA 504 HQ Live + Automated Backorder Scale-Up",
    description: "SBA 504 close on 16651 Ronan Rd HQ. AskIsaac fully automates the 6-year backorder RFQ business — the flagship line jumps from ~$300K historical to $1.2M Y1 as we extend from Texas Medical Center into regional Texas hospital systems. Water/Devices/Construction/Consumables all in market.",
    revenue: "$4.54M",
    icon: Home,
    color: "from-blue-900 to-blue-900",
    future: true,
    forecast: true
  },
  {
    year: "2028",
    title: "Regional Healthcare Platform Leader",
    description: "Automated Backorder Sourcing scales to $2.1M as the AskIsaac RFQ engine extends regionally. Full 7-stream mix operational; DSCR 6.71x on the SBA 504 stack. Real estate asset appreciation.",
    revenue: "$7.59M",
    icon: Award,
    color: "from-blue-900 to-blue-900",
    future: true,
    forecast: true
  }
];

// Major Active Projects Pipeline - 2026
const majorProjects = [
  {
    name: "Harris Health — Water Softening Systems",
    description: "Facility-wide water softener system installations for Harris Health System hospitals",
    value: "$791K",
    timeline: "2026",
    status: "In Progress",
    client: "Harris Health System"
  },
  {
    name: "Harris Health — Dialysis Water Purification",
    description: "Complete dialysis water purification system for new Harris Health System hospital facility",
    value: "$391K",
    timeline: "2026",
    status: "In Progress",
    client: "Harris Health System"
  },
  {
    name: "Automated Backorder Sourcing — Statewide Rollout",
    description: "AskIsaac automates the 6-year backorder RFQ workflow that has been Sterile Mate's benchmark and door-opener into every Texas hospital system. Scaling from $1.2M Y1 to $3.2M Y3 as we extend regionally and statewide.",
    value: "$1.2M → $3.2M",
    timeline: "2027 – 2029",
    status: "Automated / Scaling",
    client: "Texas Hospital Systems → Regional → Statewide"
  }
];

// Strategic growth areas
const growthAreas = [
  {
    title: "Water Purification Capital Projects",
    description: "Custom-designed RO/DI systems, AAMI ST108 compliance, saving hospitals $500K+ vs competitors",
    icon: Droplets,
    revenue: "$2.4M",
    percentage: "29%",
    color: "from-blue-900 to-blue-900"
  },
  {
    title: "Janitorial Manufacturing & EVS",
    description: "Branded products, hardware systems, and ATP technology",
    icon: Package,
    revenue: "$1.2M",
    percentage: "15%",
    color: "from-blue-900 to-blue-900"
  },
  {
    title: "Bleu Flux Alkaline Water",
    description: "Premium alkaline water brand launching 2026 with Bleu Flux Beverage Co",
    icon: Droplets,
    revenue: "$600K",
    percentage: "8%",
    color: "from-blue-900 to-blue-900"
  },
  {
    title: "Automated Backorder Sourcing (AskIsaac)",
    description: "Sterile Mate's 6-year benchmark and door-opener into every Texas hospital system — now fully automated via the AskIsaac RFQ engine. Primary revenue engine scaling from $1.2M Y1 to $5.0M Y5 as we extend regionally and statewide.",
    icon: Zap,
    revenue: "$1.2M → $5.0M",
    percentage: "Flagship",
    color: "from-blue-900 to-blue-900"
  }
];

// Financial projections — Plan C canonical (matches Excel pro-forma /api/investor/plan-c/proforma.xlsx)
const financialProjections = [
  { year: "2027 (Y1)", revenue: "$4,541,860", expenses: "$3,593,671", netIncome: "$948,189" },
  { year: "2028 (Y2)", revenue: "$7,585,776", expenses: "$5,219,592", netIncome: "$2,366,184" },
  { year: "2029 (Y3)", revenue: "$11,641,336", expenses: "$7,386,740", netIncome: "$4,254,596" }
];

// Strategic partnerships
const partnerships = [
  { name: "Intco Global", focus: "Nitrile Exam Gloves — 3.5-mil + 4.0-mil private-label direct-import (Haiphong, Vietnam)" },
  { name: "Texas Microfiber", focus: "Private-Label Disposable Microfiber Cloths — branded 50-count pop-up boxes (Blue \u00b7 White \u00b7 Black)" },
  { name: "Filmop USA", focus: "Janitorial Hardware & EVS Manufacturing" },
  { name: "Empire Bio Diagnostics", focus: "ATP Testing & Borescopes (BioBurden Cleaning verification)" }
];

// Use of funds breakdown — SBA 7(a) Capital Deployment (Plan A · Humble · $3.415M)
const useOfFunds = [
  { category: "Building Acquisition", amount: "$2,100,000", percentage: 61, description: "10,766 SF freestanding commercial building at 20121 Highway 59 N, Humble, TX — adjacent to Deerbrook Mall" },
  { category: "Renovations & Buildout", amount: "$150,000", percentage: 4, description: "Showroom build-out (Water Purification + EVS), retail space for The Supply Doctor, training center, warehouse" },
  { category: "Equipment & Fixtures", amount: "$115,000", percentage: 3, description: "Display systems, training equipment, DME showroom fittings, point-of-sale, signage" },
  { category: "Initial Inventory", amount: "$100,000", percentage: 3, description: "Water purification components, EVS products, DME inventory (Vive Health, MHC Medical), retail medical supplies" },
  { category: "Working Capital (12 months)", amount: "$950,000", percentage: 28, description: "Personnel ($570,025 for 8 FTE + 1 outsourced developer), $120K annual marketing budget (Google Ads, billboards, trade shows), insurance, utilities, operations" }
];

// Use of funds breakdown — SBA 7(a) Capital Deployment (Plan B · 263 N Sam Houston / 2SIXTY3 · $4.7M)
const useOfFundsPlanB = [
  { category: "Building Acquisition", amount: "$3,400,000", percentage: 72, description: "87,282 SF Class-B \u201C2SIXTY3\u201D office campus at 263 N Sam Houston Pkwy E, Houston, TX — built 1978 / renovated 2023, 3 stories, 213 parking spaces" },
  { category: "Renovation & Capital Improvements", amount: "$800,000", percentage: 17, description: "HQ floor reconfiguration, AskIsaac engineering suite, training center, showroom build-out, prominent building signage, multi-tenant lease-up improvements" },
  { category: "Operating Capital & Working Reserves", amount: "$500,000", percentage: 11, description: "12-month operating reserve, leasing-up costs and tenant improvement allowance for the surplus floor area generating ancillary rental income" }
];

// Use of funds breakdown — SBA 504 Capital Deployment (Plan C · 16651 Ronan Rd · $4.49M)
// 22,750 SF Class-A 3-story office + 5.08 acres in Greenspoint. Sterile Mate occupies
// the full building — no third-party tenant. Financed via SBA 504 (fixed-rate, 25-yr).
const useOfFundsPlanC = [
  { category: "Building & Land Acquisition", amount: "$3,500,000", percentage: 78.0, description: "22,750 SF Class-A 3-story office building on 5.08 acres at 16651 Ronan Rd, Houston, TX 77060 — Greenspoint corridor with direct Beltway 8 + I-45 access. Includes a 2.84-acre platted expansion pad reserved for a future Distribution Center / Medical Device Packaging Center, plus 2.23 acres of flex / fleet-parking land — no additional real estate required to scale." },
  { category: "Renovation & Capital Improvements", amount: "$160,000", percentage: 3.6, description: "Formal renovation program engaging BWS (accredited architectural firm) plus MEP consultants for the 2nd-level executive layout, Texas Accessibility (TAS/RAS) review, and construction visitation.", detail: [
    "2nd Level Estimate & Layout — $102,000",
    "Schematic Design — $6,875",
    "Design Development — $9,750",
    "Construction Documents — $13,875",
    "MEP Consultant Fees — $25,000",
    "MEP Coordination Fee — $2,500",
    "RAS Fees for TAS review — TBD (not to exceed $1,200)",
    "Construction Visitation — TBD (~5 hrs/wk)",
    "Total BWS contract — $58,000 (excluding RAS Fee & Construction Visitation)",
  ] },
  { category: "Statewide B2B Marketing Launch", amount: "$180,000", percentage: 4.0, description: "Sterile Mate's first dedicated marketing budget — a Texas-wide B2B campaign across digital advertising, healthcare event sponsorships, and trade-show participation (medical expos, AAMI conferences, GPO conventions). Direct ROI investment: every dollar funds pipeline generation against the LBJ / Ben Taub / John M. O'Quinn / Parkland / DFW market expansion." },
  { category: "Initial Consumables Inventory (Gloves + Microfiber)", amount: "$150,000", percentage: 3.3, description: "Seed the Consumables Division from Day 1: 1 container of 4-mil nitrile exam gloves (~$58,237), 1 container of 3.5-mil nitrile exam gloves (~$58,403), plus 3,000 microfiber pop-up boxes (1,000 Blue + 1,000 White + 1,000 Black @ $11.24 delivered = $33,720). Total working inventory $150,360 rounded to $150K." },
  { category: "Operating Capital & Working Reserves", amount: "$500,000", percentage: 11.1, description: "Twelve-month operating reserve — structured as a separate revolving line of credit. Funds fit-out cash flow, payroll runway, and integrated B2B operations while the Consumables Division ramps." },
];

// Competitive advantages
const competitiveAdvantages = [
  {
    title: "Front-Line Command System",
    description: "This isn't a CRM. It's not a ticketing system. It's the on-demand command network that deploys when hospital supply chains and infrastructure fail—giving front-line workers the tools to be successful and efficient when it counts most.",
    icon: Zap,
    savings: "30 seconds to response"
  },
  {
    title: "Hospital-Grade Security Suite",
    description: "Zero-Knowledge password architecture (bcrypt hashed, never visible to admins), MFA with TOTP, 30-minute session timeouts, WORM audit logs, and PHI detection. IT Director approved.",
    icon: Lock,
    savings: "Enterprise Security"
  },
  {
    title: "Isaac Bot — Everywhere",
    description: "Telegram @askisaac_bot + Web Chat on desktop. Full lifecycle demo simulations (Maintenance & Backorder journeys), real-time alerts, and AI-powered assistance. Staff choose their preferred interface.",
    icon: MessageSquare,
    savings: "Omni-Channel Access"
  },
  {
    title: "Enterprise Validated",
    description: "One of the largest public health systems in the United States is actively using AskIsaac in live beta. This isn't a demo, it's production deployment at enterprise scale.",
    icon: Hospital,
    savings: "Proven at Scale"
  },
  {
    title: "Two-Sided Marketplace",
    description: "Network effects: More hospitals = more suppliers want in. More suppliers = better service for hospitals. We don't just track vendors—we DEPLOY them. Pre-vetted local contractors who can act TODAY.",
    icon: Users,
    savings: "Network Effects"
  },
  {
    title: "Mobile-First Field Execution",
    description: "A nurse texts Isaac from the OR floor. Done in 30 seconds. No desktop login, no ticket forms, no IT department needed. Front-line workers make decisions that count in the moment.",
    icon: Phone,
    savings: "Meet staff where they work"
  },
  {
    title: "The Dedicated Engine",
    description: "When a front-line worker asks Isaac for help, the engine knows what they need and orchestrates the response: auto-routes to vetted contractors, triggers compliance, notifies all stakeholders.",
    icon: Layers,
    savings: "Everyone Under One Hood"
  },
  {
    title: "Configurable Compliance Framework",
    description: "Not just AAMI ST108 — hospitals define ANY compliance measure for ANY department. Sterile processing, HVAC, plumbing, pharmacy, lab. 3-quote rules, audit trails—handled automatically.",
    icon: ShieldCheck,
    savings: "Universal Compliance Engine"
  },
  {
    title: "QuickBid Exchange",
    description: "Bypass the 3-quote bottleneck. Instant matching with pre-vetted, credentialed local contractors who can act today, not in weeks. Texas compliance ($2,500+) built-in.",
    icon: Award,
    savings: "Hours, not weeks"
  },
  {
    title: "Built by Operators, Not Outsiders",
    description: "6 years in the Texas Medical Center trenches. Built by people who've actually fixed critical water systems while surgeries waited. This isn't Silicon Valley guessing what hospitals need.",
    icon: Wrench,
    savings: "We lived it"
  }
];

// Testimonials
const testimonials = [
  {
    quote: "AskIsaac is transforming how we manage facility operations. The Telegram integration means our staff can report issues and track progress without leaving the floor. This is the future of hospital operations software.",
    author: "Major Public Health System",
    role: "Facility Operations - Live Beta",
    highlight: "One of the Largest Public Health Systems in the US"
  },
  {
    quote: "Sterile Mate has been instrumental in upgrading our water purification systems. Their custom approach saved us significant capital while exceeding our quality requirements.",
    author: "Major Public Health System",
    role: "Sterile Processing Department",
    highlight: "3 Contracts & Counting"
  },
  {
    quote: "The team's understanding of federal healthcare requirements and their ability to deliver compliant solutions on time has made them our go-to partner for water purification projects.",
    author: "National Integrated Healthcare System",
    role: "Facilities Management",
    highlight: "Nationwide Partnership"
  }
];

// Pipeline / Warm Leads - Year 1 Customer Acquisition Strategy
const pipelineData = {
  summary: "Year 1 customers come from existing Sterile Mate LLC relationships in the Texas Medical Center — no cold outreach required.",
  keyAdvantage: "We are already approved vendors with these organizations, dramatically shortening the sales cycle from 180+ days to ~90 days.",
  warmLeads: [
    {
      name: "Texas Medical Center Health Systems",
      status: "Active Vendor",
      relationship: "Capital equipment installations, ongoing service contracts",
      software: "AskIsaac Backorder RFQ portal in active use for shortage sourcing",
      probability: "85%+",
      notes: "Decision makers know us by name. Trust established over 6 years."
    },
    {
      name: "Major Public Health System",
      status: "Live Beta",
      relationship: "AskIsaac platform in production use",
      software: "Full platform deployment",
      probability: "95%",
      notes: "Converting beta to paid contract. Already validating ROI."
    },
    {
      name: "VA Healthcare System",
      status: "Active Pipeline",
      relationship: "Existing SDVOSB contracts",
      software: "Water purification + facility services",
      probability: "70%",
      notes: "Federal procurement pathway established."
    },
    {
      name: "Regional Hospital Networks",
      status: "Warm Introductions",
      relationship: "Referrals from TMC relationships",
      software: "Evaluating full suite",
      probability: "50%",
      notes: "Second-degree connections through satisfied customers."
    }
  ],
  salesCycleAdvantage: "Traditional healthcare software sales: 180-365 days. Our sales cycle: 90 days. Why? We're not strangers—we're the team they already call when equipment breaks."
};

// Competitive Moat - Why Competitors Can't Replicate
const competitiveMoat = [
  {
    moat: "6 Years of Embedded Relationships",
    description: "We've been in the Texas Medical Center trenches since 2020. These aren't leads—they're colleagues who've watched us deliver under pressure. A competitor would need 5+ years just to get the first meeting.",
    icon: "handshake",
    defensibility: "High"
  },
  {
    moat: "Integrated Software Ecosystem",
    description: "AskIsaac has already digitized the exact backorder-sourcing workflow hospital materials managers rely on us for. Six years of pricing data, supplier relationships, and hospital-specific SKU maps live inside the platform. A competitor would need to rebuild both the software AND the six-year benchmark relationships to catch up.",
    icon: "layers",
    defensibility: "High"
  },
  {
    moat: "Hospital Trust Takes Years to Build",
    description: "Healthcare IT decisions require vendor vetting, compliance audits, security reviews, and board approvals. We've already passed these gates. A new entrant starts at zero.",
    icon: "shield",
    defensibility: "Very High"
  },
  {
    moat: "Two-Sided Marketplace Network Effects",
    description: "Every hospital we add makes the platform more valuable to suppliers. Every supplier we add makes it more valuable to hospitals. Competitors can't just 'copy the code'—they'd need both sides of the network.",
    icon: "network",
    defensibility: "Very High"
  },
  {
    moat: "HIPAA & Compliance Expertise",
    description: "Healthcare compliance isn't a feature—it's a foundation. Our zero-knowledge architecture, WORM audit logs, and PHI detection were built by people who understand what 'audit-ready' actually means.",
    icon: "lock",
    defensibility: "High"
  },
  {
    moat: "Founder-Market Fit",
    description: "George isn't just the CEO — he built this company during the pandemic, supplying hospitals on the front lines. This isn't a market opportunity to him. It's personal. Competitors can copy features; they can't copy authenticity.",
    icon: "heart",
    defensibility: "Unmatched"
  }
];

// FAQ Data
const faqData = [
  {
    question: "What is the investment amount and structure?",
    answer: "The total project cost is $3,415,000 via SBA 7(a) financing, covering building acquisition ($2.1M), renovations ($150K), equipment ($115K), inventory ($100K), and 12-month working capital ($950K including $120K marketing budget). Equity injection is approximately $340K (10%)."
  },
  {
    question: "What is the expected timeline to profitability?",
    answer: "We are already profitable. With this investment, we project hitting 500+ active supplier nodes by Q4 2026 and targeting a Series A at $30M+ valuation in 2027."
  },
  {
    question: "How will my investment be protected?",
    answer: "Investors receive equity stake with board observer rights, quarterly financial reports, and direct access to leadership. We operate with full transparency."
  },
  {
    question: "What makes AskIsaac different from competitors?",
    answer: "AskIsaac is not a CRM or ticketing system. It's a front-line command network that deploys when critical environments need help. We built a two-sided marketplace with network effects—more hospitals means more suppliers want in. Our 30-second response time (text from the floor → problem solved) is something enterprise software can't match."
  },
  {
    question: "Why should I believe you can execute?",
    answer: "We're operators, not outsiders. 6 years of trust in the Texas Medical Center. We've fixed critical water systems while surgeries waited. One of the largest public health systems in the U.S. is using AskIsaac TODAY. This isn't a pitch deck—it's a working platform with real revenue."
  },
  {
    question: "What is the exit strategy?",
    answer: "Multiple exit paths: strategic acquisition by healthcare/tech companies (Salesforce Health Cloud, Microsoft Azure Health) at $30M+ valuation in 2027, private equity rollup, or continued growth with recurring SaaS revenue."
  }
];

// Risk factors
const riskFactors = [
  {
    risk: "Market Competition",
    description: "Large national distributors may enter our niche markets",
    mitigation: "Exclusive partnerships and custom solutions create barriers to entry"
  },
  {
    risk: "Key Person Dependency",
    description: "Business relies heavily on founder relationships",
    mitigation: "Building sales team and systematizing processes with investment"
  },
  {
    risk: "Partnership Concentration",
    description: "Revenue dependent on key supplier partnerships",
    mitigation: "Diversifying across 4 strategic partners and adding new verticals"
  },
  {
    risk: "Healthcare Regulatory Changes",
    description: "Compliance requirements may shift",
    mitigation: "Strong relationships with VA and hospital compliance teams"
  }
];

// Deal structure
const dealStructure = {
  askAmount: "$3,415,000",
  equityOffered: "SBA 7(a) Loan",
  instrumentType: "SBA 7(a) — Real Estate + Working Capital",
  boardRights: "Equity injection ~$340K (10%)",
  timeline: "Seeking to close Q2 2026"
};

// Exit strategy options
const exitStrategies = [
  {
    type: "Strategic Acquisition",
    timeline: "3-5 Years",
    description: "Healthcare distributors (McKesson, Henry Schein, Medline) actively acquiring specialized providers",
    multiple: "4-6x Revenue"
  },
  {
    type: "Private Equity Rollup",
    timeline: "4-6 Years",
    description: "PE firms consolidating healthcare services sector",
    multiple: "5-8x EBITDA"
  },
  {
    type: "Dividend Distributions",
    timeline: "Ongoing",
    description: "Strong margins enable profit distributions while growing",
    multiple: "8-12% Annual Yield"
  }
];

// Why now urgency points
const whyNowPoints = [
  {
    title: "Real Estate Opportunity",
    description: "10,766 SF freestanding building at $2.1M in prime Deerbrook Mall corridor — below asking price with immediate availability",
    icon: Landmark,
    urgency: "Available Now"
  },
  {
    title: "$791K Pipeline in 90 Days",
    description: "Active Harris Health System contracts ready to execute — revenue starts immediately post-acquisition",
    icon: TrendingUp,
    urgency: "Active"
  },
  {
    title: "Expanded Backorder Sourcing",
    description: "AskIsaac-automated national supplier network handles hospital RFQs end-to-end — 2-hour pricing turnaround becomes an on-demand revenue engine from Day 1",
    icon: Search,
    urgency: "Phase I"
  },
  {
    title: "DME Insurance Revenue",
    description: "Phase II introduces Medicare/private insurance billing — creating high-margin recurring revenue from DME products",
    icon: Home,
    urgency: "Phase II"
  }
];

// =============================================================================
// SBA LOAN SCENARIOS — toggle between alternative HQ acquisition plans.
// Admin can grant an investor access to either or both via investor.allowed_scenarios.
// =============================================================================
const SCENARIOS = {
  ronan: {
    id: "ronan",
    label: "Plan C — 16651 Ronan Rd HQ",
    short: "16651 Ronan Rd (Greenspoint)",
    address: "16651 Ronan Rd, Houston, TX 77060",
    sqft: "22,750 SF \u00b7 3 Stories \u00b7 Class-A Corporate Office \u00b7 5.08 Acres",
    asking: 3_500_000,
    renovation: 160_000,
    operating: 500_000,
    total: 4_490_000,
    // Loan / debt-service / DSCR — Plan C: SBA 504 on $3.84M (acquisition + reno + marketing); $500K operating LOC
    // Acquisition $3.5M + reno $160K + marketing $180K = $3,840,000 → SBA ~$3.456M @ 10.5% / 25-yr \u2248 $32,650/mo P&I
    askAmount: "$4,490,000",
    loanType: "504",
    sbaLoanLabel: "~$3.46M",
    sbaLoanAmount: "~$3,456,000",
    equityInjectionLabel: "~$384K",
    equityInjectionAmount: "~$384,000",
    equityPctNote: "Equity injection ~$384K (10%) + $500K LOC for operating capital",
    monthlyDebtSBA: "~$32,650",
    monthlyDebtTotal: "~$34,950",
    existingDebtMonthly: "$2,300/mo",
    // DSCR strengthened by seven diversified operating streams anchored by Backorder Sourcing flagship — see PrivateLabelGloves tab in the pro-forma
    dscrRangeY2: "6.64x",
    pdfFileLabel: "Plan C PDF",
    galleryLabel: "ACTUAL BUILDING PHOTOS \u00b7 16651 RONAN RD \u00b7 GREENSPOINT",
    listingUrl: "https://customer-assets.emergentagent.com/job_831e67a6-d823-415e-9bc6-ce18d6b738ca/artifacts/u4b738e0_Brochure%2016651%20Ronan.pdf",
    listingLabel: "Download full DEALCO brochure (PDF) →",
    asbuiltUrl: "https://customer-assets.emergentagent.com/job_831e67a6-d823-415e-9bc6-ce18d6b738ca/artifacts/m6tj2e9v_George-16651%20Ronan%20-%20AsBuilt%20Pages%20%28003%29.pdf",
    asbuiltLabel: "Download as-built floor plans (PDF) →",
    proformaUrl: "/api/investor/plan-c/proforma.xlsx",
    proformaLabel: "Download Plan C Pro-Forma (XLSX) →",
    rendering: "/images/ronan_building/cover_aerial_branded.jpg",
    gallery: [
      { src: "/images/ronan_building/exterior_main.jpg", caption: "16651 Ronan Rd — Class-A 3-story brick fa\u00e7ade", role: "hero" },
      { src: "/images/ronan_building/aerial_freeway.jpg", caption: "Direct Beltway 8 + I-45 access — corporate corridor" },
      { src: "/images/ronan_building/site_plan.jpg", caption: "5.08-acre site — 2.84 acres reserved for future Distribution / Medical Device Packaging Center" },
      { src: "/images/ronan_building/interior_lobby.jpg", caption: "Lobby & atrium — stone, fireplace, executive feel" },
      { src: "/images/ronan_building/interior_office.jpg", caption: "Interior office finish — wood floors, full-height glass" },
      { src: "/images/ronan_building/interior_balcony.jpg", caption: "Third-floor executive balcony — campus views" },
      { src: "/images/ronan_building/floor_1.jpg", caption: "Floor 1 plan — 10,438 SF — future Sterile Mate HQ + showroom" },
      { src: "/images/ronan_building/floor_2.jpg", caption: "Floor 2 plan — 12,640 SF — 3,500 SF short-stock warehouse + Texas expansion sales office" },
    ],
    facts: [
      { label: "Building Size", value: "22,750 SF" },
      { label: "Stories", value: "3" },
      { label: "Building Class", value: "A (Corporate HQ)" },
      { label: "Lot Size", value: "5.08 Acres" },
      { label: "Floor 1", value: "10,438 SF — Sterile Mate HQ + Showroom" },
      { label: "Floor 2", value: "12,640 SF — Warehouse (3,500 SF) + Sales Office" },
      { label: "Floor 3", value: "Executive Suite + Boardroom" },
      { label: "Expansion Pad", value: "2.84 Acres — Future Distribution Center" },
      { label: "Parking", value: "88 Available Spaces" },
      { label: "Access", value: "Beltway 8 \u00b7 I-45" },
      { label: "Submarket", value: "Greenspoint \u2022 North Houston" },
      { label: "Airport", value: "IAH — 8 minutes" },
    ],
    amenities: [
      "Class-A finish — brick exterior, executive lobby with fireplace",
      "Top-floor private executive suite + adjacent boardroom",
      "Multiple conference rooms (1st, 2nd, 3rd floor)",
      "Courtyard & landscaped patios with seating",
      "Two stairwells + elevator — ADA accessible",
      "Workout / wellness room on 2nd floor",
      "Lunch room + employee lounge",
      "Server room + IT room + A/V closet",
      "Existing single-tenant build-out — minimal demo required",
      "Floor 2: 3,500 SF short-stock warehouse (on-the-go items + janitorial disposables for daily hospital distribution) + Texas expansion sales office",
      "2.84 acres reserved for future Distribution Center / Medical Device Packaging Center",
      "Additional 2.23 acres for fleet parking or pad-site monetization",
      "Direct Beltway 8 frontage — ExxonMobil campus 1.5 mi north",
      "8 minutes to George Bush Intercontinental Airport (IAH)",
    ],
    headline: "Sterile Mate Executive HQ — 16651 Ronan Rd",
    sub:
      "Class-A 3-story office on 5.08 acres in the Greenspoint corporate corridor. Sterile Mate stands on its own legs — Floor 1 hosts the B2B HQ, showroom, and training center; Floor 2 houses a 3,500 SF short-stock warehouse plus the Texas expansion sales office (San Antonio, Dallas, Austin, El Paso, Fort Worth, Galveston, Waco); Floor 3 is the executive suite and boardroom.",
    breakdown: [
      { category: "Building & Land Acquisition", amount: 3_500_000, pct: 80.6, color: "blue" },
      { category: "Renovation & Capital Improvements", amount: 160_000, pct: 3.7, color: "emerald", detail: "2nd Level estimate & layout $102K \u00b7 Schematic Design $6,875 \u00b7 Design Development $9,750 \u00b7 Construction Documents $13,875 \u00b7 MEP Consultant Fees $25,000 \u00b7 MEP Coordination Fee $2,500 \u00b7 RAS Fees for TAS review TBD (not to exceed $1,200) \u00b7 Construction Visitation TBD (~5H/wk). Total BWS contract $58,000 excluding RAS Fee & Construction Visitation." },
      { category: "Statewide B2B Marketing Launch", amount: 180_000, pct: 4.1, color: "violet", detail: "Sterile Mate\u2019s first coordinated statewide launch — digital ads, medical-expo sponsorships, trade-show participation across TX metros." },
      { category: "Operating Capital & Working Reserves", amount: 500_000, pct: 11.5, color: "amber", detail: "Separate $500K SBA operating LOC for working capital and 12-month reserves." },
    ],
    // Plan C: no third-party anchor tenant — Sterile Mate stands on its own.
    secondaryTenant: null,
  },
};
const SCENARIO_DEFAULT = "ronan";



export default function InvestorPortalPage({ bypassGate = false, forceScenario = null }) {
  const navigate = useNavigate();
  const [isUnlocked, setIsUnlocked] = useState(bypassGate);
  const [email, setEmail] = useState(bypassGate ? "frost.bank@invited" : "");
  const [investorForm, setInvestorForm] = useState({
    fullName: "",
    email: "",
    company: "",
    role: "",
    investorType: "",
    howHeard: ""
  });
  const [loading, setLoading] = useState(false);
  const [activeSection, setActiveSection] = useState("executive");
  const [expandedFaq, setExpandedFaq] = useState(null);

  // ===== SBA Loan Scenario toggle (Plan A: Humble · Plan B: North Sam Houston) =====
  // Persisted across sessions; respects allowed_scenarios from investor record.
  const [scenarioId, setScenarioId] = useState(() => {
    if (typeof window === "undefined") return SCENARIO_DEFAULT;
    return window.localStorage.getItem("sm_active_scenario") || SCENARIO_DEFAULT;
  });
  // Plan A + Plan B have been retired. Only Plan C (Ronan Rd) remains.
  const allowedScenarios = ["ronan"];
  // If the saved scenario is not in the allowed list, fall back to the first allowed.
  const activeScenarioId = allowedScenarios.includes(scenarioId)
    ? scenarioId
    : allowedScenarios[0];
  const scenario = SCENARIOS[activeScenarioId] || SCENARIOS[SCENARIO_DEFAULT];
  const switchScenario = (id) => {
    if (!allowedScenarios.includes(id)) return;
    setScenarioId(id);
    try {
      window.localStorage.setItem("sm_active_scenario", id);
    } catch {
      /* ignore storage errors */
    }
  };
  const fmtUSD = (n) =>
    new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      maximumFractionDigits: 0,
    }).format(n);

  const [contactForm, setContactForm] = useState({ name: "", email: "", company: "", message: "" });
  const [formSubmitting, setFormSubmitting] = useState(false);
  const [showDemo, setShowDemo] = useState(false);

  useEffect(() => {
    // Check if already unlocked in this session
    const unlockedEmail = sessionStorage.getItem("investor_unlocked");
    if (unlockedEmail) {
      setIsUnlocked(true);
      setEmail(unlockedEmail);
    }
    // URL query-string bypass (e.g., admin click-through: /investor?bypass=1&scenario=ronan)
    try {
      const params = new URLSearchParams(window.location.search);
      const qpBypass = params.get("bypass");
      const qpScenario = params.get("scenario");
      if (qpBypass === "1" || qpBypass === "admin") {
        setIsUnlocked(true);
        setEmail(`admin@sterilemate.com`);
      }
      if (qpScenario && ["humble", "northsam", "ronan"].includes(qpScenario)) {
        window.localStorage.setItem("sm_active_scenario", qpScenario);
        setScenarioId(qpScenario);
      }
    } catch { /* ignore */ }
    // Prop-based bypass (e.g., dedicated /investor/frost-bank route).
    if (bypassGate) {
      setIsUnlocked(true);
      if (forceScenario) {
        try {
          window.localStorage.setItem("sm_active_scenario", forceScenario);
          setScenarioId(forceScenario);
        } catch { /* ignore */ }
      }
    }
  }, [bypassGate, forceScenario]);

  const handleUnlock = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    // Prepare investor data
    const investorData = {
      ...investorForm,
      accessedAt: new Date().toISOString(),
      userAgent: navigator.userAgent
    };
    
    try {
      // Send to backend API
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/investor-leads`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(investorData)
      });
      
      if (!response.ok) {
        throw new Error('Failed to submit');
      }
      
      const result = await response.json();
      console.log("Lead captured:", result);
      
      // Grant access
      const normalizedEmail = investorForm.email.toLowerCase().trim();
      setIsUnlocked(true);
      setEmail(normalizedEmail);
      sessionStorage.setItem("investor_unlocked", normalizedEmail);
      sessionStorage.setItem("investor_data", JSON.stringify(investorData));
      toast.success(`Welcome, ${investorForm.fullName}! Access granted.`);
      
    } catch (error) {
      console.error("Error submitting lead:", error);
      // Still allow access even if API fails
      const normalizedEmail = investorForm.email.toLowerCase().trim();
      setIsUnlocked(true);
      setEmail(normalizedEmail);
      sessionStorage.setItem("investor_unlocked", normalizedEmail);
      toast.success(`Welcome, ${investorForm.fullName}! Access granted.`);
    } finally {
      setLoading(false);
    }
  };

  const handleContactSubmit = (e) => {
    e.preventDefault();
    setFormSubmitting(true);
    setTimeout(() => {
      toast.success("Thank you! George will contact you within 24 hours.");
      setContactForm({ name: "", email: "", company: "", message: "" });
      setFormSubmitting(false);
    }, 1000);
  };

  // Presentation switcher component
  const PresentationSwitcher = () => (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" className="gap-2 bg-white/50 border-slate-600 hover:bg-slate-700 text-slate-900">
          <Layers className="w-4 h-4" />
          <span className="hidden sm:inline">Presentations</span>
          <ChevronDown className="w-4 h-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="bg-white border-slate-200">
        <DropdownMenuItem 
          onClick={() => navigate("/investor")}
          className="text-slate-900 hover:bg-slate-700 cursor-pointer"
        >
          <Briefcase className="w-4 h-4 mr-2 text-blue-900" />
          Investor Presentation
        </DropdownMenuItem>
        <DropdownMenuItem 
          onClick={() => navigate("/investor/one-pager")}
          className="text-slate-900 hover:bg-slate-700 cursor-pointer"
        >
          <FileText className="w-4 h-4 mr-2 text-blue-900" />
          Investor One-Pager
        </DropdownMenuItem>
        <DropdownMenuItem 
          onClick={() => navigate("/hospital-presentation")}
          className="text-slate-900 hover:bg-slate-700 cursor-pointer"
        >
          <Hospital className="w-4 h-4 mr-2 text-blue-900" />
          Hospital Beta Program
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );

  if (!isUnlocked) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-white via-slate-800 to-white flex items-center justify-center p-6">
        {/* Background effects */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl animate-pulse" />
          <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-cyan-500/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />
        </div>

        <div className="relative z-10 w-full max-w-lg">
          {/* Logo & Title */}
          <div className="flex flex-col items-center mb-6">
            <img src={STERILEMATE_LOGO} alt="Sterile Mate" className="h-24 w-auto max-w-full object-contain mb-4" />
            <h1 className="text-3xl font-bold text-slate-900 text-center mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Sterile Mate LLC &mdash; SBA Business Plan
            </h1>
            <p className="text-emerald-600 text-sm font-medium">SBA 7(a) Loan Application Package</p>
          </div>
          <p className="text-slate-600 text-center mb-6">
            Please provide your information to access our business plan materials
          </p>

          {/* Enhanced Registration Form */}
          <form onSubmit={handleUnlock} className="space-y-4 bg-white/50 border border-slate-200 rounded-2xl p-6">
            {/* Full Name */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">Full Name *</label>
              <div className="relative">
                <Users className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-600" />
                <Input
                  type="text"
                  placeholder="John Smith"
                  value={investorForm.fullName}
                  onChange={(e) => setInvestorForm({...investorForm, fullName: e.target.value})}
                  className="w-full pl-11 pr-4 py-3 bg-white border-slate-600 text-slate-900 placeholder:text-slate-500 rounded-xl focus:border-blue-500"
                  required
                  data-testid="investor-name-input"
                />
              </div>
            </div>

            {/* Email */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">Email Address *</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-600" />
                <Input
                  type="email"
                  placeholder="john@investmentfirm.com"
                  value={investorForm.email}
                  onChange={(e) => setInvestorForm({...investorForm, email: e.target.value})}
                  className="w-full pl-11 pr-4 py-3 bg-white border-slate-600 text-slate-900 placeholder:text-slate-500 rounded-xl focus:border-blue-500"
                  required
                  data-testid="investor-email-input"
                />
              </div>
            </div>

            {/* Company & Role Row */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">Company *</label>
                <div className="relative">
                  <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-600" />
                  <Input
                    type="text"
                    placeholder="Acme Ventures"
                    value={investorForm.company}
                    onChange={(e) => setInvestorForm({...investorForm, company: e.target.value})}
                    className="w-full pl-11 pr-4 py-3 bg-white border-slate-600 text-slate-900 placeholder:text-slate-500 rounded-xl focus:border-blue-500"
                    required
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">Title/Role *</label>
                <div className="relative">
                  <Briefcase className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-600" />
                  <Input
                    type="text"
                    placeholder="Partner"
                    value={investorForm.role}
                    onChange={(e) => setInvestorForm({...investorForm, role: e.target.value})}
                    className="w-full pl-11 pr-4 py-3 bg-white border-slate-600 text-slate-900 placeholder:text-slate-500 rounded-xl focus:border-blue-500"
                    required
                  />
                </div>
              </div>
            </div>

            {/* Investor Type */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">I am a... *</label>
              <select
                value={investorForm.investorType}
                onChange={(e) => setInvestorForm({...investorForm, investorType: e.target.value})}
                className="w-full px-4 py-3 bg-white border border-slate-600 text-slate-900 rounded-xl focus:border-blue-500 focus:ring-blue-500/20"
                required
              >
                <option value="" className="bg-white">Select one...</option>
                <option value="sba_lender" className="bg-white">SBA Lender / Bank</option>
                <option value="angel" className="bg-white">Angel Investor</option>
                <option value="vc" className="bg-white">Venture Capital</option>
                <option value="family_office" className="bg-white">Family Office</option>
                <option value="corporate" className="bg-white">Corporate / Strategic Partner</option>
                <option value="advisor" className="bg-white">Advisor / Consultant</option>
                <option value="potential_customer" className="bg-white">Potential Customer</option>
                <option value="other" className="bg-white">Other</option>
                <option value="other" className="bg-white">Other</option>
              </select>
            </div>

            {/* How did you hear about us */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">How did you hear about us?</label>
              <select
                value={investorForm.howHeard}
                onChange={(e) => setInvestorForm({...investorForm, howHeard: e.target.value})}
                className="w-full px-4 py-3 bg-white border border-slate-600 text-slate-900 rounded-xl focus:border-blue-500 focus:ring-blue-500/20"
              >
                <option value="" className="bg-white">Select one (optional)...</option>
                <option value="referral" className="bg-white">Referral from contact</option>
                <option value="linkedin" className="bg-white">LinkedIn</option>
                <option value="pitch_event" className="bg-white">Pitch Event / Demo Day</option>
                <option value="website" className="bg-white">Company Website</option>
                <option value="news" className="bg-white">News / Press</option>
                <option value="existing_relationship" className="bg-white">Existing Relationship</option>
                <option value="other" className="bg-white">Other</option>
              </select>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full py-4 bg-blue-900 hover:from-blue-700 hover:to-cyan-600 text-white font-semibold rounded-xl shadow-lg shadow-blue-500/25 transition-all mt-2"
              data-testid="investor-unlock-btn"
            >
              {loading ? (
                <span className="flex items-center gap-2 justify-center">
                  <span className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Granting Access...
                </span>
              ) : (
                <span className="flex items-center gap-2 justify-center">
                  <Unlock className="w-5 h-5" />
                  Access Business Plan
                </span>
              )}
            </Button>
          </form>

          <p className="text-slate-500 text-sm text-center mt-6">
            Questions? Contact <a href="mailto:george.broughton@sterilemate.com" className="text-blue-900 hover:text-blue-900">george.broughton@sterilemate.com</a>
          </p>

          {/* Quick Access to One-Pager */}
          <div className="mt-6 p-4 bg-white/50 rounded-xl border border-slate-200">
            <p className="text-slate-600 text-sm mb-3 text-center">Quick Access (No Login Required):</p>
            <div className="space-y-2">
              <button 
                onClick={() => navigate("/investor/one-pager")}
                className="w-full py-3 bg-gradient-to-r from-cyan-600/20 to-blue-600/20 border border-slate-200 text-blue-900 hover:bg-cyan-600/30 rounded-lg text-sm font-medium flex items-center justify-center gap-2 transition-all"
              >
                <FileText className="w-4 h-4" />
                View Investor One-Pager
              </button>
              <button 
                onClick={() => navigate("/investor/financials")}
                className="w-full py-3 bg-white border border-slate-200 text-emerald-600 hover:bg-emerald-600/30 rounded-lg text-sm font-medium flex items-center justify-center gap-2 transition-all"
              >
                <BarChart3 className="w-4 h-4" />
                3-Year Financial Projections
              </button>
            </div>
          </div>

          {/* Link to Hospital Presentation */}
          <div className="mt-8 text-center">
            <button 
              onClick={() => navigate("/hospital-presentation")}
              className="text-slate-500 hover:text-slate-700 text-sm flex items-center gap-2 mx-auto"
            >
              Looking for the Hospital Beta Program? <ChevronDown className="w-4 h-4 rotate-[-90deg]" />
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900">
      {/* Background effects — soft United-style accents */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-blue-200/30 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-1/4 w-80 h-80 bg-cyan-200/30 rounded-full blur-3xl" />
        <div className="absolute top-1/2 left-0 w-64 h-64 bg-slate-200/40 rounded-full blur-3xl" />
      </div>

      {/* Header */}
      <header className="relative z-10 border-b border-slate-200 bg-white/95 backdrop-blur-lg sticky top-0 shadow-sm">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 py-3 sm:py-4 flex items-center justify-between">
          <div className="flex items-center gap-2 sm:gap-4">
            <img src={STERILEMATE_LOGO} alt="Sterile Mate" className="h-10 sm:h-12 w-auto object-contain" />
            <div>
              <h1 className="font-bold text-sm sm:text-lg">Sterile Mate LLC</h1>
              <p className="text-[10px] sm:text-xs text-emerald-600">Powered by AskIsaac</p>
            </div>
          </div>
          <div className="flex items-center gap-2 sm:gap-4">
            <PresentationSwitcher />
            <PDFDownloadLink 
              document={<InvestorPackagePDF scenario={activeScenarioId} />} 
              fileName={getInvestorPackageFileName(activeScenarioId)}
              key={activeScenarioId}
              data-testid={`download-business-plan-${activeScenarioId}`}
              className="inline-flex items-center gap-1.5 px-2 sm:px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white text-xs sm:text-sm font-medium rounded-lg transition-colors"
            >
              {({ loading }) => (
                loading ? (
                  <>
                    <span className="animate-spin">⏳</span>
                    <span className="hidden sm:inline">Generating...</span>
                  </>
                ) : (
                  <>
                    <Download className="w-3.5 h-3.5" />
                    <span className="hidden sm:inline">Plan C PDF</span>
                    <span className="sm:hidden">PDF</span>
                  </>
                )
              )}
            </PDFDownloadLink>
            <ShareButtons 
              title="Sterile Mate LLC - Investor Portal"
              description="6 years in healthcare. Powered by AskIsaac technology. Uber-like Healthcare Concierge."
              url={window.location.href}
              variant="dark"
              size="sm"
            />
            <Button
              variant="outline"
              size="sm"
              className="border-blue-900 text-blue-900 hover:bg-blue-900 hover:text-white"
              data-testid="copy-share-link-btn"
              onClick={async () => {
                const shareUrl = `${window.location.origin}/investor/share`;
                try {
                  await navigator.clipboard.writeText(shareUrl);
                  toast.success("Share link copied — anyone with this link enters their name & email to view the plan.");
                } catch {
                  window.prompt("Copy this shareable link:", shareUrl);
                }
              }}
            >
              <Link2 className="w-4 h-4 mr-1" />
              <span className="hidden sm:inline">Copy Share Link</span>
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                sessionStorage.removeItem("investor_unlocked");
                setIsUnlocked(false);
                setEmail("");
              }}
              className="text-slate-600 hover:text-slate-900 hover:bg-white text-xs sm:text-sm px-2 sm:px-3"
            >
              Exit
            </Button>
          </div>
        </div>
      </header>

      {/* Scenario Toggle — Plan A (Humble) vs Plan B (North Sam Houston) */}
      {allowedScenarios.length > 1 && (
        <div className="relative z-10 border-b border-slate-200 bg-white backdrop-blur" data-testid="scenario-toggle-bar">
          <div className="max-w-7xl mx-auto px-3 sm:px-6 py-3 flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-2 sm:gap-3 min-w-0">
              <Building className="w-4 h-4 text-blue-900 shrink-0" />
              <div className="min-w-0">
                <p className="text-[10px] sm:text-[11px] font-bold tracking-[0.18em] uppercase text-emerald-700">
                  SBA Loan Scenario
                </p>
                <p className="text-xs sm:text-sm text-slate-900 font-semibold truncate">
                  {scenario.label} <span className="text-slate-600 font-normal">· {scenario.short} · {fmtUSD(scenario.total)} total ask</span>
                </p>
              </div>
            </div>
            <div className="inline-flex items-center bg-white border border-slate-200 rounded-lg p-1 shrink-0" data-testid="scenario-toggle">
              {Object.values(SCENARIOS)
                .filter((s) => allowedScenarios.includes(s.id))
                .map((s) => {
                  const active = s.id === activeScenarioId;
                  return (
                    <button
                      key={s.id}
                      onClick={() => switchScenario(s.id)}
                      data-testid={`scenario-toggle-${s.id}`}
                      className={`px-3 sm:px-4 py-1.5 rounded-md text-xs sm:text-sm font-semibold transition-all whitespace-nowrap ${
                        active
                          ? "bg-emerald-500 text-white shadow"
                          : "text-slate-700 hover:text-slate-900"
                      }`}
                    >
                      {s.id === "humble"
                        ? "Plan A \u00b7 Humble"
                        : s.id === "northsam"
                          ? "Plan B \u00b7 N. Sam Houston"
                          : "Plan C \u00b7 Ronan Rd"}
                    </button>
                  );
                })}
            </div>
          </div>
        </div>
      )}

      {/* Admin Scenario Access panel — retired now that only Plan C (Ronan Rd) exists. */}

      {/* Navigation */}
      <nav className="relative z-10 border-b border-slate-200 bg-white/50 backdrop-blur-lg">
        <div className="max-w-7xl mx-auto px-2 sm:px-6 py-2 sm:py-3 flex gap-1 sm:gap-2 overflow-x-auto scrollbar-hide items-center">
          {[
            { id: "executive", label: "Executive Summary" },
            { id: "business", label: "Business Description" },
            { id: "operations", label: "Operations Plan" },
            { id: "market", label: "Market Analysis" },
            { id: "management", label: "Management" },
            { id: "financials", label: "Financial Projections" },
            { id: "loan", label: "Loan Request" },
            { id: "faq", label: "FAQ" },
            { id: "contact", label: "Contact" }
          ].map((item) => (
            <button
              key={item.id}
              onClick={() => setActiveSection(item.id)}
              className={`px-2.5 sm:px-4 py-1.5 sm:py-2 rounded-lg font-medium text-xs sm:text-sm whitespace-nowrap transition-all flex-shrink-0 ${
                activeSection === item.id
                  ? "bg-blue-900 text-white shadow-lg"
                  : "text-slate-600 hover:text-slate-900 hover:bg-slate-100"
              }`}
            >
              {item.label}
            </button>
          ))}
          {/* External link — separated by a small divider */}
          <div className="hidden sm:block w-px h-6 bg-slate-700 mx-1 flex-shrink-0" />
          <a
            href="/living-waters"
            target="_blank"
            rel="noreferrer"
            data-testid="investor-nav-living-waters-link"
            title="Open the Living Waters × Bleu Flux marketing site in a new tab"
            className="flex items-center gap-1.5 px-2.5 sm:px-4 py-1.5 sm:py-2 rounded-lg font-bold text-xs sm:text-sm whitespace-nowrap flex-shrink-0 bg-blue-900 hover:from-cyan-500 hover:to-blue-500 text-white shadow-lg shadow-cyan-500/20 transition-all hover:-translate-y-0.5"
          >
            <span>💧 Living Waters</span>
            <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M7 7h10v10"/><path d="M7 17 17 7"/></svg>
          </a>
        </div>
      </nav>

      {/* Enterprise Validation Banner */}
      <div className="relative z-10 bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-center gap-3">
          <Building2 className="w-5 h-5 text-slate-700" />
          <p className="text-sm text-slate-800">
            <strong className="text-slate-900">6 Years in Healthcare:</strong> Sterile Mate LLC — Established 2020 | $3.2M+ Cumulative Revenue | Multi-Hospital System Partner | HUB Certified
          </p>
        </div>
      </div>

      {/* Meet the Founder & CEO — persistent above all section tabs so investors
          see the founder anchor immediately on landing, regardless of which
          tab is active. Links to the public executive CV. */}
      <div className="relative z-10 bg-slate-100 border-b border-cyan-400/20" data-testid="investor-portal-founder-strip">
        <div className="max-w-7xl mx-auto px-4 py-3 sm:py-4 flex items-center gap-3 sm:gap-4">
          <img
            src={`${process.env.REACT_APP_BACKEND_URL}/api/static/email_assets/george_broughton_portrait.jpg`}
            alt="George Broughton III, Founder & CEO of Sterile Mate LLC"
            className="w-12 h-12 sm:w-14 sm:h-14 rounded-lg object-cover border border-cyan-400/40 shadow-lg shrink-0"
          />
          <div className="flex-1 min-w-0">
            <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-blue-900 mb-0.5">Meet the Founder &amp; CEO</p>
            <p className="text-sm sm:text-base font-bold text-slate-900 truncate">George Broughton III</p>
            <p className="text-[11px] sm:text-xs text-slate-600 truncate">
              Healthcare supply chain executive · AAMI ST108 SME · Architect of AskIsaac.io
            </p>
          </div>
          <a
            href="https://www.linkedin.com/in/georgebroughtoniii"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 px-3 sm:px-4 h-9 rounded-md bg-[#0056A8] hover:bg-[#003366] text-slate-900 text-xs sm:text-sm font-semibold whitespace-nowrap shadow-md transition-colors"
            data-testid="investor-portal-linkedin-link"
          >
            Connect on LinkedIn
            <span aria-hidden="true">→</span>
          </a>
        </div>
      </div>

      {/* Main Content */}
      <main className="relative z-10 max-w-7xl mx-auto px-3 sm:px-6 py-6 sm:py-10">
        
        {/* Overview Section */}
        {activeSection === "executive" && (
          <div className="space-y-6 sm:space-y-10 animate-fade-in">

            {/* ============================================================ */}
            {/* EXECUTIVE SNAPSHOT — Problem · Solution · Value · Ask         */}
            {/* Designed for executive skimmers — full thesis in one gulp    */}
            {/* ============================================================ */}
            <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-sm">
              <div className="px-6 sm:px-10 py-4 bg-slate-50 border-b border-slate-200 flex items-center justify-between flex-wrap gap-2">
                <span className="text-xs font-bold text-blue-900 uppercase tracking-[0.25em]">Executive Snapshot · 30-Second Read</span>
                <span className="text-xs text-slate-500">Sterile Mate LLC · Powered by AskIsaac · SBA 504</span>
              </div>
              <div className="grid md:grid-cols-4 gap-0 divide-y md:divide-y-0 md:divide-x divide-slate-200">
                {/* Problem */}
                <div className="p-6 lg:p-7">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-8 h-8 rounded-lg bg-slate-100 border border-slate-200 flex items-center justify-center">
                      <AlertCircle className="w-4 h-4 text-slate-700" />
                    </div>
                    <span className="text-[10px] font-black text-slate-700 uppercase tracking-widest">The Problem</span>
                  </div>
                  <p className="text-slate-900 font-bold text-base leading-snug mb-2">Healthcare backorders happen every day — and paralyze every hospital.</p>
                  <p className="text-slate-600 text-sm leading-relaxed">Traditional distributors take days. Materials Managers stop everything to hunt substitutes. During disasters, the crisis goes national in hours.</p>
                </div>
                {/* Solution */}
                <div className="p-6 lg:p-7">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-8 h-8 rounded-lg bg-slate-100 border border-slate-200 flex items-center justify-center">
                      <Zap className="w-4 h-4 text-slate-700" />
                    </div>
                    <span className="text-[10px] font-black text-slate-700 uppercase tracking-widest">Our Solution</span>
                  </div>
                  <p className="text-slate-900 font-bold text-base leading-snug mb-2">6 years of hospital trust + AskIsaac RFQ automation = minutes, not days.</p>
                  <p className="text-slate-600 text-sm leading-relaxed">Automated multi-supplier broadcast, ETA tracking, hospital-alert engine. One operator does the work of ten. National supplier network already in place.</p>
                </div>
                {/* Value */}
                <div className="p-6 lg:p-7">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-8 h-8 rounded-lg bg-slate-100 border border-slate-200 flex items-center justify-center">
                      <Heart className="w-4 h-4 text-slate-700" />
                    </div>
                    <span className="text-[10px] font-black text-slate-700 uppercase tracking-widest">Value to Healthcare</span>
                  </div>
                  <p className="text-slate-900 font-bold text-base leading-snug mb-2">Hospitals stay open. Patients don&apos;t wait.</p>
                  <p className="text-slate-600 text-sm leading-relaxed">We shrink the backorder response time from days to minutes, protect OR/ED uptime, cover the 20% of hospital spend that lives outside GPO contracts, and give every facility a national safety-valve.</p>
                </div>
                {/* Ask */}
                <div className="p-6 lg:p-7 bg-slate-50">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-8 h-8 rounded-lg bg-blue-900 border border-blue-900 flex items-center justify-center">
                      <Landmark className="w-4 h-4 text-white" />
                    </div>
                    <span className="text-[10px] font-black text-blue-900 uppercase tracking-widest">The Ask</span>
                  </div>
                  <p className="text-slate-900 font-bold text-base leading-snug mb-2">${(scenario.total / 1_000_000).toFixed(2)}M SBA {scenario.loanType} → $4.54M Y1 → $11.64M Y3.</p>
                  <p className="text-slate-600 text-sm leading-relaxed">Owner-occupied Class-A HQ + LOC funds the platform, national supplier build-out, and inside-sales scale-up. Y2 DSCR 6.71x.</p>
                </div>
              </div>
              <div className="px-6 sm:px-10 py-3 bg-slate-50 border-t border-slate-200 text-center">
                <p className="text-xs text-slate-600">
                  <span className="text-blue-900 font-semibold">Where we win:</span> the ~20% of hospital procurement that flows <em>outside</em> traditional GPO channels — plus a path to placing our branded consumables <em>inside</em> GPO contracts. <span className="text-slate-500">Skim to any section below for depth.</span>
                </p>
              </div>
            </div>

            {/* HERO — The Vision: Why We're Buying This Building */}
            <div className="relative bg-white rounded-3xl overflow-hidden border border-slate-200 p-8 sm:p-10">
              <div className="absolute top-0 right-0 w-72 h-72 bg-blue-500/10 rounded-full blur-3xl" />
              <div className="relative z-10">
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-blue-100 border border-slate-200 rounded-full text-blue-900 text-sm mb-6">
                  <Landmark className="w-4 h-4" />
                  SBA {scenario.loanType} Expansion — Building a Healthcare Platform
                </div>
                <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-6 leading-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  One Building.<br />
                  <span className="text-blue-900">Two Revenue Engines.</span><br />
                  <span className="text-emerald-600">Three Income Streams.</span>
                </h1>
                <p className="text-xl text-slate-700 max-w-3xl mb-6">
                  Sterile Mate is acquiring a <span className="text-blue-900 font-bold">{scenario.id === "humble" ? "10,766 SF freestanding commercial building" : scenario.id === "ronan" ? "22,750 SF Class-A 3-story corporate HQ on 5.08 acres" : "87,282 SF Class-B \u201C2SIXTY3\u201D campus"}</span> to establish a fully integrated headquarters — combining <span className="text-slate-900 font-bold">B2B healthcare operations</span>, {scenario.id === "ronan" ? <><span className="text-blue-900 font-bold">expanded backorder sourcing</span>, <span className="text-emerald-600 font-bold">water purification systems</span>, and <span className="text-slate-800 font-bold">healthcare construction</span></> : <>a <span className="text-emerald-600 font-bold">DME &amp; Mobility Superstore</span></>}, and a centralized hub for sales, order fulfillment{scenario.id === "northsam" ? ", AskIsaac engineering, and multi-tenant rental income" : scenario.id === "ronan" ? ", short-stock warehousing on Floor 2, and a Texas-metro expansion sales office (San Antonio, Dallas, Austin, El Paso, Fort Worth, Galveston, Waco)" : ", and field operations"}.
                </p>
                <div className="grid sm:grid-cols-4 gap-4 mt-6">
                  <div className="bg-white rounded-xl p-4 border border-blue-500/20 text-center">
                    <div className="text-2xl font-bold text-blue-900">$1.182M</div>
                    <div className="text-xs text-slate-600 mt-1">2026 Pipeline (Confirmed)</div>
                  </div>
                  <div className="bg-white rounded-xl p-4 border border-emerald-500/20 text-center">
                    <div className="text-2xl font-bold text-emerald-600">$3.9M+</div>
                    <div className="text-xs text-slate-600 mt-1">Cumulative Revenue</div>
                  </div>
                  <div className="bg-white rounded-xl p-4 border border-slate-200 text-center">
                    <div className="text-2xl font-bold text-slate-800">{scenario.id === "humble" ? "168K" : scenario.id === "ronan" ? "200K+" : "180K"}</div>
                    <div className="text-xs text-slate-600 mt-1">{scenario.id === "humble" ? "Vehicles/Day on US-59" : scenario.id === "ronan" ? "Vehicles/Day on Beltway 8 + I-45" : "Vehicles/Day on Beltway 8"}</div>
                  </div>
                  <div className="bg-white rounded-xl p-4 border border-cyan-500/20 text-center" data-testid="hero-total-project">
                    <div className="text-2xl font-bold text-blue-900">${(scenario.total / 1_000_000).toFixed(1)}M</div>
                    <div className="text-xs text-slate-600 mt-1">Total SBA Project · {scenario.label}</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Building Rendering */}
            <div className="rounded-2xl overflow-hidden border border-blue-500/20" data-testid="active-scenario-rendering">
              <img
                src={scenario.rendering}
                alt={`Sterile Mate HQ — Post-Renovation Rendering (${scenario.label})`}
                className="w-full h-auto object-cover"
                data-testid="exec-building-rendering"
              />
              <div className="bg-white px-6 py-4 flex items-center justify-between">
                <div>
                  <p className="text-slate-900 font-semibold">Post-Renovation Rendering — {scenario.headline}</p>
                  <p className="text-slate-600 text-sm">{scenario.address} — {scenario.sqft}</p>
                </div>
                <div className="text-right">
                  <p className="text-blue-900 font-bold text-lg">{fmtUSD(scenario.asking)}</p>
                  <p className="text-slate-500 text-xs">Acquisition Price</p>
                </div>
              </div>
            </div>

            {/* The Strategic Vision — clean white card */}
            <div className="bg-white rounded-2xl border border-slate-200 p-6 sm:p-8">
              <div className="grid md:grid-cols-2 gap-8 items-center">
                <div>
                  <span className="inline-flex items-center gap-2 px-3 py-1 bg-blue-900 text-white rounded text-xs font-bold uppercase tracking-widest mb-4">
                    <Building2 className="w-4 h-4" />
                    Building on What We Have
                  </span>
                  <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
                    From Living Room to <span className="text-blue-900">Healthcare HQ</span>
                  </h2>
                  <p className="text-lg text-slate-700 mb-4">
                    Founded during COVID-19 as a frontline triage supplier for Houston hospitals, Sterile Mate has grown from a pandemic-era startup to a <span className="text-slate-900 font-semibold">$1.1M revenue company</span> with active government contracts and hospital partnerships.
                  </p>
                  <p className="text-slate-700 mb-6">
                    This building gives us what we&apos;ve never had: <span className="text-slate-900 font-semibold">a physical headquarters</span> to house our sales team, stage inventory, demonstrate products to healthcare buyers, and fulfill B2B orders — all powered by our proprietary <span className="text-blue-900 font-semibold">AskIsaac.io</span> technology platform.
                  </p>
                </div>
                <div className="space-y-3">
                  {[
                    { icon: Building2, label: "B2B Operations Hub", desc: "Sales team HQ, water purification showroom, client training center, project coordination" },
                    { icon: Search, label: "Expanded Backorder Sourcing", desc: "Fully automated national supplier network — AskIsaac handles hospital RFQs end-to-end, freeing the team to scale accounts" },
                    { icon: Package, label: "Order Fulfillment & Logistics", desc: "Centralized inventory, pre-staged installation materials, rapid deployment to hospitals" },
                    { icon: Brain, label: "AskIsaac Digital Platform", desc: "AI-powered project management, procurement, compliance — enhancing every operation under this roof" },
                  ].map((item, idx) => (
                    <div key={idx} className="flex items-start gap-4 bg-slate-50 rounded-lg p-4 border border-slate-200">
                      <div className="w-9 h-9 rounded bg-blue-900 flex items-center justify-center flex-shrink-0">
                        <item.icon className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <p className="font-semibold text-slate-900">{item.label}</p>
                        <p className="text-slate-600 text-sm">{item.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* How AskIsaac Enhances Everything — clean white card, navy tiles */}
            <div className="bg-white rounded-2xl border border-slate-200 p-6 sm:p-8">
              <div className="text-center mb-6">
                <span className="inline-flex items-center gap-2 px-3 py-1 bg-blue-900 text-white rounded text-xs font-bold uppercase tracking-widest mb-4">
                  <Zap className="w-4 h-4" />
                  Digital Backbone
                </span>
                <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-2">
                  AskIsaac.io Powers the Entire Operation
                </h2>
                <p className="text-slate-700 max-w-3xl mx-auto">
                  Our proprietary platform isn&apos;t just software — it&apos;s the nervous system connecting every division under this roof
                </p>
              </div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {[
                  { title: "B2B Project Management", desc: "Track water purification installations, healthcare construction bids, EVS contracts, and service agreements in real-time with automated compliance reporting", icon: Target },
                  { title: "Backorder Sourcing", desc: "Fully automated supplier-network broadcasting, RFQ discovery, and quote aggregation — turns a legacy manual grind into an on-demand revenue engine", icon: Search },
                  { title: "Sales & Pipeline Tracking", desc: "CRM-integrated pipeline management, automated follow-ups, and proposal generation — from lead to signed contract", icon: TrendingUp },
                  { title: "Procurement & Fulfillment", desc: "Supplier network broadcasting, order fulfillment tracking, GPS-verified deliveries, and audit-ready documentation", icon: Package },
                ].map((item, idx) => (
                  <div key={idx} className="bg-blue-900 rounded-lg p-5 text-center">
                    <item.icon className="w-8 h-8 text-white mx-auto mb-3" />
                    <h3 className="font-semibold text-white mb-2 text-sm">{item.title}</h3>
                    <p className="text-blue-100 text-xs">{item.desc}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Three Revenue Streams — clean white card */}
            <div className="bg-white rounded-2xl border border-slate-200 p-8 sm:p-10">
              <div className="text-center mb-8">
                <h2 className="text-3xl font-bold text-slate-900 mb-2">Three Revenue Streams, One Roof</h2>
                <p className="text-slate-600">A resilient, diversified model that lenders can believe in</p>
              </div>
              <div className="grid md:grid-cols-3 gap-6">
                <div className="bg-white/50 border border-blue-500/20 rounded-2xl p-6 text-center">
                  <div className="w-14 h-14 rounded-xl bg-blue-100 flex items-center justify-center mx-auto mb-4">
                    <Search className="w-7 h-7 text-blue-900" />
                  </div>
                  <h3 className="text-xl font-bold text-blue-900 mb-2">Expanded Backorder Sourcing</h3>
                  <p className="text-slate-600 text-sm mb-4">National supplier network + AskIsaac automation. Hospital RFQs on hard-to-find and allocated SKUs return priced quotes in 2 hours &mdash; a legacy manual grind turned into an on-demand revenue engine.</p>
                  <div className="bg-blue-500/10 rounded-lg p-3">
                    <p className="text-blue-900 font-bold text-lg">2-Hour Turnaround</p>
                    <p className="text-xs text-slate-500">Live List: 170+ SKUs across CIA Medical + Keystone</p>
                  </div>
                </div>
                <div className="bg-white/50 border border-emerald-500/20 rounded-2xl p-6 text-center">
                  <div className="w-14 h-14 rounded-xl bg-slate-100 flex items-center justify-center mx-auto mb-4">
                    <Droplets className="w-7 h-7 text-emerald-600" />
                  </div>
                  <h3 className="text-xl font-bold text-emerald-600 mb-2">Water Purification Systems</h3>
                  <p className="text-slate-600 text-sm mb-4">AAMI ST108-compliant water treatment for sterile processing, dialysis, and endoscopy. Ameriwater, MediQA, RO/DI systems, water softeners &mdash; the SPD water standard.</p>
                  <div className="bg-emerald-500/10 rounded-lg p-3">
                    <p className="text-emerald-600 font-bold text-lg">$1M+ Installed</p>
                    <p className="text-xs text-slate-500">Harris Health System &bull; 2022 onward</p>
                  </div>
                </div>
                <div className="bg-white/50 border border-violet-500/20 rounded-2xl p-6 text-center">
                  <div className="w-14 h-14 rounded-xl bg-violet-500/20 flex items-center justify-center mx-auto mb-4">
                    <Landmark className="w-7 h-7 text-blue-900" />
                  </div>
                  <h3 className="text-xl font-bold text-blue-900 mb-2">Healthcare Construction</h3>
                  <p className="text-slate-600 text-sm mb-4">Dialysis rooms, nurseries, healthcare clinics, water-purification build-outs, and sterile-processing retrofits &mdash; bid Local, State, County &amp; Federal now that we have a team with prior healthcare-construction experience.</p>
                  <div className="bg-violet-500/10 rounded-lg p-3">
                    <p className="text-blue-900 font-bold text-lg">Government + Private</p>
                    <p className="text-xs text-slate-500">Long payment cycles, high stability</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Quick Stats Bar */}
            <div className="grid sm:grid-cols-5 gap-3">
              <div className="bg-white/50 border border-slate-200 rounded-xl p-4 text-center">
                <p className="text-2xl font-bold text-blue-900">HUB</p>
                <p className="text-xs text-slate-500">Certified | SAM.gov</p>
              </div>
              <div className="bg-white/50 border border-slate-200 rounded-xl p-4 text-center">
                <p className="text-2xl font-bold text-emerald-600">Licensed</p>
                <p className="text-xs text-slate-500">Medical Device Supplier</p>
              </div>
              <div className="bg-white/50 border border-slate-200 rounded-xl p-4 text-center">
                <p className="text-2xl font-bold text-slate-800">Harris Co</p>
                <p className="text-xs text-slate-500">Active Gov Contracts</p>
              </div>
              <div className="bg-white/50 border border-slate-200 rounded-xl p-4 text-center">
                <p className="text-2xl font-bold text-blue-900">2020</p>
                <p className="text-xs text-slate-500">Founded</p>
              </div>
              <div className="bg-white/50 border border-slate-200 rounded-xl p-4 text-center">
                <p className="text-2xl font-bold text-blue-900">AskIsaac</p>
                <p className="text-xs text-slate-500">Proprietary Platform</p>
              </div>
            </div>
          </div>
        )}

        {/* SterileMate Section - The Foundation */}
        {activeSection === "business" && (
          <div className="space-y-10 animate-fade-in">
            {/* Hero Banner */}
            <div className="relative bg-white rounded-3xl overflow-hidden border border-slate-200 p-8 sm:p-10">
              <div className="absolute top-0 right-0 w-64 h-64 bg-emerald-500/10 rounded-full blur-3xl" />
              <div className="relative z-10 grid lg:grid-cols-2 gap-8 items-center">
                <div>
                  <div className="inline-flex items-center gap-2 px-4 py-2 bg-slate-100 border border-slate-200 rounded-full text-emerald-600 text-sm mb-6">
                    <Building2 className="w-4 h-4" />
                    Established 2020 | HUB Certified
                  </div>
                  <div className="flex items-center gap-4 mb-6">
                    <img src={STERILEMATE_LOGO} alt="Sterile Mate" className="h-20 w-auto object-contain" />
                    <div>
                      <h1 className="text-4xl lg:text-5xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                        Sterile Mate LLC
                      </h1>
                      <p className="text-emerald-600 font-medium">6 Years Serving Healthcare</p>
                    </div>
                  </div>
                  <p className="text-xl text-slate-700 leading-relaxed mb-6">
                    Founded during COVID-19 from George Broughton&apos;s living room, Sterile Mate has grown into a <span className="text-emerald-600 font-semibold">trusted healthcare solutions provider</span> serving hospitals across Texas and the VA system nationwide.
                  </p>
                  <p className="text-lg text-slate-600">
                    <span className="text-slate-800 font-semibold">AskIsaac</span> is the technology platform we built to scale our operations and eventually license to other distributors as a white-label solution.
                  </p>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-white rounded-xl p-6 border border-emerald-500/20 text-center">
                    <div className="text-3xl font-bold text-emerald-600 mb-1">$3.9M+</div>
                    <div className="text-sm text-slate-600">Cumulative Revenue</div>
                  </div>
                  <div className="bg-white rounded-xl p-6 border border-cyan-500/20 text-center">
                    <div className="text-3xl font-bold text-blue-900 mb-1">6 Years</div>
                    <div className="text-sm text-slate-600">In Healthcare</div>
                  </div>
                  <div className="bg-white rounded-xl p-6 border border-slate-200 text-center">
                    <div className="text-3xl font-bold text-slate-800 mb-1">$1.182M</div>
                    <div className="text-sm text-slate-600">2026 Pipeline</div>
                  </div>
                  <div className="bg-white rounded-xl p-6 border border-violet-500/20 text-center">
                    <div className="text-3xl font-bold text-blue-900 mb-1">VA + Harris</div>
                    <div className="text-sm text-slate-600">Health Systems</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Founder Story - George's Journey */}
            <div className="bg-white rounded-2xl p-6 border border-slate-200">
              <div className="grid md:grid-cols-3 gap-6 items-center">
                <div className="md:col-span-1 flex justify-center">
                  <div className="relative">
                    <img
                      src={`${process.env.REACT_APP_BACKEND_URL}/api/static/email_assets/george_broughton_portrait.jpg`}
                      alt="George Broughton III"
                      className="w-48 h-64 object-cover object-top rounded-2xl border-2 border-slate-200"
                      loading="lazy"
                    />
                    <div className="absolute -bottom-3 -right-3 bg-blue-500 text-slate-900 text-xs font-bold px-3 py-1 rounded-full">
                      Founder &amp; CEO
                    </div>
                  </div>
                </div>
                <div className="md:col-span-2">
                  <div className="flex items-center gap-2 mb-4">
                    <Building2 className="w-6 h-6 text-blue-900" />
                    <h2 className="text-2xl font-bold text-slate-900">A Founder Who Built This From the Ground Up</h2>
                  </div>
                  <div className="space-y-4 text-slate-700">
                    <p>
                      <span className="text-blue-900 font-semibold">In 2020</span>, when COVID-19 overwhelmed Houston&apos;s healthcare system, George Broughton III stepped in as a <span className="text-slate-900 font-semibold">frontline triage supplier</span> — sourcing and delivering critical PPE, medical devices, and infection prevention supplies directly to hospitals in crisis.
                    </p>
                    <p>
                      What began as an emergency response became a mission: build a company that healthcare facilities can depend on — <span className="text-slate-900 font-semibold">not just in a crisis, but every day</span>. From a living room startup to a HUB-certified government contractor with $3.9M+ in cumulative revenue, George has built Sterile Mate through relationships, tenacity, and deep personal commitment to healthcare.
                    </p>
                    <p className="text-lg">
                      <Quote className="w-5 h-5 inline text-slate-800 mr-2" />
                      <em>&quot;I built this company because I saw hospitals struggling to get what they needed, when they needed it. Now we&apos;re building a headquarters to serve them even better.&quot;</em>
                    </p>
                  </div>
                  <div className="mt-4 flex items-center gap-4">
                    <div className="px-4 py-2 bg-blue-500/10 border border-slate-200 rounded-lg">
                      <span className="text-blue-900 font-semibold">2020:</span> <span className="text-slate-600">Founded During COVID</span>
                    </div>
                    <div className="px-4 py-2 bg-emerald-500/10 border border-slate-200 rounded-lg">
                      <span className="text-emerald-600 font-semibold">2024:</span> <span className="text-slate-600">$1.1M Revenue</span>
                    </div>
                    <div className="px-4 py-2 bg-slate-100 border border-slate-200 rounded-lg">
                      <span className="text-slate-800 font-semibold">2026:</span> <span className="text-slate-600">HQ Acquisition</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Why Invest Now - Urgency Section */}
            <div className="bg-white rounded-2xl p-6 border border-slate-200">
              <div className="text-center mb-6">
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-slate-100 border border-slate-200 rounded-full text-slate-800 text-sm mb-4">
                  <Zap className="w-4 h-4" />
                  TIMING IS EVERYTHING
                </div>
                <h2 className="text-3xl font-bold text-slate-900">Why Invest Now?</h2>
              </div>
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="bg-slate-100 rounded-xl p-5 border border-slate-200">
                  <Hospital className="w-8 h-8 text-slate-800 mb-3" />
                  <h3 className="font-bold text-slate-900 mb-2">Aging Infrastructure</h3>
                  <p className="text-sm text-slate-600">60% of US hospital infrastructure is 25+ years old. Massive water system upgrades required.</p>
                </div>
                <div className="bg-slate-100 rounded-xl p-5 border border-slate-200">
                  <Shield className="w-8 h-8 text-blue-900 mb-3" />
                  <h3 className="font-bold text-slate-900 mb-2">Post-COVID Awareness</h3>
                  <p className="text-sm text-slate-600">Supply chain resilience is now a C-suite priority. Hospitals diversifying vendor relationships.</p>
                </div>
                <div className="bg-slate-100 rounded-xl p-5 border border-slate-200">
                  <Award className="w-8 h-8 text-emerald-600 mb-3" />
                  <h3 className="font-bold text-slate-900 mb-2">HUB Advantage</h3>
                  <p className="text-sm text-slate-600">Texas HUB certification opens doors to $billions in state healthcare contracts.</p>
                </div>
                <div className="bg-slate-100 rounded-xl p-5 border border-slate-200">
                  <Droplets className="w-8 h-8 text-blue-900 mb-3" />
                  <h3 className="font-bold text-slate-900 mb-2">Water Treatment Growth</h3>
                  <p className="text-sm text-slate-600">Healthcare water purification market growing 8% CAGR. New hospital construction = new water system contracts.</p>
                </div>
              </div>
            </div>

            {/* Trusted By - Partner Logos */}
            <div className="bg-white/50 rounded-2xl p-6 border border-slate-200">
              <h2 className="text-xl font-bold text-center mb-6 text-slate-600">Trusted By & Partnered With</h2>
              <div className="flex flex-wrap justify-center items-center gap-8">
                <div className="text-center">
                  <div className="bg-slate-100 px-6 py-4 rounded-xl border border-slate-200">
                    <Hospital className="w-10 h-10 text-blue-900 mx-auto mb-2" />
                    <p className="text-slate-900 font-bold">Harris Health System</p>
                    <p className="text-xs text-slate-500">Major Hospital Projects</p>
                  </div>
                </div>
                <div className="text-center">
                  <div className="bg-slate-100 px-6 py-4 rounded-xl border border-slate-200">
                    <Shield className="w-10 h-10 text-blue-900 mx-auto mb-2" />
                    <p className="text-slate-900 font-bold">VA Healthcare</p>
                    <p className="text-xs text-slate-500">Nationwide</p>
                  </div>
                </div>
                <div className="text-center">
                  <div className="bg-slate-100 px-6 py-4 rounded-xl border border-slate-200">
                    <Package className="w-10 h-10 text-emerald-600 mx-auto mb-2" />
                    <p className="text-slate-900 font-bold">Intco Global</p>
                    <p className="text-xs text-slate-500">Private-Label Nitrile Exam Gloves</p>
                  </div>
                </div>
                <div className="text-center">
                  <div className="bg-slate-100 px-6 py-4 rounded-xl border border-slate-200">
                    <Package className="w-10 h-10 text-blue-900 mx-auto mb-2" />
                    <p className="text-slate-900 font-bold">Texas Microfiber</p>
                    <p className="text-xs text-slate-500">Private-Label Disposable Microfiber</p>
                  </div>
                </div>
                <div className="text-center">
                  <div className="bg-slate-100 px-6 py-4 rounded-xl border border-slate-200">
                    <Package className="w-10 h-10 text-slate-800 mx-auto mb-2" />
                    <p className="text-slate-900 font-bold">Filmop USA</p>
                    <p className="text-xs text-slate-500">Janitorial Solutions</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Interactive Revenue Chart */}
            <div className="bg-white/50 rounded-2xl p-6 border border-slate-200">
              <h2 className="text-2xl font-bold mb-6 flex items-center gap-3">
                <TrendingUp className="w-6 h-6 text-emerald-600" />
                Revenue Growth Trajectory
              </h2>
              <div className="space-y-4">
                {[
                  { year: "2021", revenue: 800000, max: 12000000, note: "COVID Response", color: "bg-blue-500" },
                  { year: "2022", revenue: 700000, max: 12000000, note: "Water Purification Launch", color: "bg-cyan-500" },
                  { year: "2023", revenue: 600000, max: 12000000, note: "Distribution Center", color: "bg-teal-500" },
                  { year: "2024", revenue: 1100000, max: 12000000, note: "Major Contracts", color: "bg-emerald-500" },
                  { year: "2025", revenue: 1500000, max: 12000000, note: "Platform Launch", color: "bg-green-500", current: true },
                  { year: "2026", revenue: 3000000, max: 12000000, note: "Scale & Automate", color: "bg-lime-500", forecast: true },
                  { year: "2027", revenue: 6000000, max: 12000000, note: "Regional Expansion", color: "bg-yellow-500", forecast: true },
                  { year: "2029", revenue: 12000000, max: 12000000, note: "National Leader", color: "bg-amber-500", forecast: true },
                ].map((item, idx) => (
                  <div key={idx} className="group">
                    <div className="flex items-center justify-between mb-1">
                      <div className="flex items-center gap-3">
                        <span className={`text-sm font-bold ${item.current ? 'text-emerald-600' : item.forecast ? 'text-slate-500' : 'text-slate-900'}`}>{item.year}</span>
                        <span className="text-xs text-slate-500">{item.note}</span>
                        {item.forecast && <span className="text-[10px] px-2 py-0.5 bg-slate-700 rounded text-slate-600">Projected</span>}
                      </div>
                      <span className={`font-bold ${item.current ? 'text-emerald-600' : item.forecast ? 'text-slate-600' : 'text-slate-900'}`}>
                        ${(item.revenue / 1000000).toFixed(1)}M
                      </span>
                    </div>
                    <div className="h-6 bg-white rounded-full overflow-hidden">
                      <div 
                        className={`h-full ${item.color} ${item.forecast ? 'opacity-50' : ''} rounded-full transition-all duration-1000 ease-out`}
                        style={{ width: `${(item.revenue / item.max) * 100}%` }}
                      />
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-6 text-center">
                <p className="text-2xl font-bold text-emerald-600">15x Revenue Growth</p>
                <p className="text-slate-600">From $800K (2021) to $12M+ (2029)</p>
              </div>
            </div>

            {/* Competitive Advantage Matrix */}
            <div className="bg-white/50 rounded-2xl p-6 border border-slate-200">
              <h2 className="text-2xl font-bold mb-6 flex items-center gap-3">
                <Target className="w-6 h-6 text-blue-900" />
                Competitive Advantage
              </h2>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-slate-200">
                      <th className="text-left py-3 px-4 text-slate-600 font-medium">Capability</th>
                      <th className="text-center py-3 px-4 text-emerald-600 font-bold">SterileMate</th>
                      <th className="text-center py-3 px-4 text-slate-500 font-medium">Traditional Distributors</th>
                    </tr>
                  </thead>
                  <tbody className="text-sm">
                    {[
                      { capability: "HUB Certified", sterilemate: true, traditional: "Rarely" },
                      { capability: "Water Purification Expertise", sterilemate: true, traditional: false },
                      { capability: "Automated Backorder RFQ Engine (AskIsaac)", sterilemate: true, traditional: false },
                      { capability: "Tech-Enabled Platform (AskIsaac)", sterilemate: true, traditional: false },
                      { capability: "Backorder Specialists", sterilemate: true, traditional: "Limited" },
                      { capability: "VA Nationwide Coverage", sterilemate: true, traditional: "Some" },
                      { capability: "Founder Healthcare Experience", sterilemate: "COVID Frontline", traditional: false },
                      { capability: "Response Time", sterilemate: "< 30 mins", traditional: "24-48 hrs" },
                    ].map((row, idx) => (
                      <tr key={idx} className="border-b border-slate-200">
                        <td className="py-3 px-4 text-slate-700">{row.capability}</td>
                        <td className="py-3 px-4 text-center">
                          {row.sterilemate === true ? (
                            <CheckCircle2 className="w-5 h-5 text-emerald-600 mx-auto" />
                          ) : (
                            <span className="text-emerald-600 font-medium">{row.sterilemate}</span>
                          )}
                        </td>
                        <td className="py-3 px-4 text-center">
                          {row.traditional === false ? (
                            <XCircle className="w-5 h-5 text-red-600/50 mx-auto" />
                          ) : (
                            <span className="text-slate-500">{row.traditional}</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Core Business Lines */}
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="bg-white rounded-xl p-6 border border-slate-200">
                <Droplets className="w-8 h-8 text-blue-900 mb-4" />
                <h3 className="font-bold text-slate-900 mb-2">Water Purification</h3>
                <p className="text-sm text-slate-600">Water purification solutions. RO/DI systems for SPD & dialysis. AAMI ST108 compliant.</p>
                <div className="mt-4 text-lg font-bold text-blue-900">$1.182M Pipeline</div>
              </div>
              <div className="bg-white rounded-xl p-6 border border-slate-200">
                <Package className="w-8 h-8 text-emerald-600 mb-4" />
                <h3 className="font-bold text-slate-900 mb-2">Medical Supplies</h3>
                <p className="text-sm text-slate-600">Backorder specialists. Hard-to-find supplies. PPE & janitorial. Filmop USA partner.</p>
                <div className="mt-4 text-lg font-bold text-emerald-600">Core Business</div>
              </div>
              <div className="bg-white rounded-xl p-6 border border-slate-200">
                <Package className="w-8 h-8 text-blue-900 mb-4" />
                <h3 className="font-bold text-slate-900 mb-2">Consumables Division</h3>
                <p className="text-sm text-slate-600">Private-label exam gloves (Intco Global) + private-label microfiber cloths (Texas Microfiber). Recurring hospital pull-through.</p>
                <div className="mt-4 text-lg font-bold text-blue-900">Recurring Revenue</div>
              </div>
              <div className="bg-white rounded-xl p-6 border border-slate-200">
                <Shield className="w-8 h-8 text-slate-800 mb-4" />
                <h3 className="font-bold text-slate-900 mb-2">Government Contracts</h3>
                <p className="text-sm text-slate-600">SAM.gov registered. VA nationwide. SDVOSB pathway. HUB certified.</p>
                <div className="mt-4 text-lg font-bold text-slate-800">Federal Pipeline</div>
              </div>
            </div>

            {/* Strategic Partnerships */}
            <div className="bg-white/50 rounded-2xl p-6 border border-slate-200">
              <h2 className="text-2xl font-bold mb-6 flex items-center gap-3">
                <Handshake className="w-6 h-6 text-blue-900" />
                Strategic Partnerships
              </h2>
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
                {partnerships.map((partner, idx) => (
                  <div key={idx} className="bg-white rounded-xl p-4 border border-slate-200">
                    <h3 className="font-bold text-slate-900 mb-2">{partner.name}</h3>
                    <p className="text-sm text-slate-600">{partner.focus}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Website Showcase - SterileMate.com */}
            <div className="bg-white/50 rounded-2xl p-6 border border-slate-200">
              <h2 className="text-2xl font-bold mb-2 flex items-center gap-3">
                <Globe className="w-6 h-6 text-blue-900" />
                SterileMate.com — Live Website
              </h2>
              <p className="text-slate-600 mb-6">Our professional healthcare e-commerce platform serving hospitals nationwide</p>
              
              <div className="grid md:grid-cols-2 gap-6">
                {/* Sterile Mate Logo */}
                <div className="group relative rounded-xl overflow-hidden border border-slate-600 hover:border-cyan-500/50 transition-all">
                  <div className="absolute top-3 left-3 z-10 px-3 py-1 bg-slate-50 backdrop-blur-sm rounded-full text-xs text-blue-900 font-medium">
                    Homepage
                  </div>
                  <img
                    src={STERILEMATE_LOGO}
                    alt="SterileMate Logo"
                    className="w-full h-48 object-contain bg-white p-6"
                    loading="lazy"
                  />
                  <div className="p-4 bg-slate-100">
                    <h3 className="font-bold text-slate-900 mb-1">Advancing Healthcare Hygiene</h3>
                    <p className="text-sm text-slate-600">Sterile processing, water purification, janitorial essentials, and AAMI ST108-certified solutions</p>
                  </div>
                </div>

                {/* Team Photo — George */}
                <div className="group relative rounded-xl overflow-hidden border border-slate-600 hover:border-emerald-500/50 transition-all">
                  <div className="absolute top-3 left-3 z-10 px-3 py-1 bg-slate-50 backdrop-blur-sm rounded-full text-xs text-emerald-600 font-medium">
                    Our Team
                  </div>
                  <img
                    src={`${process.env.REACT_APP_BACKEND_URL}/api/static/email_assets/team_george_broughton.jpg`}
                    alt="George Broughton III"
                    className="w-full h-64 object-contain object-top bg-white"
                    loading="lazy"
                  />
                  <div className="p-4 bg-slate-100">
                    <h3 className="font-bold text-slate-900 mb-1">George Broughton III, CEO</h3>
                    <p className="text-sm text-slate-600">Founder, President &amp; Strategic Director</p>
                  </div>
                </div>

                {/* Sterile Mate Private-Label Gloves */}
                <div className="group relative rounded-xl overflow-hidden border border-slate-600 hover:border-emerald-500/50 transition-all">
                  <div className="absolute top-3 left-3 z-10 px-3 py-1 bg-slate-50 backdrop-blur-sm rounded-full text-xs text-emerald-600 font-medium">
                    Private-Label Gloves
                  </div>
                  <img
                    src={`${process.env.REACT_APP_BACKEND_URL}/api/static/gloves/sterilemate_nitrile_exam_hero.png`}
                    alt="Sterile Mate 3.5-mil + 4.0-mil Nitrile Exam Gloves"
                    className="w-full h-48 object-contain bg-white p-4"
                    loading="lazy"
                  />
                  <div className="p-4 bg-slate-100">
                    <h3 className="font-bold text-slate-900 mb-1">Nitrile Exam Gloves &mdash; Intco Direct Import</h3>
                    <p className="text-sm text-slate-600">3.5-mil + 4.0-mil, 200 pcs/box (2,000 gloves/master case)</p>
                  </div>
                </div>

                {/* Sterile Mate Microfiber */}
                <div className="group relative rounded-xl overflow-hidden border border-slate-600 hover:border-blue-500/50 transition-all">
                  <div className="absolute top-3 left-3 z-10 px-3 py-1 bg-slate-50 backdrop-blur-sm rounded-full text-xs text-blue-900 font-medium">
                    Private-Label Microfiber
                  </div>
                  <img
                    src={`${process.env.REACT_APP_BACKEND_URL}/api/static/microfiber-rags/hero_branded_blue.png`}
                    alt="Sterile Mate Disposable Microfiber Cloths"
                    className="w-full h-48 object-contain bg-white p-4"
                    loading="lazy"
                  />
                  <div className="p-4 bg-slate-100">
                    <h3 className="font-bold text-slate-900 mb-1">Disposable Microfiber &mdash; Texas Microfiber Partnership</h3>
                    <p className="text-sm text-slate-600">50-count pop-up box &middot; Blue, White, Black &middot; 12&Prime; &times; 12&Prime;</p>
                  </div>
                </div>
              </div>

              {/* Compliance Badges */}
              <div className="mt-6 p-4 bg-white rounded-xl border border-slate-200">
                <p className="text-sm text-slate-600 text-center mb-4">Production Compliance & Certifications</p>
                <div className="flex flex-wrap justify-center gap-6 items-center">
                  <img src="https://sterilemate.com/wp-content/uploads/2023/09/iso.webp" alt="ISO" className="h-10 opacity-80 hover:opacity-100 transition-opacity" />
                  <img src="https://sterilemate.com/wp-content/uploads/2023/09/astm.webp" alt="ASTM" className="h-10 opacity-80 hover:opacity-100 transition-opacity" />
                  <img src="https://sterilemate.com/wp-content/uploads/2023/09/ce.webp" alt="CE" className="h-10 opacity-80 hover:opacity-100 transition-opacity" />
                  <img src="https://sterilemate.com/wp-content/uploads/2023/09/fda.webp" alt="FDA" className="h-10 opacity-80 hover:opacity-100 transition-opacity" />
                  <img src="https://sterilemate.com/wp-content/uploads/2023/09/sgs.webp" alt="SGS" className="h-10 opacity-80 hover:opacity-100 transition-opacity" />
                  <img src="https://sterilemate.com/wp-content/uploads/2022/11/hub-certified.jpg" alt="HUB Certified" className="h-12 opacity-80 hover:opacity-100 transition-opacity rounded" />
                </div>
              </div>

              {/* Visit Website CTA */}
              <div className="mt-6 text-center">
                <a 
                  href="https://sterilemate.com" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-blue-900 hover:from-cyan-500 hover:to-blue-500 text-white font-semibold rounded-xl transition-all"
                >
                  <Globe className="w-5 h-5" />
                  Visit SterileMate.com
                  <ExternalLink className="w-4 h-4" />
                </a>
              </div>
            </div>

            {/* News & Updates from SterileMate.com */}
            <div className="bg-white/50 rounded-2xl p-6 border border-slate-200">
              <h2 className="text-2xl font-bold mb-6 flex items-center gap-3">
                <FileText className="w-6 h-6 text-blue-900" />
                Recent Company News
              </h2>
              <div className="grid md:grid-cols-3 gap-4">
                <div className="bg-white rounded-xl p-4 border border-slate-200 hover:border-slate-200 transition-all">
                  <div className="text-xs text-blue-900 mb-2">March 2026</div>
                  <h3 className="font-bold text-slate-900 mb-2">CEO George Broughton III Announces HQ Acquisition</h3>
                  <p className="text-sm text-slate-600">Securing {scenario.id === "humble" ? "10,766 SF freestanding building on US-59" : scenario.id === "ronan" ? "22,750 SF Class-A 3-story HQ on 5.08 acres in Greenspoint" : "the 87,282 SF \u201C2SIXTY3\u201D Class-B campus on Beltway 8"} for B2B + retail healthcare operations</p>
                </div>
                <div className="bg-white rounded-xl p-4 border border-slate-200 hover:border-slate-200 transition-all">
                  <div className="text-xs text-blue-900 mb-2">November 2025</div>
                  <h3 className="font-bold text-slate-900 mb-2">Consumables Division Launch — Gloves + Microfiber</h3>
                  <p className="text-sm text-slate-600">Direct-import private-label nitrile exam gloves (Intco Global) and disposable microfiber cloths (Texas Microfiber) targeted for 2027 acute-care roll-out</p>
                </div>
                <div className="bg-white rounded-xl p-4 border border-slate-200 hover:border-slate-200 transition-all">
                  <div className="text-xs text-blue-900 mb-2">October 2025</div>
                  <h3 className="font-bold text-slate-900 mb-2">Statewide B2B Marketing Launch Approved</h3>
                  <p className="text-sm text-slate-600">$180K dedicated Texas-wide digital + medical-expo + trade-show campaign approved for 2027 rollout</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ============================================= */}
        {/* HQ EXPANSION SECTION — SBA Business Plan */}
        {/* ============================================= */}
        {activeSection === "operations" && (
          <div className="space-y-10 animate-fade-in">
            {/* Hero Banner */}
            <div className="relative bg-white rounded-3xl overflow-hidden border border-slate-200 p-8 sm:p-10">
              <div className="absolute top-0 right-0 w-64 h-64 bg-blue-500/10 rounded-full blur-3xl" />
              <div className="relative z-10">
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-blue-100 border border-slate-200 rounded-full text-blue-900 text-sm mb-6">
                  <Landmark className="w-4 h-4" />
                  SBA 7(a) Expansion &mdash; Real Estate Acquisition
                </div>
                <h1 className="text-4xl lg:text-5xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  B2B Operations Hub &mdash; Sterile Mate HQ
                </h1>
                <p className="text-xl text-slate-700 max-w-3xl mb-6">
                  Acquiring a <span className="text-blue-900 font-semibold">{scenario.id === "humble" ? "10,766 SF freestanding commercial building" : scenario.id === "ronan" ? "22,750 SF Class-A 3-story corporate HQ on 5.08 acres" : "87,282 SF Class-B \u201C2SIXTY3\u201D campus"}</span> to establish Sterile Mate&apos;s first fully integrated B2B Healthcare Solutions Headquarters — combining showroom, training, logistics, {scenario.id === "northsam" ? "AskIsaac engineering, and multi-tenant rental income" : scenario.id === "ronan" ? "DME + Insurance Billing, a 3,500 SF short-stock warehouse, and a Texas-metro expansion sales office" : "and retail"} under one roof.
                </p>
                <div className="grid sm:grid-cols-4 gap-4">
                  <div className="bg-white rounded-xl p-4 border border-blue-500/20 text-center">
                    <div className="text-2xl font-bold text-blue-900">{scenario.id === "humble" ? "10,766 SF" : scenario.id === "ronan" ? "22,750 SF" : "87,282 SF"}</div>
                    <div className="text-xs text-slate-600 mt-1">{scenario.id === "humble" ? "Freestanding Building" : scenario.id === "ronan" ? "Class-A 3-Story HQ" : "3-Story Class-B Campus"}</div>
                  </div>
                  <div className="bg-white rounded-xl p-4 border border-emerald-500/20 text-center">
                    <div className="text-2xl font-bold text-emerald-600">{scenario.id === "humble" ? "40K SF" : scenario.id === "ronan" ? "5.08 Acres" : "213"}</div>
                    <div className="text-xs text-slate-600 mt-1">{scenario.id === "humble" ? "Lot (0.92 Acres)" : scenario.id === "ronan" ? "Lot + 2.84-acre growth pad" : "Surface + Garage Spaces"}</div>
                  </div>
                  <div className="bg-white rounded-xl p-4 border border-slate-200 text-center">
                    <div className="text-2xl font-bold text-slate-800">{scenario.id === "humble" ? "168K" : scenario.id === "ronan" ? "200K+" : "180K"}</div>
                    <div className="text-xs text-slate-600 mt-1">{scenario.id === "humble" ? "Cars/Day (US-59)" : scenario.id === "ronan" ? "Cars/Day (Beltway 8 + I-45)" : "Cars/Day (Beltway 8)"}</div>
                  </div>
                  <div className="bg-white rounded-xl p-4 border border-cyan-500/20 text-center">
                    <div className="text-2xl font-bold text-blue-900">${(scenario.asking / 1_000_000).toFixed(1)}M</div>
                    <div className="text-xs text-slate-600 mt-1">Purchase Price</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Property Location & Strategic Value */}
            <div className="bg-white/50 border border-slate-200 rounded-2xl p-8" data-testid="property-location-section">
              <h2 className="text-2xl font-bold mb-4 flex items-center gap-3">
                <MapPin className="w-6 h-6 text-blue-900" />
                {scenario.address}
              </h2>
              {scenario.id === "humble" ? (
                <p className="text-slate-700 mb-6">
                  Strategically positioned adjacent to Deerbrook Mall in one of the highest-traffic retail corridors in Northeast Houston. Surrounded by Walmart, Home Depot, Sam&apos;s Club, Total Wine, and Chick-fil-A. Household incomes of $77K&ndash;$90K and total consumer spending up to $1.3B within a 5-mile radius.
                </p>
              ) : scenario.id === "ronan" ? (
                <p className="text-slate-700 mb-6">
                  Class-A corporate office at the intersection of Beltway 8 and I-45 in the Greenspoint corridor &mdash; 1.5 miles from the ExxonMobil campus, 8 minutes from George Bush Intercontinental Airport (IAH). Three-story brick-clad executive headquarters on a 5.08-acre site, with <span className="text-blue-900 font-semibold">2.84 acres of contiguous expansion land reserved for a future Distribution Center / Medical Device Packaging Center</span> and an additional 2.23 acres for fleet parking or pad-site monetization. Existing build-out is move-in ready, requiring only minor renovations to consolidate Sterile Mate operations across all three floors &mdash; Floor 1 HQ + showroom + training center, Floor 2 with a 3,500 SF short-stock warehouse plus the Texas-metro expansion sales office, and Floor 3 executive suite &amp; boardroom.
                </p>
              ) : (
                <p className="text-slate-700 mb-6">
                  Class-A office on the Beltway 8 (North Sam Houston Pkwy E) corridor — direct freeway exposure with 200K+ daily vehicles, immediate access to George Bush Intercontinental Airport (12 min) and the Texas Medical Center via I-45. The North Houston District corridor is the fastest-growing healthcare-services submarket in greater Houston, with $4.8B in healthcare construction underway within a 10-mile radius.
                </p>
              )}
              <div className="grid md:grid-cols-3 gap-4">
                {scenario.id === "humble" ? (
                  <>
                    <div className="bg-slate-100 rounded-xl p-5 border border-slate-200/30">
                      <div className="text-blue-900 font-bold text-lg mb-1">Prime Visibility</div>
                      <p className="text-slate-600 text-sm">Direct access to FM 1960 &amp; US-59/I-69 with 39K&ndash;168K vehicles per day. High-visibility freestanding building in a major retail hub.</p>
                    </div>
                    <div className="bg-slate-100 rounded-xl p-5 border border-slate-200/30">
                      <div className="text-emerald-600 font-bold text-lg mb-1">Value-Add Asset</div>
                      <p className="text-slate-600 text-sm">Current tenant on month-to-month lease — immediate availability. Building can be fully repurposed for dual B2B + retail healthcare operations.</p>
                    </div>
                    <div className="bg-slate-100 rounded-xl p-5 border border-slate-200/30">
                      <div className="text-slate-800 font-bold text-lg mb-1">Market Strength</div>
                      <p className="text-slate-600 text-sm">Centralized commercial hub of NE Harris County. 13K&ndash;51K daytime employees. Proximity to George Bush Intercontinental Airport.</p>
                    </div>
                  </>
                ) : scenario.id === "ronan" ? (
                  <>
                    <div className="bg-slate-100 rounded-xl p-5 border border-slate-200/30">
                      <div className="text-blue-900 font-bold text-lg mb-1">Corporate Corridor</div>
                      <p className="text-slate-600 text-sm">Direct Beltway 8 frontage with 200K+ daily vehicles. ExxonMobil headquarters 1.5 miles north. Surrounded by Class-A office, retail, and hospitality.</p>
                    </div>
                    <div className="bg-slate-100 rounded-xl p-5 border border-slate-200/30">
                      <div className="text-emerald-600 font-bold text-lg mb-1">Texas Expansion Sales Office</div>
                      <p className="text-slate-600 text-sm">Floor 2 (12,640 SF) houses a 3,500 SF short-stock warehouse for on-the-go items and janitorial disposables (daily hospital distribution), plus the Texas-metro expansion sales office covering San Antonio, Dallas, Austin, El Paso, Fort Worth, Galveston, and Waco.</p>
                    </div>
                    <div className="bg-slate-100 rounded-xl p-5 border border-slate-200/30">
                      <div className="text-slate-800 font-bold text-lg mb-1">5.08-Acre Site</div>
                      <p className="text-slate-600 text-sm">22,750 SF Class-A building plus 5.07 acres of platted land &mdash; <span className="text-blue-900 font-semibold">2.84 acres reserved for a future build-to-suit Distribution Center / Medical Device Packaging Center</span>, plus 2.23 acres for fleet parking or pad-site monetization. Scale without re-platting risk or new real-estate acquisition.</p>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="bg-slate-100 rounded-xl p-5 border border-slate-200/30">
                      <div className="text-blue-900 font-bold text-lg mb-1">Beltway 8 Visibility</div>
                      <p className="text-slate-600 text-sm">Frontage on North Sam Houston Pkwy E with 200K+ daily vehicles. Lit signage opportunity directly visible to airport-bound traffic.</p>
                    </div>
                    <div className="bg-slate-100 rounded-xl p-5 border border-slate-200/30">
                      <div className="text-emerald-600 font-bold text-lg mb-1">Class-A Footprint</div>
                      <p className="text-slate-600 text-sm">Multi-story Class-A office building — supports a full HQ, training center, AskIsaac engineering team, and dedicated investor &amp; client experience floor.</p>
                    </div>
                    <div className="bg-slate-100 rounded-xl p-5 border border-slate-200/30">
                      <div className="text-slate-800 font-bold text-lg mb-1">Healthcare Corridor</div>
                      <p className="text-slate-600 text-sm">North Houston District — $4.8B of healthcare construction underway within 10 miles. Direct I-45 access to the Texas Medical Center.</p>
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Building Visual — Plan A: post-renovation rendering · Plan B: real building photos */}
            <div className="bg-white/50 border border-slate-200 rounded-2xl overflow-hidden" data-testid="building-visual-section">
              {scenario.id === "humble" ? (
                <div className="relative">
                  <img
                    src={scenario.rendering}
                    alt={`Sterile Mate HQ — ${scenario.address} (Post-Renovation Rendering)`}
                    className="w-full h-auto object-cover"
                    data-testid="building-rendering-image"
                  />
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-white via-slate-50/80 to-transparent p-6 sm:p-8">
                    <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-blue-100 border border-slate-200 rounded-full text-blue-900 text-xs font-semibold mb-3">
                      <Eye className="w-3 h-3" />
                      POST-RENOVATION RENDERING · {scenario.label.toUpperCase()}
                    </div>
                    <h3 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-1">Sterile Mate Headquarters</h3>
                    <p className="text-slate-700 text-sm">{scenario.address} — {scenario.sqft}</p>
                  </div>
                </div>
              ) : (
                <div className="p-6 sm:p-8">
                  <div className="flex items-center justify-between flex-wrap gap-3 mb-5">
                    <div>
                      <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-blue-100 border border-slate-200 rounded-full text-blue-900 text-[10px] sm:text-xs font-semibold mb-2">
                        <Building className="w-3 h-3" />
                        {scenario.galleryLabel || "ACTUAL BUILDING PHOTOS"}
                      </div>
                      <h3 className="text-2xl sm:text-3xl font-bold text-slate-900">{scenario.headline}</h3>
                      <p className="text-slate-600 text-sm mt-1">{scenario.address} — {scenario.sqft}</p>
                    </div>
                    <div className="flex flex-col items-end gap-1 text-xs">
                      {scenario.listingUrl && (
                        <a
                          href={scenario.listingUrl}
                          target="_blank"
                          rel="noreferrer"
                          className="text-emerald-700 hover:text-emerald-200 underline-offset-2 hover:underline"
                          data-testid="property-listing-link"
                        >
                          {scenario.listingLabel || "View original listing →"}
                        </a>
                      )}
                      {scenario.asbuiltUrl && (
                        <a
                          href={scenario.asbuiltUrl}
                          target="_blank"
                          rel="noreferrer"
                          className="text-blue-900 hover:text-cyan-200 underline-offset-2 hover:underline"
                          data-testid="property-asbuilt-link"
                        >
                          {scenario.asbuiltLabel}
                        </a>
                      )}
                      {scenario.proformaUrl && (
                        <a
                          href={scenario.proformaUrl}
                          target="_blank"
                          rel="noreferrer"
                          className="text-slate-800 hover:text-slate-800 underline-offset-2 hover:underline font-semibold"
                          data-testid="property-proforma-link"
                        >
                          {scenario.proformaLabel}
                        </a>
                      )}
                    </div>
                  </div>

                  {/* Hero exterior */}
                  <div className="rounded-xl overflow-hidden border border-slate-200 mb-4">
                    <img
                      src={scenario.gallery[0].src}
                      alt={scenario.gallery[0].caption}
                      className="w-full h-auto object-cover"
                      data-testid="building-hero-image"
                    />
                  </div>

                  {/* Gallery grid */}
                  <div className="grid grid-cols-2 lg:grid-cols-3 gap-3" data-testid="building-gallery">
                    {scenario.gallery.slice(1).map((shot, i) => (
                      <figure
                        key={i}
                        className="group relative rounded-lg overflow-hidden border border-slate-200 bg-slate-50"
                      >
                        <img
                          src={shot.src}
                          alt={shot.caption}
                          className="w-full h-40 sm:h-48 object-cover transition-transform duration-500 group-hover:scale-105"
                        />
                        <figcaption className="absolute bottom-0 left-0 right-0 px-3 py-2 bg-gradient-to-t from-slate-100/95 via-slate-950/80 to-transparent text-[11px] text-slate-700 leading-snug">
                          {shot.caption}
                        </figcaption>
                      </figure>
                    ))}
                  </div>

                  {/* Property facts */}
                  <div className="mt-6 pt-6 border-t border-slate-200">
                    <h4 className="text-sm font-bold tracking-[0.18em] uppercase text-emerald-700 mb-3">Property Facts</h4>
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3" data-testid="property-facts">
                      {scenario.facts.map((f) => (
                        <div key={f.label} className="bg-slate-100 rounded-lg p-3 border border-slate-200/40">
                          <div className="text-[10px] uppercase tracking-wider text-slate-600 mb-1">{f.label}</div>
                          <div className="text-sm font-bold text-slate-900 leading-tight">{f.value}</div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Amenities */}
                  <div className="mt-6">
                    <h4 className="text-sm font-bold tracking-[0.18em] uppercase text-emerald-700 mb-3">Amenities & Highlights</h4>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2" data-testid="property-amenities">
                      {scenario.amenities.map((a) => (
                        <div key={a} className="flex items-start gap-2 text-sm text-slate-700">
                          <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />
                          <span>{a}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Plan C ONLY — Floor 2 Anchor Salon-Suite Tenant */}
            {scenario.id === "ronan" && scenario.secondaryTenant && (
              <div
                className="bg-white rounded-2xl p-8 border border-pink-500/30"
                data-testid="salon-suite-tenant-section"
              >
                <div className="flex items-start gap-4 mb-5 flex-wrap">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-900 to-blue-900 flex items-center justify-center shrink-0">
                    <Store className="w-6 h-6 text-slate-900" />
                  </div>
                  <div className="flex-1 min-w-[260px]">
                    <h2 className="text-2xl font-bold text-slate-900">{scenario.secondaryTenant.name}</h2>
                    <p className="text-pink-300 text-sm font-medium mt-1">{scenario.secondaryTenant.tagline}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-[10px] uppercase tracking-wider text-slate-600">Projected Annual NNN Income</p>
                    <p className="text-2xl font-bold text-emerald-600">
                      {fmtUSD(scenario.secondaryTenant.lease_estimate.annual_low)} &ndash; {fmtUSD(scenario.secondaryTenant.lease_estimate.annual_high)}
                    </p>
                  </div>
                </div>
                <p className="text-slate-700 mb-5">{scenario.secondaryTenant.summary}</p>
                <div className="grid md:grid-cols-2 gap-3 mb-5">
                  {scenario.secondaryTenant.bullets.map((b, i) => (
                    <div key={i} className="flex items-start gap-2 text-sm text-slate-700 bg-slate-50 rounded-lg p-3 border border-pink-500/10">
                      <CheckCircle2 className="w-4 h-4 text-pink-400 mt-0.5 shrink-0" />
                      <span>{b}</span>
                    </div>
                  ))}
                </div>
                <div className="grid sm:grid-cols-3 gap-3 pt-5 border-t border-pink-500/20">
                  <div className="bg-slate-100 rounded-lg p-3 border border-pink-500/20">
                    <p className="text-[10px] uppercase tracking-wider text-pink-300 mb-1">Leasable Area</p>
                    <p className="text-sm font-bold text-slate-900">{scenario.secondaryTenant.lease_estimate.sf}</p>
                  </div>
                  <div className="bg-slate-100 rounded-lg p-3 border border-pink-500/20">
                    <p className="text-[10px] uppercase tracking-wider text-pink-300 mb-1">Market Rate</p>
                    <p className="text-sm font-bold text-slate-900">{scenario.secondaryTenant.lease_estimate.rate}</p>
                  </div>
                  <div className="bg-slate-100 rounded-lg p-3 border border-pink-500/20">
                    <p className="text-[10px] uppercase tracking-wider text-pink-300 mb-1">DSCR Impact</p>
                    <p className="text-sm font-bold text-emerald-700">Lifts Y2 DSCR to {scenario.dscrRangeY2}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Plan C ONLY — Growth Potential: 2.84-acre future Distribution / Medical Device Packaging Center */}
            {scenario.id === "ronan" && (
              <div
                className="bg-white rounded-2xl p-8 border border-slate-200"
                data-testid="ronan-growth-potential-section"
              >
                <div className="flex items-start gap-4 mb-5 flex-wrap">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-900 to-blue-900 flex items-center justify-center shrink-0">
                    <Truck className="w-6 h-6 text-slate-900" />
                  </div>
                  <div className="flex-1 min-w-[260px]">
                    <h2 className="text-2xl font-bold text-slate-900">Growth Potential &mdash; 2.84-Acre Expansion Pad</h2>
                    <p className="text-blue-900 text-sm font-medium mt-1">Future Distribution Center &amp; Medical Device Packaging Center &mdash; built on land we already own</p>
                  </div>
                  <div className="text-right">
                    <p className="text-[10px] uppercase tracking-wider text-slate-600">Expansion Land Included</p>
                    <p className="text-2xl font-bold text-blue-900">2.84 Acres</p>
                    <p className="text-[11px] text-slate-600">+ 2.23 acres flex pad (Tract B)</p>
                  </div>
                </div>

                <p className="text-slate-700 mb-5">
                  The 16651 Ronan Rd site includes <span className="text-blue-900 font-semibold">2.84 platted, contiguous acres (Tract A)</span> directly adjacent to the existing Class-A HQ &mdash; <span className="text-slate-900 font-semibold">reserved as a future build-to-suit Distribution Center / Medical Device Packaging Center</span>. As Sterile Mate scales its <span className="text-emerald-700 font-semibold">B2B healthcare logistics</span> and its <span className="text-emerald-700 font-semibold">Online DME retail fulfillment</span> for hospitals, surgery centers, and direct-to-patient Medicare/Medicaid clients, this tract eliminates the need to identify, lease, or acquire a separate warehouse property. Every dollar of future expansion CapEx compounds back into the already-collateralized SBA asset &mdash; <span className="text-slate-900 font-semibold">strengthening the lender&rsquo;s LTV and lifting long-term ROI</span>.
                </p>

                <div className="grid md:grid-cols-3 gap-3 mb-5">
                  {[
                    { icon: Package, title: "Medical Device Packaging Center", desc: "On-site repackaging, kitting, sterile-pack labeling, and lot/serial tracking for Sterile Mate B2B contracts and Supply Doctor retail — reduces 3PL fees and accelerates fulfillment SLAs." },
                    { icon: Truck, title: "DME / Online Retail Distribution", desc: "Build-to-suit warehouse + dock-high loading to support The Supply Doctor online DME orders, Medicare/Medicaid drop-ship, and direct-to-patient mobility shipments." },
                    { icon: TrendingUp, title: "Lender-Friendly Compounding ROI", desc: "Every future CapEx dollar lands on the same SBA-collateralized parcel — LTV improves, debt service is covered by existing tenants, and bank exposure stays inside one title." },
                  ].map((item, i) => (
                    <div key={i} className="bg-slate-50 rounded-xl p-4 border border-blue-500/15">
                      <item.icon className="w-5 h-5 text-blue-900 mb-2" />
                      <p className="text-slate-900 font-semibold text-sm mb-1">{item.title}</p>
                      <p className="text-slate-600 text-xs leading-relaxed">{item.desc}</p>
                    </div>
                  ))}
                </div>

                <div className="grid sm:grid-cols-4 gap-3 pt-5 border-t border-blue-500/20">
                  <div className="bg-slate-100 rounded-lg p-3 border border-blue-500/20">
                    <p className="text-[10px] uppercase tracking-wider text-blue-900 mb-1">Future Use</p>
                    <p className="text-sm font-bold text-slate-900">Distribution / Packaging</p>
                  </div>
                  <div className="bg-slate-100 rounded-lg p-3 border border-blue-500/20">
                    <p className="text-[10px] uppercase tracking-wider text-blue-900 mb-1">Acquisition Cost</p>
                    <p className="text-sm font-bold text-emerald-700">$0 (already owned)</p>
                  </div>
                  <div className="bg-slate-100 rounded-lg p-3 border border-blue-500/20">
                    <p className="text-[10px] uppercase tracking-wider text-blue-900 mb-1">Collateral Effect</p>
                    <p className="text-sm font-bold text-slate-900">Compounds LTV</p>
                  </div>
                  <div className="bg-slate-100 rounded-lg p-3 border border-blue-500/20">
                    <p className="text-[10px] uppercase tracking-wider text-blue-900 mb-1">Airport (IAH)</p>
                    <p className="text-sm font-bold text-slate-900">8 minutes</p>
                  </div>
                </div>
              </div>
            )}

            {/* PHASE I / II — Ronan Road plan puts DME + Insurance in Phase I,
                drops The Supply Doctor entirely, and moves regional expansion
                + Healthcare Construction bidding into Phase II. Other plans
                keep the original Supply-Doctor-based rollout. */}
            {scenario.id === "ronan" ? (
              <>
                {/* PHASE I — Ronan: Executive HQ + DME/Insurance from Day 1 */}
                <div className="bg-white rounded-2xl p-8 border border-slate-200">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-12 h-12 rounded-xl bg-emerald-700 flex items-center justify-center">
                      <Building2 className="w-6 h-6 text-slate-900" />
                    </div>
                    <div>
                      <h2 className="text-2xl font-bold text-slate-900">Phase I (Year 1): Sterile Mate Executive HQ</h2>
                      <p className="text-emerald-600 text-sm font-medium">B2B HQ &bull; Showroom &bull; DME + Insurance Billing &bull; Training Center &bull; Warehouse &bull; Texas Expansion Sales Office</p>
                    </div>
                  </div>
                  <p className="text-slate-700 mb-6">
                    16651 Ronan Rd becomes Sterile Mate&apos;s <strong className="text-slate-900">true executive headquarters</strong> &mdash; the company stands on its own legs, no third-party retail DBA. Backorder sourcing is fully automated by AskIsaac, freeing the team to scale water purification, healthcare construction, DME, and the Texas-metro secondary-vendor footprint.
                  </p>
                  <div className="grid md:grid-cols-2 gap-4">
                    {[
                      { title: "Water Purification & EVS Showroom", desc: "AAMI ST108-compliant water purification, RO/DI, water softeners, EVS + infection-prevention equipment on display. Expanding water-purification sales now that we have a team with prior healthcare-construction experience.", icon: Droplets },
                      { title: "Expanded Backorder Sourcing", desc: "AskIsaac-automated national supplier network. Hospital RFQs on hard-to-find and allocated SKUs return priced quotes in 2 hours — the legacy manual grind is now an on-demand revenue engine.", icon: Search },
                      { title: "Healthcare Construction Sales Team", desc: "Dialysis rooms, nurseries, healthcare clinics, water-purification build-outs, and sterile-processing retrofits — new capability with a team experienced in healthcare construction.", icon: Landmark },
                      { title: "Client & Staff Training Center", desc: "Dedicated training space for hospital SPD teams, facility management, EVS leaders, and internal staff certification.", icon: Users },
                      { title: "$10K / mo Marketing Engine", desc: "Billboards, digital, healthcare trade shows (SPD, AHRMM, APIC), direct hospital outreach, and content marketing on AAMI ST108 compliance.", icon: Megaphone },
                      { title: "Floor 2: 3,500 SF Warehouse + Texas Expansion Sales Office", desc: "Short-stock warehouse for on-the-go items and janitorial disposables (daily hospital distribution). Sales office expands Sterile Mate&apos;s secondary-vendor footprint into San Antonio, Dallas, Austin, El Paso, Fort Worth, Galveston, and Waco now that backorder sourcing is fully automated.", icon: Truck },
                    ].map((item, i) => (
                      <div key={i} className="bg-white/50 rounded-lg p-4 border border-slate-200/30">
                        <div className="flex items-start gap-3">
                          <item.icon className="w-5 h-5 text-emerald-600 mt-0.5 flex-shrink-0" />
                          <div>
                            <h4 className="font-semibold text-slate-900 text-sm">{item.title}</h4>
                            <p className="text-slate-600 text-xs mt-1">{item.desc}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* PHASE II — Ronan: Regional expansion + Healthcare Construction bidding */}
                <div className="bg-white rounded-2xl p-8 border border-slate-200">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-12 h-12 rounded-xl bg-blue-900 flex items-center justify-center">
                      <Home className="w-6 h-6 text-slate-900" />
                    </div>
                    <div>
                      <h2 className="text-2xl font-bold text-slate-900">Phase II (Year 2+): Regional Expansion + Healthcare Construction Bidding</h2>
                      <p className="text-blue-900 text-sm font-medium">Gulf &amp; Southern-Region sales &bull; Local, State, County &amp; Federal project bidding</p>
                    </div>
                  </div>
                  <p className="text-slate-700 mb-6">
                    With Phase I revenue engines humming, Year 2+ shifts to <strong className="text-slate-900">geographic expansion</strong> and <strong className="text-slate-900">large-scale healthcare-construction bidding</strong>.
                  </p>
                  <div className="grid md:grid-cols-2 gap-4 mb-6">
                    <div className="bg-white/50 rounded-xl p-5 border border-violet-500/20">
                      <Truck className="w-8 h-8 text-blue-900 mb-3" />
                      <h4 className="font-semibold text-slate-900 mb-2">Gulf &amp; Southern-Region Sales &amp; Marketing</h4>
                      <p className="text-slate-600 text-sm">Expand sales and marketing into <strong className="text-slate-900">Louisiana, Mississippi, and Alabama</strong> — leveraging the fully automated backorder-sourcing platform to serve hospitals across the Gulf and southern states.</p>
                    </div>
                    <div className="bg-white/50 rounded-xl p-5 border border-violet-500/20">
                      <Landmark className="w-8 h-8 text-blue-900 mb-3" />
                      <h4 className="font-semibold text-slate-900 mb-2">Expanded Healthcare Construction Bidding</h4>
                      <p className="text-slate-600 text-sm">Bid <strong className="text-slate-900">Local, State, County, and Federal</strong> healthcare-construction projects — dialysis rooms, nurseries, clinics, water-purification installations, sterile-processing build-outs, and infection-prevention retrofits.</p>
                    </div>
                  </div>
                  <div className="bg-violet-500/10 border border-violet-500/20 rounded-xl p-5">
                    <h4 className="text-blue-900 font-semibold mb-3">Strategic Value of Phase II</h4>
                    <div className="grid md:grid-cols-3 gap-4">
                      <div className="flex items-start gap-2">
                        <CheckCircle2 className="w-4 h-4 text-blue-900 mt-0.5 flex-shrink-0" />
                        <span className="text-slate-700 text-sm"><strong className="text-slate-900">Multi-state footprint</strong> across TX &bull; LA &bull; MS &bull; AL</span>
                      </div>
                      <div className="flex items-start gap-2">
                        <CheckCircle2 className="w-4 h-4 text-blue-900 mt-0.5 flex-shrink-0" />
                        <span className="text-slate-700 text-sm"><strong className="text-slate-900">Government contract revenue</strong> &mdash; long payment cycles, high stability</span>
                      </div>
                      <div className="flex items-start gap-2">
                        <CheckCircle2 className="w-4 h-4 text-blue-900 mt-0.5 flex-shrink-0" />
                        <span className="text-slate-700 text-sm"><strong className="text-slate-900">DSCR 3.24x &rarr; 11.21x</strong> &mdash; substantially exceeds SBA 504 1.15x&ndash;1.25x minimums across all 3 years</span>
                      </div>
                    </div>
                  </div>
                </div>
              </>
            ) : (
              <>
                {/* PHASE I: B2B HQ + The Supply Doctor (Plans A & B) */}
                <div className="bg-white rounded-2xl p-8 border border-slate-200">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-xl bg-emerald-700 flex items-center justify-center">
                  <Building2 className="w-6 h-6 text-slate-900" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-slate-900">Phase I: B2B Headquarters + Retail Launch</h2>
                  <p className="text-emerald-600 text-sm font-medium">Sterile Mate HQ + The Supply Doctor (DBA)</p>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6 mb-6">
                {/* B2B Operations */}
                <div>
                  <h3 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
                    <Building2 className="w-5 h-5 text-blue-900" />
                    B2B Operations Hub (Sterile Mate HQ)
                  </h3>
                  <div className="space-y-3">
                    {[
                      { title: "Healthcare Solutions Showroom", desc: "Demonstrate AAMI ST108-compliant water purification, RO/DI systems, water softeners, EVS equipment, and infection prevention solutions. Transforms sales from conceptual to experiential.", icon: Eye },
                      { title: "Client Training & Demonstration Center", desc: "Dedicated training for hospital SPD teams, facility management, EVS leadership, and vendor product demos. Positions Sterile Mate as a trusted clinical partner.", icon: Users },
                      { title: "Inventory & Logistics Hub", desc: "On-site critical components, pre-staged water purification systems, rapid deployment capability. Eliminates lead times and third-party warehousing dependency.", icon: Truck },
                      { title: "Project Coordination Center", desc: "Centralized project management, technician staging, and field mobilization. Higher project throughput and revenue capacity.", icon: Target },
                    ].map((item, i) => (
                      <div key={i} className="bg-white/50 rounded-lg p-4 border border-slate-200/30">
                        <div className="flex items-start gap-3">
                          <item.icon className="w-5 h-5 text-blue-900 mt-0.5 flex-shrink-0" />
                          <div>
                            <h4 className="font-semibold text-slate-900 text-sm">{item.title}</h4>
                            <p className="text-slate-600 text-xs mt-1">{item.desc}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Retail — The Supply Doctor */}
                <div>
                  <h3 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
                    <Store className="w-5 h-5 text-emerald-600" />
                    Retail Division: The Supply Doctor (DBA)
                  </h3>
                  <div className="bg-white border border-emerald-500/20 rounded-xl p-5 mb-4">
                    <p className="text-slate-700 text-sm">
                      <strong className="text-slate-900">The Supply Doctor</strong> operates as a community-facing medical supply and mobility solutions provider — offering both cash-pay retail products and insurance-supported durable medical equipment (DME).
                    </p>
                  </div>
                  <div className="space-y-3">
                    {[
                      "Mobility equipment (wheelchairs, scooters, walkers)",
                      "Orthopedic braces (fitted by certified professional)",
                      "Rehabilitation equipment",
                      "Home health devices (BP monitors, oximeters, scales)",
                      "Orthopedic footwear",
                      "PPE, wound care, and infection prevention supplies",
                    ].map((item, i) => (
                      <div key={i} className="flex items-center gap-2 text-sm">
                        <CheckCircle2 className="w-4 h-4 text-emerald-600 flex-shrink-0" />
                        <span className="text-slate-700">{item}</span>
                      </div>
                    ))}
                  </div>
                  <div className="mt-4 bg-emerald-500/10 border border-emerald-500/20 rounded-lg p-4">
                    <p className="text-emerald-600 text-sm font-semibold">Revenue Impact</p>
                    <p className="text-slate-600 text-xs mt-1">Immediate foot traffic revenue from Day 1. Highway-visible retail location in $1.3B consumer spending corridor. Cash-pay + insurance-reimbursed sales.</p>
                  </div>
                </div>
              </div>
            </div>

            {/* PHASE II: DME Depot + Insurance Billing */}
            <div className="bg-white rounded-2xl p-8 border border-slate-200">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-xl bg-blue-900 flex items-center justify-center">
                  <Home className="w-6 h-6 text-slate-900" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-slate-900">Phase II: DME Depot &amp; Insurance Billing</h2>
                  <p className="text-blue-900 text-sm font-medium">Vive Health &bull; MHC Medical &bull; Medicare/Private Insurance</p>
                </div>
              </div>

              <p className="text-slate-700 mb-6">
                Phase II introduces a <strong className="text-slate-900">Durable Medical Equipment (DME) Distribution, Showroom, and Billing Operation</strong> — expanding revenue streams into insurance-reimbursed healthcare products and creating recurring revenue.
              </p>

              <div className="grid md:grid-cols-3 gap-4 mb-6">
                <div className="bg-white/50 rounded-xl p-5 border border-violet-500/20">
                  <Store className="w-8 h-8 text-blue-900 mb-3" />
                  <h4 className="font-semibold text-slate-900 mb-2">DME Showroom &amp; Fitting</h4>
                  <p className="text-slate-600 text-sm">Dedicated showroom and fitting area for mobility equipment, rehabilitation gear, and orthopedic products. Professional fittings by certified staff.</p>
                </div>
                <div className="bg-white/50 rounded-xl p-5 border border-violet-500/20">
                  <Package className="w-8 h-8 text-blue-900 mb-3" />
                  <h4 className="font-semibold text-slate-900 mb-2">Distribution Partnerships</h4>
                  <p className="text-slate-600 text-sm">Storage and distribution for <strong className="text-slate-900">Vive Health</strong> and <strong className="text-slate-900">MHC Medical</strong> products. Authorized distributor of premium DME brands.</p>
                </div>
                <div className="bg-white/50 rounded-xl p-5 border border-violet-500/20">
                  <FileCheck className="w-8 h-8 text-blue-900 mb-3" />
                  <h4 className="font-semibold text-slate-900 mb-2">Insurance Billing Capability</h4>
                  <p className="text-slate-600 text-sm">Medicare and private insurance billing. Physician order processing, intake verification, and claims submission. Recurring revenue from reimbursed products.</p>
                </div>
              </div>

                <div className="bg-violet-500/10 border border-violet-500/20 rounded-xl p-5">
                  <h4 className="text-blue-900 font-semibold mb-3">Strategic Value of Phase II</h4>
                  <div className="grid md:grid-cols-3 gap-4">
                    <div className="flex items-start gap-2">
                      <CheckCircle2 className="w-4 h-4 text-blue-900 mt-0.5 flex-shrink-0" />
                      <span className="text-slate-700 text-sm"><strong className="text-slate-900">Recurring revenue</strong> from insurance-reimbursed products</span>
                    </div>
                    <div className="flex items-start gap-2">
                      <CheckCircle2 className="w-4 h-4 text-blue-900 mt-0.5 flex-shrink-0" />
                      <span className="text-slate-700 text-sm"><strong className="text-slate-900">Higher margins</strong> on reimbursed DME vs. cash-pay retail</span>
                    </div>
                    <div className="flex items-start gap-2">
                      <CheckCircle2 className="w-4 h-4 text-blue-900 mt-0.5 flex-shrink-0" />
                      <span className="text-slate-700 text-sm"><strong className="text-slate-900">Provider status</strong> — not just retailer, healthcare-integrated</span>
                    </div>
                  </div>
                </div>
              </div>
              </>
            )}

            {/* Marketing Strategy */}
            <div className="bg-white/50 border border-slate-200 rounded-2xl p-8">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-xl bg-slate-800 flex items-center justify-center">
                  <Megaphone className="w-6 h-6 text-slate-900" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-slate-900">Marketing &amp; Customer Acquisition Strategy</h2>
                  <p className="text-slate-800 text-sm font-medium">$10,000/month &bull; $120,000 annually</p>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6 mb-6">
                <div className="bg-white border border-blue-500/20 rounded-xl p-6">
                  <h3 className="text-blue-900 font-bold mb-3 flex items-center gap-2">
                    <Building2 className="w-5 h-5" />
                    B2B Marketing &mdash; $6,000/mo
                  </h3>
                  <ul className="space-y-2 text-sm text-slate-700">
                    <li className="flex items-start gap-2"><CheckCircle2 className="w-3.5 h-3.5 text-blue-900 mt-0.5 flex-shrink-0" />Healthcare trade shows (SPD, AHRMM, APIC)</li>
                    <li className="flex items-start gap-2"><CheckCircle2 className="w-3.5 h-3.5 text-blue-900 mt-0.5 flex-shrink-0" />Direct outreach to hospital systems</li>
                    <li className="flex items-start gap-2"><CheckCircle2 className="w-3.5 h-3.5 text-blue-900 mt-0.5 flex-shrink-0" />Digital campaigns targeting facility managers, SPD leaders, infection prevention</li>
                    <li className="flex items-start gap-2"><CheckCircle2 className="w-3.5 h-3.5 text-blue-900 mt-0.5 flex-shrink-0" />AAMI ST108 compliance content marketing</li>
                  </ul>
                </div>
                <div className="bg-white border border-emerald-500/20 rounded-xl p-6">
                  <h3 className="text-emerald-600 font-bold mb-3 flex items-center gap-2">
                    <Store className="w-5 h-5" />
                    Retail Marketing &mdash; $4,000/mo
                  </h3>
                  <ul className="space-y-2 text-sm text-slate-700">
                    <li className="flex items-start gap-2"><CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 mt-0.5 flex-shrink-0" />Google Ads (local search dominance)</li>
                    <li className="flex items-start gap-2"><CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 mt-0.5 flex-shrink-0" />Billboard advertising (highway visibility on US-59)</li>
                    <li className="flex items-start gap-2"><CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 mt-0.5 flex-shrink-0" />Local healthcare referrals (clinics, physicians)</li>
                    <li className="flex items-start gap-2"><CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 mt-0.5 flex-shrink-0" />Community outreach (senior centers, rehab facilities)</li>
                  </ul>
                </div>
              </div>

              <div className="bg-slate-100 border border-slate-200 rounded-lg p-4">
                <p className="text-slate-800 text-sm font-semibold mb-1">Strategic Intent (SBA-Critical)</p>
                <p className="text-slate-700 text-sm">The company is proactively investing in customer acquisition to accelerate revenue growth, reduce time-to-market, and establish brand dominance in both B2B healthcare solutions and retail medical supply sectors. This is intentional growth investment — not guesswork.</p>
              </div>
            </div>

            {/* SBA Use of Funds */}
            <div className="bg-white/50 border border-slate-200 rounded-2xl p-8" data-testid="sba-use-of-funds">
              <h2 className="text-2xl font-bold mb-2 flex items-center gap-3">
                <PieChart className="w-6 h-6 text-emerald-600" />
                SBA {scenario.loanType} Use of Funds &mdash; ${(scenario.total / 1_000_000).toFixed(2)}M Total
              </h2>
              <p className="text-sm text-slate-600 mb-6">
                {scenario.label} &middot; {scenario.address}
              </p>

              {/* Headline 3-line summary that switches with the active scenario */}
              <div className="grid sm:grid-cols-3 gap-3 mb-6">
                {scenario.breakdown.map((row) => (
                  <div
                    key={row.category}
                    className={`rounded-xl border p-4 bg-slate-50 border-${row.color}-500/30`}
                  >
                    <div className={`text-xs font-bold uppercase tracking-wider text-${row.color}-300`}>
                      {row.category}
                    </div>
                    <div className="text-2xl font-bold text-slate-900 mt-1">
                      {fmtUSD(row.amount)}
                    </div>
                    <div className="text-xs text-slate-600 mt-0.5">{row.pct}% of total</div>
                  </div>
                ))}
              </div>
              <div className="space-y-4">
                {(scenario.id === "northsam" ? useOfFundsPlanB : scenario.id === "ronan" ? useOfFundsPlanC : useOfFunds).map((item, idx) => {
                  const colors = ['from-blue-900 to-blue-900', 'from-blue-900 to-blue-900', 'from-blue-900 to-blue-900', 'from-blue-900 to-blue-900', 'from-blue-900 to-blue-900'];
                  return (
                    <div key={idx} className="bg-white border border-slate-200 rounded-xl p-5">
                      <div className="flex items-center justify-between mb-3">
                        <div>
                          <h3 className="font-semibold text-lg text-slate-900">{item.category}</h3>
                          <p className="text-slate-600 text-sm">{item.description}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-xl font-bold text-emerald-600">{item.amount}</p>
                          <p className="text-slate-500 text-sm">{item.percentage}%</p>
                        </div>
                      </div>
                      <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                        <div className={`h-full rounded-full bg-gradient-to-r ${colors[idx]}`} style={{ width: `${item.percentage}%` }} />
                      </div>
                      {Array.isArray(item.detail) && item.detail.length > 0 && (
                        <ul className="mt-3 pl-4 space-y-1 text-xs text-slate-600 list-disc list-outside marker:text-emerald-500" data-testid={`use-of-funds-detail-${idx}`}>
                          {item.detail.map((line, di) => (
                            <li key={di}>{line}</li>
                          ))}
                        </ul>
                      )}
                    </div>
                  );
                })}
              </div>
              <div className="mt-6 bg-white rounded-xl p-6 border border-blue-500/20 text-center" data-testid="sba-total-project-cost">
                <p className="text-slate-600 text-sm">Total Project Cost</p>
                <p className="text-4xl font-bold text-slate-900 mt-1">{scenario.askAmount}</p>
                <div className="flex justify-center gap-8 mt-3 text-sm">
                  <div><span className="text-blue-900 font-semibold">SBA Loan:</span> <span className="text-slate-900">{scenario.sbaLoanLabel}</span></div>
                  <div><span className="text-emerald-600 font-semibold">Equity Injection:</span> <span className="text-slate-900">{scenario.equityInjectionLabel}</span></div>
                </div>
              </div>
            </div>

            {/* DSCR Analysis */}
            <div className="bg-white border border-slate-200 rounded-2xl p-8">
              <h2 className="text-2xl font-bold mb-6 flex items-center gap-3">
                <Calculator className="w-6 h-6 text-emerald-600" />
                Debt Service Coverage Analysis
              </h2>
              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div className="bg-white rounded-xl p-4 border border-slate-200">
                    <div className="flex justify-between items-center">
                      <span className="text-slate-600">SBA Monthly Payment</span>
                      <span className="text-xl font-bold text-slate-900">{scenario.monthlyDebtSBA}/mo</span>
                    </div>
                  </div>
                  <div className="bg-white rounded-xl p-4 border border-slate-200">
                    <div className="flex justify-between items-center">
                      <span className="text-slate-600">Existing Debt Service</span>
                      <span className="text-xl font-bold text-slate-900">~$2,300/mo</span>
                    </div>
                  </div>
                  <div className="bg-white rounded-xl p-4 border border-slate-200">
                    <div className="flex justify-between items-center">
                      <span className="text-slate-600">Total Monthly Debt Service</span>
                      <span className="text-xl font-bold text-slate-800">{scenario.monthlyDebtTotal}/mo</span>
                    </div>
                  </div>
                </div>
                <div className="flex flex-col justify-center">
                  <div className="bg-slate-50 rounded-2xl p-8 text-center border border-slate-200">
                    <p className="text-slate-600 mb-2">Year 2 DSCR</p>
                    <p className="text-5xl font-bold text-emerald-600 mb-2">{scenario.dscrRangeY2}</p>
                    <p className="text-lg font-semibold text-slate-900">Strong Coverage</p>
                    <p className="text-xs text-slate-500 mt-2">SBA typically requires 1.15&ndash;1.25x minimum</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Power Statement */}
            <div className="bg-gradient-to-r from-blue-600/20 to-emerald-600/20 rounded-2xl p-8 border border-slate-200 text-center">
              <h3 className="text-2xl font-bold text-slate-900 mb-4">Why This Model Works</h3>
              <div className="grid md:grid-cols-5 gap-4 mb-6">
                {[
                  { label: "Immediate Revenue", desc: "Retail (Day 1)", icon: Store, color: "text-emerald-600" },
                  { label: "High-Ticket Contracts", desc: "B2B Healthcare", icon: Building2, color: "text-blue-900" },
                  { label: "Recurring Revenue", desc: "DME + Insurance", icon: RefreshCw, color: "text-blue-900" },
                  { label: "Marketing Engine", desc: "$120K/yr Investment", icon: Megaphone, color: "text-slate-800" },
                  { label: "Real Estate Asset", desc: "Appreciating Value", icon: Landmark, color: "text-blue-900" },
                ].map((item, i) => (
                  <div key={i} className="bg-white/50 rounded-xl p-4 border border-slate-200/30">
                    <item.icon className={`w-8 h-8 ${item.color} mx-auto mb-2`} />
                    <p className="text-slate-900 text-sm font-semibold">{item.label}</p>
                    <p className="text-slate-500 text-xs mt-1">{item.desc}</p>
                  </div>
                ))}
              </div>
              <p className="text-lg text-slate-700 max-w-3xl mx-auto italic">
                &ldquo;This is no longer just a business&hellip; it&apos;s a scalable healthcare platform anchored by a prime real estate asset.&rdquo;
              </p>
            </div>
          </div>
        )}

        {/* Pipeline Section - Major Active Projects */}
        {activeSection === "operations" && (
          <div className="space-y-10 animate-fade-in">
            {/* Pipeline Hero */}
            <div className="relative bg-white rounded-3xl overflow-hidden border border-slate-200 p-8 sm:p-10">
              <div className="text-center mb-8">
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-slate-100 border border-slate-200 rounded-full text-slate-800 text-sm mb-4">
                  <Target className="w-4 h-4" />
                  MAJOR PROJECTS 2026
                </div>
                <h1 className="text-4xl font-bold text-slate-900 mb-4">$1.182M Active Pipeline for 2026</h1>
                <p className="text-xl text-slate-700 max-w-3xl mx-auto">
                  Confirmed water purification projects with Harris Health System — $791K in water softening systems plus $391K in dialysis water purification
                </p>
              </div>
              <div className="grid md:grid-cols-3 gap-6">
                {majorProjects.map((project, idx) => (
                  <div key={idx} className="bg-white rounded-2xl p-6 border border-slate-200">
                    <div className="flex items-center justify-between mb-4">
                      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                        project.status === 'In Progress' ? 'bg-slate-100 text-slate-800' :
                        project.status === 'Active Partnership' ? 'bg-slate-100 text-emerald-600' :
                        'bg-blue-100 text-blue-900'
                      }`}>
                        {project.status}
                      </span>
                      <span className="text-2xl font-bold text-slate-900">{project.value}</span>
                    </div>
                    <h3 className="text-lg font-bold text-slate-900 mb-2">{project.name}</h3>
                    <p className="text-sm text-slate-600 mb-4">{project.description}</p>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-slate-500">{project.client}</span>
                      <span className="text-blue-900">{project.timeline}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Water Purification Projects Deep Dive */}
            <div className="bg-white/50 rounded-2xl p-6 border border-slate-200">
              <h2 className="text-2xl font-bold mb-6 flex items-center gap-3">
                <Hospital className="w-6 h-6 text-blue-900" />
                Water Purification Projects — Live Progress
              </h2>
              <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-white rounded-xl p-6 border border-slate-200">
                  <div className="flex items-center gap-2 mb-4">
                    <Droplets className="w-6 h-6 text-blue-900" />
                    <h3 className="text-lg font-bold text-slate-900">Water Softening Systems</h3>
                  </div>
                  <div className="space-y-3 mb-4">
                    <div className="flex justify-between">
                      <span className="text-slate-600">Project Value</span>
                      <span className="text-blue-900 font-bold">$791,000</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-600">Timeline</span>
                      <span className="text-slate-900">2026</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-600">Client</span>
                      <span className="text-slate-900">Harris Health System</span>
                    </div>
                  </div>
                  {/* Progress Tracker */}
                  <div className="mt-4 pt-4 border-t border-blue-500/20">
                    <p className="text-xs text-slate-500 mb-2">Project Phase</p>
                    <div className="flex gap-1">
                      {[
                        { name: "Planning", done: true },
                        { name: "Design", done: true },
                        { name: "Procurement", done: false, current: true },
                        { name: "Installation", done: false },
                        { name: "Testing", done: false },
                      ].map((phase, i) => (
                        <div key={i} className="flex-1">
                          <div className={`h-2 rounded-full ${phase.done ? 'bg-blue-500' : phase.current ? 'bg-blue-500/50 animate-pulse' : 'bg-slate-700'}`} />
                          <p className={`text-[10px] mt-1 ${phase.current ? 'text-blue-900 font-medium' : 'text-slate-500'}`}>{phase.name}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                  <p className="text-sm text-slate-600 mt-4">Facility-wide duplex water softener systems for Harris Health System hospital campuses. Includes salt delivery and maintenance contracts.</p>
                </div>
                <div className="bg-white rounded-xl p-6 border border-slate-200">
                  <div className="flex items-center gap-2 mb-4">
                    <Building2 className="w-6 h-6 text-emerald-600" />
                    <h3 className="text-lg font-bold text-slate-900">Dialysis Water Purification</h3>
                  </div>
                  <div className="space-y-3 mb-4">
                    <div className="flex justify-between">
                      <span className="text-slate-600">Project Value</span>
                      <span className="text-emerald-600 font-bold">$391,000</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-600">Timeline</span>
                      <span className="text-slate-900">2026</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-600">Client</span>
                      <span className="text-slate-900">Harris Health System — New Hospital</span>
                    </div>
                  </div>
                  {/* Progress Tracker */}
                  <div className="mt-4 pt-4 border-t border-emerald-500/20">
                    <p className="text-xs text-slate-500 mb-2">Project Phase</p>
                    <div className="flex gap-1">
                      {[
                        { name: "Planning", done: true },
                        { name: "Design", done: true },
                        { name: "Procurement", done: false, current: true },
                        { name: "Installation", done: false },
                        { name: "Testing", done: false },
                      ].map((phase, i) => (
                        <div key={i} className="flex-1">
                          <div className={`h-2 rounded-full ${phase.done ? 'bg-emerald-500' : phase.current ? 'bg-emerald-500/50 animate-pulse' : 'bg-slate-700'}`} />
                          <p className={`text-[10px] mt-1 ${phase.current ? 'text-emerald-600 font-medium' : 'text-slate-500'}`}>{phase.name}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                  <p className="text-sm text-slate-600 mt-4">AAMI ST108-compliant dialysis water purification system for new Harris Health System hospital. Includes RO/DI system, distribution loop, and long-term service agreement.</p>
                </div>
              </div>
            </div>

            {/* Automated Backorder Sourcing — Flagship Revenue Engine */}
            <div className="bg-white rounded-2xl p-6 border border-slate-200">
              <div className="flex items-center gap-4 mb-6">
                <Zap className="w-10 h-10 text-slate-800" />
                <div>
                  <h2 className="text-2xl font-bold text-slate-900">Automated Backorder Sourcing</h2>
                  <p className="text-slate-800">Our 6-Year Benchmark, Now Automated — Flagship Revenue Engine</p>
                </div>
              </div>
              <p className="text-slate-700 text-sm mb-6 leading-relaxed">
                Backorder sourcing has been Sterile Mate\u2019s benchmark, carrier, and door-opener into every Texas hospital system for the last six years — it drove the majority of our first-3-year revenue. AskIsaac now automates the full RFQ, supplier-quote, ETA-tracking, and hospital-notification workflow so we can scale the volume 3-5x without adding proportional headcount. This is the primary lever behind the regional and statewide expansion plan.
              </p>
              <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-white rounded-xl p-6">
                  <h3 className="text-lg font-bold text-slate-800 mb-3">6-Year Track Record</h3>
                  <p className="text-slate-600 text-sm mb-4">Sourcing critical shortages for Harris Health, Houston Methodist, MD Anderson, Memorial Hermann, VA facilities, and the broader Texas Medical Center since 2020.</p>
                  <ul className="space-y-2">
                    {["Live supplier network across 200+ manufacturers", "Hospital-specific SKU maps + preferred-vendor rules", "Trusted door-opener into every major TX health system"].map((item, idx) => (
                      <li key={idx} className="flex items-center gap-2 text-sm text-slate-700">
                        <CheckCircle2 className="w-4 h-4 text-slate-800" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="bg-white rounded-xl p-6">
                  <h3 className="text-lg font-bold text-slate-800 mb-3">Automated → Regional / Statewide Scale</h3>
                  <p className="text-slate-600 text-sm mb-4">AskIsaac RFQ platform automates the entire flow — request intake, supplier quotes, ETA tracking, hospital alerts — so we can multiply volume without multiplying staff.</p>
                  <ul className="space-y-2">
                    {["Instant multi-supplier RFQ dispatch", "Automated ETA + \u201Cnow in stock\u201D notifications", "Revenue: $1.2M Y1 → $5.0M Y5 (flagship line)"].map((item, idx) => (
                      <li key={idx} className="flex items-center gap-2 text-sm text-slate-700">
                        <CheckCircle2 className="w-4 h-4 text-slate-800" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* AskIsaac Section */}
        {activeSection === "business" && (
          <div className="space-y-10 animate-fade-in">
            {/* Hero Banner - The Technology Platform */}
            <div className="relative bg-gradient-to-br from-[#0a1628] via-[#0d1526] to-[#0a0f1a] rounded-3xl overflow-hidden border border-cyan-500/20">
              {/* Background stars/particles effect */}
              <div className="absolute inset-0 overflow-hidden">
                {[...Array(50)].map((_, i) => (
                  <div 
                    key={i}
                    className="absolute w-1 h-1 bg-white rounded-full animate-pulse"
                    style={{
                      left: `${Math.random() * 100}%`,
                      top: `${Math.random() * 100}%`,
                      opacity: Math.random() * 0.5 + 0.2,
                      animationDelay: `${Math.random() * 3}s`
                    }}
                  />
                ))}
              </div>
              
              <div className="relative z-10 grid lg:grid-cols-2 gap-8 p-8 lg:p-12">
                {/* Left - Content */}
                <div className="flex flex-col justify-center">
                  <div className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-500/10 border border-slate-200 rounded-full text-emerald-600 text-sm mb-6 w-fit">
                    <Zap className="w-4 h-4" />
                    Technology Platform
                  </div>
                  
                  <div className="flex items-center gap-4 mb-6">
                    <img src={ASKISAAC_LOGO} alt="AskIsaac" className="w-16 h-16 rounded-2xl shadow-lg shadow-cyan-500/30" />
                    <div>
                      <h1 className="text-4xl lg:text-5xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                        AskIsaac.io
                      </h1>
                      <p className="text-emerald-600 font-medium">Powering SterileMate&apos;s Growth</p>
                    </div>
                  </div>
                  
                  <p className="text-xl text-slate-700 leading-relaxed mb-8">
                    AskIsaac is the <span className="text-blue-900 font-semibold">proprietary technology platform</span> powering every division under our new HQ roof. From <span className="text-blue-900 font-semibold">B2B project management</span> and <span className="text-emerald-600 font-semibold">retail inventory tracking</span> to <span className="text-slate-800 font-semibold">DME billing workflows</span> — AskIsaac is the digital backbone that makes our physical expansion scalable.
                  </p>
                  
                  <div className="flex flex-wrap gap-3">
                    <button 
                      className="group flex items-center gap-2 px-6 py-3 bg-[#003366] hover:bg-[#0056A8] text-slate-900 font-semibold rounded-xl shadow-lg shadow-blue-900/30 transition-all duration-200"
                      onClick={() => setShowDemo(true)}
                      data-testid="investor-watch-demo-btn"
                    >
                      <Play className="w-4 h-4" />
                      Watch Demo
                    </button>
                  </div>
                </div>
                
                {/* Right - Isaac Image */}
                <div className="relative flex items-center justify-center">
                  <div className="absolute inset-0 bg-gradient-radial from-cyan-500/20 via-blue-500/10 to-transparent blur-3xl" />
                  <img 
                    src={DIGITAL_ISAAC}
                    alt="Isaac - Your Digital Healthcare Assistant"
                    className="relative z-10 w-full max-w-md h-auto object-contain"
                    style={{ 
                      filter: 'drop-shadow(0 0 60px rgba(96, 165, 250, 0.4))',
                      animation: 'floatIsaac 6s ease-in-out infinite'
                    }}
                  />
                  <style>{`
                    @keyframes floatIsaac {
                      0%, 100% { transform: translateY(0px); }
                      50% { transform: translateY(-20px); }
                    }
                  `}</style>
                </div>
              </div>
            </div>

            {/* The Real Problem */}
            <div className="bg-slate-50 border border-slate-200 rounded-2xl p-8">
              <h2 className="text-2xl font-bold mb-6 flex items-center gap-3 text-slate-800">
                <AlertTriangle className="w-6 h-6" />
                The Real Problem: One Call at a Time
              </h2>
              <p className="text-lg text-slate-700 leading-relaxed mb-6">
                When a hospital needs something <span className="text-slate-800 font-semibold">NOW</span>, what happens? They start making calls. One supplier. Then another. Then another. <span className="text-red-600 font-semibold">Hours wasted</span> just trying to find out: <span className="text-slate-900 font-bold italic">"Who has what we need, and how fast can you get it here?"</span>
              </p>
              <p className="text-lg text-slate-700 leading-relaxed mb-6">
                And when a new supplier or contractor shows up? They have to <span className="text-slate-800 font-semibold">go through the weeds</span> — paperwork, vetting, approvals — before they can even get a chance to serve you. By the time they&apos;re approved, the crisis is over.
              </p>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="bg-red-50 border border-slate-200 rounded-xl p-5">
                  <Clock className="w-8 h-8 text-red-600 mb-3" />
                  <h3 className="font-semibold mb-2 text-red-600">Time-Consuming Outreach</h3>
                  <p className="text-slate-600 text-sm">Calling suppliers one by one. Waiting on hold. Leaving voicemails. Repeating the same request over and over. This is how hospitals spend their day.</p>
                </div>
                <div className="bg-red-50 border border-slate-200 rounded-xl p-5">
                  <FileText className="w-8 h-8 text-red-600 mb-3" />
                  <h3 className="font-semibold mb-2 text-red-600">Vendor Onboarding Nightmare</h3>
                  <p className="text-slate-600 text-sm">New suppliers exist. They want to help. But the approval process takes weeks or months. By then, the urgent need is long gone.</p>
                </div>
              </div>
            </div>

            {/* The AskIsaac Solution */}
            <div className="bg-white border border-emerald-500/20 rounded-2xl p-8">
              <h2 className="text-2xl font-bold mb-6 flex items-center gap-3 text-emerald-600">
                <Zap className="w-6 h-6" />
                The AskIsaac Solution: One Button. Everyone Hears It.
              </h2>
              <p className="text-lg text-slate-700 leading-relaxed mb-6">
                What if you could <span className="text-emerald-600 font-semibold">talk to your entire network at once</span>? Push one button and instantly reach every pre-vetted supplier and contractor in your system. No more one-by-one calls. No more wasted hours.
              </p>
              <div className="p-6 bg-gradient-to-r from-emerald-500/20 to-cyan-500/20 border border-slate-200 rounded-xl mb-6">
                <p className="text-center text-2xl font-bold text-slate-900 mb-2">
                  "Who has what we need? How soon can you get it here?"
                </p>
                <p className="text-center text-emerald-700 font-medium">
                  <Zap className="w-5 h-5 inline mr-2" />
                  One request. Everyone responds. Best answer wins. Problem solved — today.
                </p>
              </div>
              <div className="grid md:grid-cols-3 gap-4">
                <div className="bg-white/50 border border-slate-200 rounded-xl p-5 text-center">
                  <div className="w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-3">
                    <Send className="w-6 h-6 text-emerald-600" />
                  </div>
                  <h3 className="font-semibold mb-2">Broadcast Instantly</h3>
                  <p className="text-slate-600 text-sm">One request reaches your entire vetted network — suppliers, contractors, everyone — at the same time</p>
                </div>
                <div className="bg-white/50 border border-slate-200 rounded-xl p-5 text-center">
                  <div className="w-12 h-12 rounded-full bg-cyan-500/20 flex items-center justify-center mx-auto mb-3">
                    <Users className="w-6 h-6 text-blue-900" />
                  </div>
                  <h3 className="font-semibold mb-2">Pre-Vetted Network</h3>
                  <p className="text-slate-600 text-sm">No onboarding delays. Suppliers and contractors are credentialed and ready to respond before you ever need them</p>
                </div>
                <div className="bg-white/50 border border-slate-200 rounded-xl p-5 text-center">
                  <div className="w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-3">
                    <Award className="w-6 h-6 text-slate-800" />
                  </div>
                  <h3 className="font-semibold mb-2">Best Response Wins</h3>
                  <p className="text-slate-600 text-sm">Competitive responses mean better prices, faster delivery, and the right solution — not just whoever picked up the phone first</p>
                </div>
              </div>
            </div>

            {/* The Ecosystem */}
            <div className="bg-white/50 border border-slate-200 rounded-2xl p-8">
              <h2 className="text-2xl font-bold mb-6 flex items-center gap-3">
                <Layers className="w-6 h-6 text-blue-900" />
                The Ecosystem We&apos;re Building
              </h2>
              <div className="grid md:grid-cols-3 gap-4">
                <div className="bg-blue-500/10 border border-slate-200 rounded-xl p-5 text-center">
                  <Hospital className="w-10 h-10 text-blue-900 mx-auto mb-3" />
                  <h3 className="font-semibold mb-2">Hospitals</h3>
                  <p className="text-slate-600 text-sm">One dashboard to broadcast needs, receive responses, and track fulfillment in real-time</p>
                </div>
                <div className="bg-emerald-500/10 border border-slate-200 rounded-xl p-5 text-center">
                  <Wrench className="w-10 h-10 text-emerald-600 mx-auto mb-3" />
                  <h3 className="font-semibold mb-2">Contractors</h3>
                  <p className="text-slate-600 text-sm">Get instant notifications for jobs in your area. Respond fast. Win work. Build relationships.</p>
                </div>
                <div className="bg-slate-100 border border-slate-200 rounded-xl p-5 text-center">
                  <Package className="w-10 h-10 text-slate-800 mx-auto mb-3" />
                  <h3 className="font-semibold mb-2">Suppliers</h3>
                  <p className="text-slate-600 text-sm">See demand in real-time. Compete on availability and speed. Expand your healthcare footprint.</p>
                </div>
              </div>
            </div>

            {/* Why This Matters - George's Experience */}
            <div className="bg-slate-50 border border-slate-200 rounded-2xl p-8">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-900 to-blue-900 flex items-center justify-center">
                  <Rocket className="w-6 h-6 text-slate-900" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold">Why We Built This</h2>
                  <p className="text-slate-800 font-semibold">5 years as a backorder supplier taught us the real problem</p>
                </div>
              </div>
              
              <p className="text-slate-700 mb-6 leading-relaxed text-lg">
                George Broughton spent <span className="text-slate-800 font-semibold">5 years as a backorder supplier</span> for hospitals. He lived the problem every day: <span className="text-slate-900 font-semibold">the outreach alone was exhausting</span>. Calling hospitals one by one. Waiting for callbacks. Chasing down decision makers. And hospitals were doing the exact same thing — calling suppliers one by one, wasting hours that could be spent on patient care.
              </p>

              <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-white/50 border border-slate-200 rounded-xl p-6">
                  <h3 className="font-bold text-lg mb-4 flex items-center gap-2 text-red-600">
                    <Clock className="w-5 h-5" />
                    The Old Way
                  </h3>
                  <ul className="text-slate-600 text-sm space-y-2">
                    <li className="flex items-start gap-2">
                      <XCircle className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" />
                      <span>Call supplier #1... voicemail. Leave message.</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <XCircle className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" />
                      <span>Call supplier #2... on hold for 20 minutes.</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <XCircle className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" />
                      <span>Call supplier #3... they don&apos;t have it.</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <XCircle className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" />
                      <span>Repeat until you find what you need... or give up.</span>
                    </li>
                  </ul>
                </div>

                <div className="bg-white/50 border border-slate-200 rounded-xl p-6">
                  <h3 className="font-bold text-lg mb-4 flex items-center gap-2 text-emerald-600">
                    <Zap className="w-5 h-5" />
                    The AskIsaac Way
                  </h3>
                  <ul className="text-slate-600 text-sm space-y-2">
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-4 h-4 text-emerald-600 mt-0.5 flex-shrink-0" />
                      <span>Post your need once. <span className="text-emerald-600">Everyone sees it.</span></span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-4 h-4 text-emerald-600 mt-0.5 flex-shrink-0" />
                      <span>Responses come to you. Compare instantly.</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-4 h-4 text-emerald-600 mt-0.5 flex-shrink-0" />
                      <span>Pick the best option. Done in minutes.</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-4 h-4 text-emerald-600 mt-0.5 flex-shrink-0" />
                      <span>Problem solved. <span className="text-emerald-600">Back to patient care.</span></span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Investor Value Proposition */}
            <div className="bg-white border border-slate-200 rounded-2xl p-8">
              <h2 className="text-2xl font-bold mb-6 flex items-center gap-3">
                <CircleDollarSign className="w-6 h-6 text-blue-900" />
                Investor Value Proposition
              </h2>
              
              <div className="grid md:grid-cols-3 gap-6">
                <div className="bg-white/50 border border-slate-200 rounded-xl p-6">
                  <div className="w-12 h-12 rounded-xl bg-blue-900 flex items-center justify-center mb-4">
                    <Globe className="w-6 h-6 text-slate-900" />
                  </div>
                  <h3 className="font-bold text-lg mb-2">Infinite Scalability</h3>
                  <p className="text-slate-600 text-sm">
                    We no longer need to hire service techs. We sell the software to other companies who <span className="text-blue-900 font-semibold">already have the techs</span>.
                  </p>
                </div>

                <div className="bg-white/50 border border-slate-200 rounded-xl p-6">
                  <div className="w-12 h-12 rounded-xl bg-emerald-700 flex items-center justify-center mb-4">
                    <DollarSign className="w-6 h-6 text-slate-900" />
                  </div>
                  <h3 className="font-bold text-lg mb-2">High-Margin Recurring Revenue</h3>
                  <p className="text-slate-600 text-sm">
                    No hardware costs, no shipping, no inventory. <span className="text-emerald-600 font-semibold">Pure software licensing</span>.
                  </p>
                </div>

                <div className="bg-white/50 border border-slate-200 rounded-xl p-6">
                  <div className="w-12 h-12 rounded-xl bg-slate-800 flex items-center justify-center mb-4">
                    <BarChart3 className="w-6 h-6 text-slate-900" />
                  </div>
                  <h3 className="font-bold text-lg mb-2">Data Goldmine</h3>
                  <p className="text-slate-600 text-sm">
                    By white-labeling the engine, AskIsaac becomes the <span className="text-slate-800 font-semibold">central repository for industry-wide compliance data</span>.
                  </p>
                </div>
              </div>
            </div>

            {/* Use of Funds: White Label Roadmap */}
            <div className="bg-white/50 border border-slate-200 rounded-2xl p-8">
              <h2 className="text-2xl font-bold mb-6 flex items-center gap-3">
                <Target className="w-6 h-6 text-blue-900" />
                Use of Funds: "The White Label Roadmap"
              </h2>
              
              <div className="grid md:grid-cols-3 gap-6">
                <div className="bg-white border border-blue-500/20 rounded-xl p-6">
                  <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center mb-4">
                    <Building2 className="w-5 h-5 text-blue-900" />
                  </div>
                  <h3 className="font-bold mb-2">Multi-Tenant Architecture</h3>
                  <p className="text-slate-600 text-sm">
                    Upgrading the backend to allow <span className="text-blue-900 font-semibold">"Instant Rebranding"</span> for licensees.
                  </p>
                </div>

                <div className="bg-gradient-to-br from-violet-500/10 to-purple-500/10 border border-violet-500/20 rounded-xl p-6">
                  <div className="w-10 h-10 rounded-lg bg-violet-500/20 flex items-center justify-center mb-4">
                    <Code className="w-5 h-5 text-blue-900" />
                  </div>
                  <h3 className="font-bold mb-2">API Development</h3>
                  <p className="text-slate-600 text-sm">
                    Building a robust API so other companies can <span className="text-blue-900 font-semibold">plug their own hardware sensors</span> into the Isaac engine.
                  </p>
                </div>

                <div className="bg-white border border-emerald-500/20 rounded-xl p-6">
                  <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center mb-4">
                    <Shield className="w-5 h-5 text-emerald-600" />
                  </div>
                  <h3 className="font-bold mb-2">Legal & IP Protection</h3>
                  <p className="text-slate-600 text-sm">
                    Securing <span className="text-emerald-600 font-semibold">global patents</span> for the AI-driven compliance logic.
                  </p>
                </div>
              </div>
            </div>

            {/* The Pitch - Hook, Traction, Exit */}
            <div className="bg-white border border-slate-200 rounded-2xl p-8">
              <h2 className="text-2xl font-bold mb-6 flex items-center gap-3">
                <MessageSquare className="w-6 h-6 text-blue-900" />
                Pitch Talking Points
              </h2>
              
              <div className="space-y-6">
                {/* The Hook */}
                <div className="bg-white border-l-4 border-blue-500 rounded-r-xl p-6">
                  <h3 className="font-bold text-lg mb-3 text-blue-900">The "Hook"</h3>
                  <p className="text-slate-700 text-lg italic">
                    "We've built the <span className="text-blue-900 font-semibold">'Intel Inside' for hospital compliance</span>. Every medical service provider wants to offer an AI dashboard to their clients, but they don't have the $2M+ to build it. AskIsaac is the solution they can buy tomorrow."
                  </p>
                </div>

                {/* The Traction */}
                <div className="bg-white border-l-4 border-emerald-500 rounded-r-xl p-6">
                  <h3 className="font-bold text-lg mb-3 text-emerald-600">The Traction</h3>
                  <p className="text-slate-700 text-lg italic">
                    "Through my <span className="text-emerald-600 font-semibold">6-year journey building Sterile Mate</span>, I have proven that hospitals will pay for on-demand operations. Now with AskIsaac Inc, I want to give this platform to every healthcare facility in the country."
                  </p>
                </div>

                {/* The Exit */}
                <div className="bg-white border-l-4 border-amber-500 rounded-r-xl p-6">
                  <h3 className="font-bold text-lg mb-3 text-slate-800">The Exit</h3>
                  <p className="text-slate-700 text-lg italic">
                    "A white-label compliance engine is a prime acquisition target for companies like <span className="text-slate-800 font-semibold">Salesforce (Health Cloud), Microsoft (Azure Health)</span>, or private equity firms that roll up industrial service companies."
                  </p>
                </div>
              </div>
            </div>

            {/* Competition */}
            <div className="bg-white/50 border border-slate-200 rounded-2xl p-8">
              <h2 className="text-2xl font-bold mb-6 flex items-center gap-3">
                <Users className="w-6 h-6 text-red-600" />
                Competition & Our Edge
              </h2>
              
              <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-red-500/10 border border-red-500/20 rounded-xl p-6">
                  <h3 className="font-bold text-lg mb-4 text-red-600">The Giants</h3>
                  <ul className="space-y-3">
                    <li className="flex items-start gap-3">
                      <XCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                      <span className="text-slate-700 text-sm"><span className="font-semibold">Steris</span> — Has proprietary software, won't share with competitors</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <XCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                      <span className="text-slate-700 text-sm"><span className="font-semibold">McKesson/Cardinal</span> — Focus on distribution, not compliance tools</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <XCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                      <span className="text-slate-700 text-sm"><span className="font-semibold">Custom Development</span> — $2M+ and 18+ months for hospitals to build their own</span>
                    </li>
                  </ul>
                </div>

                <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-xl p-6">
                  <h3 className="font-bold text-lg mb-4 text-emerald-600">AskIsaac Advantage</h3>
                  <ul className="space-y-3">
                    <li className="flex items-start gap-3">
                      <CheckCircle2 className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                      <span className="text-slate-700 text-sm"><span className="font-semibold">Battle-Tested</span> — Already proven in real healthcare environments</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle2 className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                      <span className="text-slate-700 text-sm"><span className="font-semibold">Plug-and-Play</span> — Deploy in weeks, not months</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle2 className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                      <span className="text-slate-700 text-sm"><span className="font-semibold">White Label Ready</span> — Partners brand it as their own</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Platform Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[
                { label: "Healthcare Workflows", value: "6+", icon: Zap },
                { label: "User Portals", value: "3", icon: Users },
                { label: "Real-Time Updates", value: "24/7", icon: Clock },
                { label: "White Label Ready", value: "100%", icon: ShieldCheck }
              ].map((stat, idx) => (
                <div key={idx} className="bg-white/50 border border-slate-200 rounded-2xl p-6 text-center">
                  <stat.icon className="w-8 h-8 mx-auto mb-3 text-blue-900" />
                  <p className="text-3xl font-bold mb-1">{stat.value}</p>
                  <p className="text-sm text-slate-600">{stat.label}</p>
                </div>
              ))}
            </div>

            {/* Call to Action */}
            <div className="text-center bg-white border border-slate-200 rounded-2xl p-10">
              <h2 className="text-3xl font-bold mb-4">Ready to Scale Healthcare Compliance?</h2>
              <p className="text-slate-600 mb-8 max-w-2xl mx-auto">
                Join us in building the "Intel Inside" for healthcare operations. AskIsaac is battle-tested, white-label ready, and positioned for explosive growth.
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                <button 
                  onClick={() => setShowDemo(true)}
                  className="group flex items-center gap-2 px-8 py-4 bg-[#003366] hover:bg-[#0056A8] text-slate-900 font-bold rounded-xl shadow-lg shadow-blue-900/30 transition-all text-lg"
                  data-testid="askisaac-cta-watch-demo-btn"
                >
                  <Play className="w-5 h-5" />
                  Watch Demo
                </button>
                <button 
                  onClick={() => setActiveSection("contact")}
                  className="inline-flex items-center gap-2 px-8 py-4 border-2 border-amber-500/50 hover:border-amber-400 text-slate-800 hover:text-slate-800 font-bold rounded-xl transition-all text-lg"
                >
                  <Phone className="w-5 h-5" />
                  Contact George
                </button>
              </div>
            </div>

            {/* NEW REVENUE STREAMS — Plan C only (Ronan Rd) */}
            {scenario.id === "ronan" && (
              <div className="space-y-8" data-testid="plan-c-new-revenue-streams">
                {/* Private-Label Exam Gloves — Direct Import from Haiphong */}
                <div className="relative bg-white rounded-3xl overflow-hidden border border-slate-200 p-8 sm:p-10" data-testid="private-label-gloves-section">
                  <div className="absolute top-0 right-0 w-72 h-72 bg-emerald-500/10 rounded-full blur-3xl" />
                  <div className="relative z-10">
                    <div className="flex flex-wrap items-start gap-6 mb-6">
                      <div className="flex-1 min-w-[280px]">
                        <div className="inline-flex items-center gap-2 px-4 py-2 bg-slate-100 border border-slate-200 rounded-full text-emerald-600 text-sm mb-4">
                          <Package className="w-4 h-4" />
                          Direct-Import Recurring Revenue · Launch 2027 (Year 1)
                        </div>
                        <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-3" style={{ fontFamily: 'Manrope, sans-serif' }}>
                          Sterile Mate Private-Label Exam Gloves
                        </h2>
                        <p className="text-lg text-slate-700 max-w-2xl">
                          A direct-import <span className="text-emerald-700 font-semibold">medical examination glove line</span> shipped FOB from Haiphong, Vietnam into our Texas warehouse. Two flagship SKUs — <span className="text-emerald-700 font-semibold">3.5&#8209;mil</span> and <span className="text-emerald-700 font-semibold">4.0&#8209;mil nitrile</span> — packaged <span className="text-slate-900 font-semibold">200 pcs/box, 10 boxes/carton (2,000 gloves per master case)</span>, undercutting the standard 100/200 pcs GPO packs and unlocking a wholesale + direct-healthcare dual channel.
                        </p>
                      </div>
                      <div className="w-full sm:w-64 shrink-0">
                        <img
                          src={`${process.env.REACT_APP_BACKEND_URL}/api/static/gloves/sterilemate_nitrile_exam_hero.png`}
                          alt="Sterile Mate Private-Label Nitrile Exam Gloves — 3.5-mil + 4.0-mil"
                          className="w-full rounded-2xl border border-slate-200 shadow-lg shadow-emerald-500/10 bg-slate-50"
                          data-testid="private-label-gloves-image"
                          loading="lazy"
                        />
                        <p className="text-[10px] text-slate-500 mt-2 text-center uppercase tracking-widest">Sterile Mate branded — dieline finalized</p>
                      </div>
                    </div>

                    {/* Year-1 Baseline Unit Economics */}
                    <div className="mt-4 grid md:grid-cols-2 gap-4">
                      <div className="bg-white/50 rounded-xl p-5 border border-emerald-500/20">
                        <div className="flex items-baseline justify-between mb-3">
                          <div className="text-emerald-700 text-xs font-bold uppercase tracking-wider">SKU 1 · 3.5-mil Nitrile</div>
                          <div className="text-slate-900 text-xs">2 containers · 2,380 cartons</div>
                        </div>
                        <p className="text-slate-700 text-sm mb-3">Powder-free, textured fingertips. Everyday clinical wear — nursing, phlebotomy, admissions, EVS.</p>
                        <div className="grid grid-cols-3 gap-2 text-center text-xs">
                          <div className="bg-slate-100 rounded p-2">
                            <div className="text-slate-500 uppercase tracking-wider text-[10px]">Landed COGS</div>
                            <div className="text-slate-900 font-bold mt-1">$49.08/ct</div>
                            <div className="text-slate-500 mt-0.5 text-[10px]">$0.0245/glove</div>
                          </div>
                          <div className="bg-slate-100 rounded p-2">
                            <div className="text-emerald-700 uppercase tracking-wider text-[10px]">Wholesale</div>
                            <div className="text-emerald-600 font-bold mt-1">$65.00/ct</div>
                            <div className="text-slate-500 mt-0.5 text-[10px]">24.5% GM</div>
                          </div>
                          <div className="bg-slate-100 rounded p-2">
                            <div className="text-emerald-700 uppercase tracking-wider text-[10px]">Direct HC</div>
                            <div className="text-emerald-600 font-bold mt-1">$89.00/ct</div>
                            <div className="text-slate-500 mt-0.5 text-[10px]">44.9% GM</div>
                          </div>
                        </div>
                      </div>
                      <div className="bg-white/50 rounded-xl p-5 border border-emerald-500/20">
                        <div className="flex items-baseline justify-between mb-3">
                          <div className="text-emerald-700 text-xs font-bold uppercase tracking-wider">SKU 2 · 4.0-mil Nitrile</div>
                          <div className="text-slate-900 text-xs">6 containers · 6,660 cartons</div>
                        </div>
                        <p className="text-slate-700 text-sm mb-3">Powder-free, textured, extended-cuff. Procedure-grade — OR support, ED, sterile processing, oncology.</p>
                        <div className="grid grid-cols-3 gap-2 text-center text-xs">
                          <div className="bg-slate-100 rounded p-2">
                            <div className="text-slate-500 uppercase tracking-wider text-[10px]">Landed COGS</div>
                            <div className="text-slate-900 font-bold mt-1">$52.47/ct</div>
                            <div className="text-slate-500 mt-0.5 text-[10px]">$0.0262/glove</div>
                          </div>
                          <div className="bg-slate-100 rounded p-2">
                            <div className="text-emerald-700 uppercase tracking-wider text-[10px]">Wholesale</div>
                            <div className="text-emerald-600 font-bold mt-1">$72.00/ct</div>
                            <div className="text-slate-500 mt-0.5 text-[10px]">27.1% GM</div>
                          </div>
                          <div className="bg-slate-100 rounded p-2">
                            <div className="text-emerald-700 uppercase tracking-wider text-[10px]">Direct HC</div>
                            <div className="text-emerald-600 font-bold mt-1">$98.00/ct</div>
                            <div className="text-slate-500 mt-0.5 text-[10px]">46.5% GM</div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* 3-Year Consolidated Forecast */}
                    <div className="mt-6 rounded-xl border border-slate-200 overflow-hidden" data-testid="private-label-gloves-forecast">
                      <div className="px-5 py-3 bg-emerald-500/10 border-b border-slate-200 flex items-baseline justify-between">
                        <div className="text-emerald-700 text-xs font-bold uppercase tracking-wider">3-Year Consolidated Forecast (2027 &ndash; 2029)</div>
                        <div className="text-[10px] text-slate-600 uppercase tracking-widest">$6,400 Freight / 20GP</div>
                      </div>
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead className="bg-slate-100 text-slate-600 text-xs uppercase tracking-wider">
                            <tr>
                              <th className="text-left px-4 py-2 font-semibold">Metric</th>
                              <th className="text-right px-4 py-2 font-semibold">Year 1 · 2027</th>
                              <th className="text-right px-4 py-2 font-semibold">Year 2 · 2028</th>
                              <th className="text-right px-4 py-2 font-semibold">Year 3 · 2029</th>
                            </tr>
                          </thead>
                          <tbody className="text-slate-700">
                            {[
                              ["Containers (20GP)", "8", "16", "28"],
                              ["Mix (3.5-mil / 4.0-mil)", "2 / 6", "4 / 12", "6 / 22"],
                              ["Cartons", "9,040", "18,080", "31,560"],
                              ["Gloves", "18,080,000", "36,160,000", "63,120,000"],
                              ["Channel Mix (Wholesale / Direct)", "50 / 50", "40 / 60", "30 / 70"],
                              ["Landed COGS", "$466,228", "$932,521", "$1,631,749"],
                              ["Gross Revenue", "$749,360", "$1,544,776", "$2,786,736"],
                              ["Gross Profit", "$283,132", "$612,255", "$1,154,987"],
                            ].map(([m, y1, y2, y3], i) => (
                              <tr key={m} className={i % 2 ? "bg-slate-50" : ""}>
                                <td className="px-4 py-2 font-medium">{m}</td>
                                <td className="text-right px-4 py-2 tabular-nums">{y1}</td>
                                <td className="text-right px-4 py-2 tabular-nums">{y2}</td>
                                <td className="text-right px-4 py-2 tabular-nums">{y3}</td>
                              </tr>
                            ))}
                            <tr className="bg-emerald-500/10 border-t border-slate-200">
                              <td className="px-4 py-2 font-bold text-emerald-700 uppercase tracking-wider text-xs">Gross Margin %</td>
                              <td className="text-right px-4 py-2 font-bold text-emerald-700">37.8%</td>
                              <td className="text-right px-4 py-2 font-bold text-emerald-700">39.6%</td>
                              <td className="text-right px-4 py-2 font-bold text-emerald-700">41.4%</td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>

                    <div className="grid md:grid-cols-3 gap-3 mt-5">
                      <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
                        <div className="text-emerald-700 text-[10px] font-bold uppercase tracking-widest mb-1">Product Positioning</div>
                        <p className="text-slate-700 text-xs leading-relaxed">200 pcs/box packaging undercuts standard 100&#8209;pack GPO/distributor pricing on cost-per-glove &mdash; hospitals see a lower unit price with fewer case pulls per shift.</p>
                      </div>
                      <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
                        <div className="text-emerald-700 text-[10px] font-bold uppercase tracking-widest mb-1">Supply Chain</div>
                        <p className="text-slate-700 text-xs leading-relaxed">FOB Haiphong, Vietnam &rarr; Port of Houston &rarr; Ronan Rd Floor&nbsp;2 short-stock warehouse (3,500 SF). $6,400 freight / 20GP; ~1,190 cartons per 3.5&#8209;mil container, ~1,110 per 4.0&#8209;mil.</p>
                      </div>
                      <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
                        <div className="text-emerald-700 text-[10px] font-bold uppercase tracking-widest mb-1">Multi-Channel GTM</div>
                        <p className="text-slate-700 text-xs leading-relaxed">Wholesale master-case distributors ($65 / $72 per carton) + direct acute/non-acute healthcare facilities ($89 / $98 per carton). Channel mix shifts toward direct healthcare as the sales team seasons.</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Sterile Mate Private-Label Microfiber */}
                <div className="relative bg-white rounded-3xl overflow-hidden border border-slate-200 p-8 sm:p-10" data-testid="private-label-microfiber-section">
                  <div className="absolute top-0 right-0 w-72 h-72 bg-blue-500/10 rounded-full blur-3xl" />
                  <div className="relative z-10">
                    <div className="flex flex-wrap items-start gap-6 mb-6">
                      <div className="w-full sm:w-64 shrink-0 order-2 sm:order-1">
                        <img
                          src={`${process.env.REACT_APP_BACKEND_URL}/api/static/microfiber-rags/hero_branded_blue.png`}
                          alt="Sterile Mate Private-Label Disposable Microfiber Cloths"
                          className="w-full rounded-2xl border border-slate-200 shadow-lg shadow-blue-500/10 bg-slate-50"
                          data-testid="private-label-microfiber-image"
                          loading="lazy"
                        />
                        <p className="text-[10px] text-slate-500 mt-2 text-center uppercase tracking-widest">TM-MC-01200-STERILEMATE 50 &middot; branded pop-up box</p>
                      </div>
                      <div className="flex-1 min-w-[280px] order-1 sm:order-2">
                        <div className="inline-flex items-center gap-2 px-4 py-2 bg-blue-100 border border-slate-200 rounded-full text-blue-900 text-sm mb-4">
                          <Package className="w-4 h-4" />
                          Consumables Division · Texas Microfiber Partnership
                        </div>
                        <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-3" style={{ fontFamily: 'Manrope, sans-serif' }}>
                          Sterile Mate Private-Label Microfiber
                        </h2>
                        <p className="text-lg text-slate-700 max-w-2xl">
                          Companion consumable to the glove line &mdash; a <span className="text-blue-900 font-semibold">private-label disposable microfiber cloth program</span> manufactured through our Texas Microfiber partnership. Three color-coded SKUs (<span className="text-blue-900 font-semibold">Blue</span>, <span className="text-slate-900 font-semibold">White</span>, <span className="text-slate-600 font-semibold">Black</span>) shipped in <span className="text-slate-900 font-semibold">50-count pop-up boxes</span> inside branded master cartons &mdash; SKU <span className="font-mono text-slate-900">TM-MC-01200-STERILEMATE 50</span>. Same acute + non-acute customer base as gloves, same Floor&nbsp;2 short-stock warehouse, one sales conversation.
                        </p>
                      </div>
                    </div>

                    {/* Unit Economics */}
                    <div className="mt-4 grid md:grid-cols-3 gap-4">
                      <div className="bg-white/50 rounded-xl p-5 border border-blue-500/20">
                        <div className="text-blue-900 text-xs font-bold uppercase tracking-wider mb-3">Landed Cost</div>
                        <div className="text-3xl font-bold text-slate-900">$11.24<span className="text-slate-500 text-sm font-normal"> /box</span></div>
                        <div className="text-xs text-slate-600 mt-1">$0.2248 per cloth (50/box)</div>
                        <p className="text-[11px] text-slate-500 mt-3">Delivered &mdash; international freight, domestic shipping, tariffs, and customs duties all included.</p>
                      </div>
                      <div className="bg-white/50 rounded-xl p-5 border border-blue-500/20">
                        <div className="text-blue-900 text-xs font-bold uppercase tracking-wider mb-3">Wholesale Tier</div>
                        <div className="text-3xl font-bold text-blue-900">$15.50<span className="text-slate-500 text-sm font-normal"> /box</span></div>
                        <div className="text-xs text-slate-600 mt-1">$0.31 / cloth &middot; 37.9% markup</div>
                        <div className="mt-3 inline-flex items-center gap-1 px-2 py-1 bg-blue-500/10 rounded text-blue-900 text-[10px] font-bold">27.5% GROSS MARGIN</div>
                      </div>
                      <div className="bg-white/50 rounded-xl p-5 border border-blue-500/20">
                        <div className="text-blue-900 text-xs font-bold uppercase tracking-wider mb-3">Direct Healthcare Tier</div>
                        <div className="text-3xl font-bold text-blue-900">$21.50<span className="text-slate-500 text-sm font-normal"> /box</span></div>
                        <div className="text-xs text-slate-600 mt-1">$0.43 / cloth &middot; 91.3% markup</div>
                        <div className="mt-3 inline-flex items-center gap-1 px-2 py-1 bg-blue-500/10 rounded text-blue-900 text-[10px] font-bold">47.7% GROSS MARGIN</div>
                      </div>
                    </div>

                    {/* 3-Year Forecast */}
                    <div className="mt-6 rounded-xl border border-slate-200 overflow-hidden" data-testid="private-label-microfiber-forecast">
                      <div className="px-5 py-3 bg-blue-500/10 border-b border-slate-200 flex items-baseline justify-between">
                        <div className="text-blue-900 text-xs font-bold uppercase tracking-wider">3-Year Consolidated Forecast (2027 &ndash; 2029)</div>
                        <div className="text-[10px] text-slate-600 uppercase tracking-widest">TM-MC-01200-STERILEMATE 50</div>
                      </div>
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead className="bg-slate-100 text-slate-600 text-xs uppercase tracking-wider">
                            <tr>
                              <th className="text-left px-4 py-2 font-semibold">Metric</th>
                              <th className="text-right px-4 py-2 font-semibold">Year 1 &middot; 2027</th>
                              <th className="text-right px-4 py-2 font-semibold">Year 2 &middot; 2028</th>
                              <th className="text-right px-4 py-2 font-semibold">Year 3 &middot; 2029</th>
                            </tr>
                          </thead>
                          <tbody className="text-slate-700">
                            {[
                              ["Blue Boxes", "2,000", "4,000", "7,500"],
                              ["White Boxes", "2,000", "4,000", "7,500"],
                              ["Black Boxes", "1,000", "2,000", "3,000"],
                              ["Total Boxes", "5,000", "10,000", "18,000"],
                              ["Total Cloths (50 / box)", "250,000", "500,000", "900,000"],
                              ["Channel Mix (Wholesale / Direct)", "50 / 50", "40 / 60", "30 / 70"],
                              ["Delivered COGS", "$56,200", "$112,400", "$202,320"],
                              ["Gross Revenue", "$92,500", "$191,000", "$354,600"],
                              ["Gross Profit", "$36,300", "$78,600", "$152,280"],
                            ].map(([m, y1, y2, y3], i) => (
                              <tr key={m} className={i % 2 ? "bg-slate-50" : ""}>
                                <td className="px-4 py-2 font-medium">{m}</td>
                                <td className="text-right px-4 py-2 tabular-nums">{y1}</td>
                                <td className="text-right px-4 py-2 tabular-nums">{y2}</td>
                                <td className="text-right px-4 py-2 tabular-nums">{y3}</td>
                              </tr>
                            ))}
                            <tr className="bg-blue-500/10 border-t border-slate-200">
                              <td className="px-4 py-2 font-bold text-blue-900 uppercase tracking-wider text-xs">Gross Margin %</td>
                              <td className="text-right px-4 py-2 font-bold text-blue-900">39.2%</td>
                              <td className="text-right px-4 py-2 font-bold text-blue-900">41.2%</td>
                              <td className="text-right px-4 py-2 font-bold text-blue-900">42.9%</td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Consumables Division Roll-Up (Gloves + Microfiber) */}
                <div className="rounded-3xl overflow-hidden border border-slate-200 bg-gradient-to-br from-emerald-50 via-blue-950/30 to-white p-8 sm:p-10" data-testid="consumables-division-rollup">
                  <div className="inline-flex items-center gap-2 px-4 py-2 bg-slate-100 border border-slate-200 rounded-full text-emerald-700 text-sm mb-4">
                    <TrendingUp className="w-4 h-4" />
                    Consumables Division &middot; 3-Year Pro-Forma
                  </div>
                  <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-3" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    Consumables Division Roll-Up
                  </h2>
                  <p className="text-lg text-slate-700 max-w-3xl mb-6">
                    Combined view of the direct-import glove program (Intco) and the private-label microfiber program (Texas MF). One warehouse, one sales team, one billing conversation for every acute + non-acute customer.
                  </p>
                  <div className="overflow-x-auto rounded-xl border border-slate-200">
                    <table className="w-full text-sm">
                      <thead className="bg-slate-100 text-slate-600 text-xs uppercase tracking-wider">
                        <tr>
                          <th className="text-left px-4 py-2 font-semibold">Line</th>
                          <th className="text-right px-4 py-2 font-semibold">Year 1 &middot; 2027</th>
                          <th className="text-right px-4 py-2 font-semibold">Year 2 &middot; 2028</th>
                          <th className="text-right px-4 py-2 font-semibold">Year 3 &middot; 2029</th>
                        </tr>
                      </thead>
                      <tbody className="text-slate-700">
                        {[
                          ["Gloves Revenue (Intco, 3.5-mil + 4.0-mil)", "$749,360", "$1,544,776", "$2,786,736"],
                          ["Microfiber Revenue (Texas MF, 50-ct pop-up)", "$92,500", "$191,000", "$354,600"],
                          ["Total Consumables Revenue", "$841,860", "$1,735,776", "$3,141,336"],
                          ["Gloves Landed COGS", "$466,228", "$932,521", "$1,631,749"],
                          ["Microfiber Landed COGS", "$56,200", "$112,400", "$202,320"],
                          ["Total Consumables COGS", "$522,428", "$1,044,921", "$1,834,069"],
                        ].map(([m, y1, y2, y3], i) => (
                          <tr key={m} className={i % 2 ? "bg-slate-50" : ""}>
                            <td className="px-4 py-2 font-medium">{m}</td>
                            <td className="text-right px-4 py-2 tabular-nums">{y1}</td>
                            <td className="text-right px-4 py-2 tabular-nums">{y2}</td>
                            <td className="text-right px-4 py-2 tabular-nums">{y3}</td>
                          </tr>
                        ))}
                        <tr className="bg-emerald-500/10 border-t border-slate-200">
                          <td className="px-4 py-2 font-bold text-emerald-700 uppercase tracking-wider text-xs">Consumables Gross Profit</td>
                          <td className="text-right px-4 py-2 font-bold text-emerald-700">$319,432</td>
                          <td className="text-right px-4 py-2 font-bold text-emerald-700">$690,855</td>
                          <td className="text-right px-4 py-2 font-bold text-emerald-700">$1,307,267</td>
                        </tr>
                        <tr className="bg-emerald-500/10">
                          <td className="px-4 py-2 font-bold text-emerald-700 uppercase tracking-wider text-xs">Blended Gross Margin</td>
                          <td className="text-right px-4 py-2 font-bold text-emerald-700">37.9%</td>
                          <td className="text-right px-4 py-2 font-bold text-emerald-700">39.8%</td>
                          <td className="text-right px-4 py-2 font-bold text-emerald-700">41.6%</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* 2027 · Year of Growth — Stellar Team + Healthcare Construction */}
                <div className="relative rounded-2xl overflow-hidden border border-slate-200 bg-white p-8 sm:p-10" data-testid="year-of-growth-section">
                  <div className="absolute top-0 right-0 w-72 h-72 bg-slate-100 rounded-full blur-3xl" />
                  <div className="relative z-10">
                    <div className="inline-flex items-center gap-2 px-4 py-2 bg-slate-100 border border-slate-200 rounded-full text-slate-800 text-sm mb-4">
                      <TrendingUp className="w-4 h-4" />
                      2027 · Sterile Mate's Year of Growth
                    </div>
                    <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-3" style={{ fontFamily: 'Manrope, sans-serif' }}>
                      A Stellar New Team &mdash; Anchored by Healthcare Construction
                    </h2>
                    <p className="text-lg text-slate-700 max-w-3xl mb-6">
                      2027 is the year Sterile Mate steps up from a founder-led operation into a full <span className="text-slate-800 font-semibold">multi-division healthcare services platform</span>. As part of this expansion we will build out a team of experienced, seasoned professionals across <span className="text-slate-900 font-semibold">healthcare supply chain</span> and <span className="text-slate-900 font-semibold">healthcare infrastructure</span>. We have already begun with the <span className="text-slate-900 font-semibold">Healthcare Construction team</span> &mdash; three battle-tested industry veterans anchoring the general-contracting and MEP practice for OR / ED / cath-lab / sterile-processing renovations. Combined with the Consumables Division (Intco gloves + Texas Microfiber cloths), Backorder Sourcing via AskIsaac, and the BioBurden Cleaning practice, Sterile Mate operates <span className="text-slate-900 font-semibold">seven synchronized revenue engines</span> from a single Class-A HQ.
                    </p>

                    <div className="mb-4 text-xs text-slate-600 uppercase tracking-widest">Healthcare Construction Team &mdash; already in place</div>
                    <div className="grid md:grid-cols-3 gap-4 mb-6">
                      {[
                        {
                          name: 'George Broughton III',
                          role: 'Founder, President & CEO',
                          portrait: `${process.env.REACT_APP_BACKEND_URL}/api/static/email_assets/team_george_broughton.jpg`,
                          bio: 'Sets vision, drives capital strategy, and personally underwrites every hospital relationship. 20+ years across sterile processing, water purification, and healthcare capital projects.',
                          org: 'Sterile Mate LLC',
                        },
                        {
                          name: 'Donna Winfrey',
                          role: 'Project & Development Director',
                          portrait: `${process.env.REACT_APP_BACKEND_URL}/api/static/email_assets/team_donna_winfrey.jpg`,
                          bio: '25 years of commercial real-estate & development leadership. 7-term Planning & Zoning Commissioner (Baytown, TX). 2023 President\u2019s Lifetime Achievement Award. Leads WRED partnership and every ground-up healthcare project.',
                          org: 'Sterile Mate \u00b7 Winfrey Real-Estate Development',
                        },
                        {
                          name: 'Kimberly Phipps-Nichols',
                          role: 'Principal Architect Consultant',
                          portrait: `${process.env.REACT_APP_BACKEND_URL}/api/static/email_assets/team_kimberly_phipps_nichols.png`,
                          bio: '30+ years of healthcare architecture across Harris Health, Texas Children\u2019s, and Memorial Hermann. EDAC \u00b7 LEED AP \u00b7 NOMA \u00b7 IIDA. Owns clinical planning and design-review on every Sterile Mate capital project.',
                          org: 'Sterile Mate Healthcare Design',
                        },
                        {
                          name: 'Miguel Mendoza',
                          role: 'Principal Owner — MAM Construction Services',
                          portrait: `${process.env.REACT_APP_BACKEND_URL}/api/static/email_assets/team_miguel_mendoza.jpg`,
                          bio: '12+ years in Division 9 healthcare interior fit-outs. Fluent in ICRA barriers, negative-air setups, and after-hours OR renovations. Hispanic-owned MBE teaming partner — Tier-1 diversity credit on every project.',
                          org: 'Sterile Mate GC Partner',
                        },
                      ].map((t) => (
                        <div key={t.name} className="bg-white rounded-xl p-4 border border-slate-200 flex flex-col">
                          <img
                            src={t.portrait}
                            alt={t.name}
                            className="w-full h-48 object-cover object-top rounded-lg mb-3 bg-slate-50"
                            loading="lazy"
                            onError={(e) => { e.currentTarget.style.display = 'none'; }}
                          />
                          <div className="text-slate-900 font-bold text-sm">{t.name}</div>
                          <div className="text-slate-800 text-[11px] uppercase tracking-widest mt-1">{t.role}</div>
                          <div className="text-slate-500 text-[10px] mt-0.5">{t.org}</div>
                          <p className="text-slate-600 text-xs mt-2 leading-relaxed">{t.bio}</p>
                        </div>
                      ))}
                    </div>
                    <p className="text-slate-600 text-xs mb-6 italic">
                      Additional supply-chain, consumables, and backorder-operations hires are planned across 2027&ndash;2028 as revenue milestones are met. See the full construction team roster and case work on the <a href="/solutions/general-contracting" className="text-slate-800 underline decoration-dotted underline-offset-2">Healthcare Construction</a> page.
                    </p>
                    <div className="rounded-xl border border-slate-200 bg-slate-50 p-5 mb-4">
                      <div className="flex items-start gap-4">
                        <div className="p-3 rounded-lg bg-slate-100 border border-slate-200 shrink-0">
                          <Building2 className="w-6 h-6 text-slate-800" />
                        </div>
                        <div>
                          <h3 className="text-xl font-bold text-slate-900 mb-1">Healthcare Construction Division &mdash; Y1 Anchor</h3>
                          <p className="text-slate-700 text-sm leading-relaxed">
                            Sterile Mate&apos;s general-contracting arm is tuned for one job: <span className="text-slate-800 font-semibold">healthcare environments where downtime costs revenue</span>. Donna Winfrey (Project & Development), Kimberly Phipps-Nichols (Principal Architect, 30+ years across Harris Health / Texas Children\u2019s / Memorial Hermann), and Miguel Mendoza (MAM Construction Services, Division 9 lead) are already delivering OR &amp; sterile-processing renovations, MEP coordination, accessibility (TAS/RAS) compliance, isolation-room retrofits, and cath-lab / infusion-suite build-outs today. The same team that battle-tests our own Ronan Rd 2nd-Level renovation is the one selling and building into hospitals. <span className="text-slate-900 font-semibold">Y1 target $400K &rarr; Y3 $1.5M &rarr; Y5 $2.7M</span>, with recurring pull-through into our Water Purification and BioBurden Cleaning practices.
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="grid md:grid-cols-3 gap-3">
                      <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
                        <div className="text-slate-800 text-[10px] font-bold uppercase tracking-widest mb-1">Team In Place</div>
                        <p className="text-slate-700 text-xs">Healthcare Construction anchored by <span className="text-slate-900 font-semibold">3 industry veterans</span> already delivering projects. Supply-chain, consumables, and backorder-operations hires phased in as 2027–2028 revenue milestones land.</p>
                      </div>
                      <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
                        <div className="text-slate-800 text-[10px] font-bold uppercase tracking-widest mb-1">Multi-Division Leverage</div>
                        <p className="text-slate-700 text-xs">Every construction job seeds pull-through for Water, Consumables, and BioBurden Cleaning. One customer, four contracts.</p>
                      </div>
                      <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
                        <div className="text-slate-800 text-[10px] font-bold uppercase tracking-widest mb-1">4-State Service Zone</div>
                        <p className="text-slate-700 text-xs">Sterile Mate &mdash; Hospital Supply Backorder &amp; Healthcare Facility Management: TX &middot; LA &middot; AL &middot; MS.</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="relative bg-white rounded-3xl overflow-hidden border border-slate-200 p-8 sm:p-10" data-testid="bioburden-cleaning-section">
                  <div className="absolute top-0 right-0 w-72 h-72 bg-cyan-500/10 rounded-full blur-3xl" />
                  <div className="relative z-10">
                    <div className="inline-flex items-center gap-2 px-4 py-2 bg-cyan-500/20 border border-cyan-500/40 rounded-full text-blue-900 text-sm mb-6">
                      <Shield className="w-4 h-4" />
                      Niche Service · Only a Few Vendors Nationwide
                    </div>
                    <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
                      Specialized BioBurden Cleaning Service
                    </h2>
                    <p className="text-lg text-slate-700 max-w-3xl mb-6">
                      A dedicated <span className="text-blue-900 font-semibold">specialized BioBurden decontamination program</span> for high-touch, high-risk clinical surfaces that <span className="text-slate-900 font-semibold">EVS is not trained or equipped to handle</span> — OR beds, patient stretchers, ICU beds, OR tables and articulating poles. This is not janitorial cleaning; it is validated, protocol-driven bioburden reduction executed on a scheduled cadence per hospital contract.
                    </p>
                    <div className="grid sm:grid-cols-4 gap-4 mt-6">
                      <div className="bg-white rounded-xl p-4 border border-cyan-500/20 text-center">
                        <div className="text-2xl font-bold text-blue-900">5</div>
                        <div className="text-xs text-slate-600 mt-1">Hospitals · Year 1</div>
                      </div>
                      <div className="bg-white rounded-xl p-4 border border-cyan-500/20 text-center">
                        <div className="text-2xl font-bold text-blue-900">$40K</div>
                        <div className="text-xs text-slate-600 mt-1">Avg Contract · Yr</div>
                      </div>
                      <div className="bg-white rounded-xl p-4 border border-cyan-500/20 text-center">
                        <div className="text-2xl font-bold text-blue-900">$200K</div>
                        <div className="text-xs text-slate-600 mt-1">Y1 Gross Revenue</div>
                      </div>
                      <div className="bg-white rounded-xl p-4 border border-cyan-500/20 text-center">
                        <div className="text-2xl font-bold text-slate-900">$900K</div>
                        <div className="text-xs text-slate-600 mt-1">Y5 Target</div>
                      </div>
                    </div>
                    <div className="grid md:grid-cols-2 gap-4 mt-8">
                      <div className="bg-slate-50 rounded-xl p-5 border border-slate-200">
                        <div className="text-blue-900 text-xs font-bold uppercase tracking-wider mb-2">Assets Serviced</div>
                        <ul className="text-slate-700 text-sm space-y-1 list-disc list-inside marker:text-blue-900">
                          <li>OR beds &amp; procedure tables</li>
                          <li>Patient stretchers (ED, transport, PACU)</li>
                          <li>ICU beds &amp; specialty support surfaces</li>
                          <li>OR tables &amp; articulating poles / accessories</li>
                        </ul>
                      </div>
                      <div className="bg-slate-50 rounded-xl p-5 border border-slate-200">
                        <div className="text-blue-900 text-xs font-bold uppercase tracking-wider mb-2">Why This Wins</div>
                        <ul className="text-slate-700 text-sm space-y-1 list-disc list-inside marker:text-blue-900">
                          <li>Only a handful of vendors offer this nationally</li>
                          <li>Distinct from EVS &mdash; validated protocol, ATP-verified</li>
                          <li>Reduces HAI risk on the highest-touch capital equipment</li>
                          <li>Recurring monthly/quarterly service contracts</li>
                        </ul>
                      </div>
                    </div>
                    <p className="text-slate-600 text-sm mt-6 italic">
                      Refer to the outreach campaign already in flight — five candidate systems briefed on the service scope, with signed pilots targeted for Q3 2026 launch.
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* TRACTION SECTION - Proof of Momentum */}
        {activeSection === "market" && (
          <div className="space-y-10 animate-fade-in">
            <div className="text-center mb-12">
              <h1 className="text-4xl font-bold mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Traction & Validation
              </h1>
              <p className="text-xl text-slate-600">Built on Sterile Mate&apos;s 6-year track record serving healthcare facilities</p>
            </div>

            {/* HEADLINE HIGHLIGHT STRIP — Backorder market hook */}
            <div className="relative overflow-hidden rounded-lg border-l-4 border-blue-900 bg-slate-50 border-y border-r border-slate-200">
              <div className="relative flex flex-col md:flex-row items-center gap-4 md:gap-6 px-6 py-5">
                <div className="flex items-center gap-3">
                  <span className="text-[10px] md:text-xs font-black text-blue-900 uppercase tracking-[0.2em] whitespace-nowrap">Market Signal</span>
                </div>
                <div className="hidden md:block h-8 w-px bg-slate-300" />
                <p className="text-slate-900 text-base md:text-lg text-center md:text-left flex-1">
                  <span className="font-semibold">&ldquo;Backorder&rdquo;</span> is healthcare&rsquo;s all-hands-on-deck signal — and we&rsquo;re on track to <span className="font-semibold">top the backorder-sourcing volume we last saw during COVID-19</span>.
                </p>
                <a href="#backorder-bulletin" className="whitespace-nowrap px-4 py-2 bg-blue-900 hover:bg-blue-800 text-white text-sm font-semibold rounded transition-colors">
                  Read the market thesis
                </a>
              </div>
            </div>

            {/* Key Traction Metrics */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-slate-50 border border-slate-200 rounded-2xl p-6 text-center">
                <TrendingUp className="w-8 h-8 mx-auto mb-3 text-emerald-600" />
                <p className="text-3xl font-bold text-emerald-600">$3.9M+</p>
                <p className="text-sm text-slate-600">Cumulative Revenue (2020-2025)</p>
              </div>
              <div className="bg-white border border-slate-200 rounded-2xl p-6 text-center">
                <Hospital className="w-8 h-8 mx-auto mb-3 text-blue-900" />
                <p className="text-3xl font-bold text-blue-900">$1.182M</p>
                <p className="text-sm text-slate-600">2026 Confirmed Pipeline</p>
              </div>
              <div className="bg-white border border-slate-200 rounded-2xl p-6 text-center">
                <Droplets className="w-8 h-8 mx-auto mb-3 text-blue-900" />
                <p className="text-3xl font-bold text-blue-900">$1M+</p>
                <p className="text-sm text-slate-600">Water Projects Installed</p>
              </div>
              <div className="bg-slate-50 border border-slate-200 rounded-2xl p-6 text-center">
                <Award className="w-8 h-8 mx-auto mb-3 text-slate-800" />
                <p className="text-3xl font-bold text-slate-800">5+</p>
                <p className="text-sm text-slate-600">Major Health System Clients</p>
              </div>
            </div>

            {/* Historical Revenue Performance - Sterile Mate Foundation */}
            <div className="bg-white/50 border border-slate-200 rounded-2xl p-8">
              <h2 className="text-2xl font-bold mb-6 flex items-center gap-3">
                <BarChart3 className="w-6 h-6 text-blue-900" />
                Sterile Mate Revenue History (Foundation for AskIsaac)
              </h2>
              <p className="text-slate-600 mb-6">AskIsaac Inc. is built on Sterile Mate LLC&apos;s proven hospital relationships and operational track record.</p>
              
              <div className="grid grid-cols-2 md:grid-cols-6 gap-4 mb-6">
                {[
                  { year: "2020", revenue: "$0", note: "Founded", color: "slate" },
                  { year: "2021", revenue: "$800K", note: "COVID Response", color: "emerald" },
                  { year: "2022", revenue: "$700K", note: "Water Purification Launch", color: "blue" },
                  { year: "2023", revenue: "$600K", note: "Federal Contracts", color: "cyan" },
                  { year: "2024", revenue: "$1.1M", note: "Major Installations", color: "violet" },
                  { year: "2025", revenue: "$1.5M+", note: "Expansion Year", color: "amber" }
                ].map((item, idx) => (
                  <div key={idx} className={`bg-${item.color}-500/10 border border-${item.color}-500/30 rounded-xl p-4 text-center`}>
                    <p className="text-slate-600 text-sm">{item.year}</p>
                    <p className={`text-xl font-bold text-${item.color}-400`}>{item.revenue}</p>
                    <p className="text-xs text-slate-500">{item.note}</p>
                  </div>
                ))}
              </div>

              <div className="bg-gradient-to-r from-cyan-500/10 to-blue-500/10 border border-cyan-500/20 rounded-xl p-4">
                <p className="text-center text-blue-900 font-medium">
                  <TrendingUp className="w-4 h-4 inline mr-2" />
                  AskIsaac inherits <span className="text-slate-900 font-bold">$3.9M+ in proven healthcare revenue</span> and existing hospital relationships
                </p>
              </div>
            </div>

            {/* Enterprise Validation Banner */}
            <div className="bg-white border border-cyan-500/40 rounded-3xl p-8">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-blue-900 to-blue-900 flex items-center justify-center">
                  <Hospital className="w-7 h-7 text-slate-900" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold">Enterprise Validation</h2>
                  <p className="text-blue-900">Live Production Deployment</p>
                </div>
              </div>
              <div className="bg-white/50 rounded-xl p-6 mb-6">
                <p className="text-lg text-slate-700 mb-4">
                  <span className="text-blue-900 font-bold">Harris Health System</span>, one of the largest public health systems in the United States, has active contracts with Sterile Mate for water purification, water softening, and infrastructure services.
                </p>
                <div className="grid md:grid-cols-3 gap-4">
                  <div className="bg-cyan-500/10 border border-cyan-500/20 rounded-lg p-4 text-center">
                    <p className="text-2xl font-bold text-blue-900">$791K</p>
                    <p className="text-sm text-slate-600">Water Softening Systems</p>
                  </div>
                  <div className="bg-cyan-500/10 border border-cyan-500/20 rounded-lg p-4 text-center">
                    <p className="text-2xl font-bold text-blue-900">$391K</p>
                    <p className="text-sm text-slate-600">Dialysis Water Purification</p>
                  </div>
                  <div className="bg-cyan-500/10 border border-cyan-500/20 rounded-lg p-4 text-center">
                    <p className="text-2xl font-bold text-blue-900">Active</p>
                    <p className="text-sm text-slate-600">Harris County + State of TX</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Year 1 Customer Acquisition - Warm Pipeline */}
            <div className="bg-white border border-slate-200 rounded-2xl p-8">
              <h2 className="text-2xl font-bold mb-4 flex items-center gap-3">
                <Target className="w-6 h-6 text-emerald-600" />
                Year 1 Customers: Already in the Pipeline
              </h2>
              <p className="text-slate-700 mb-6">
                {pipelineData.summary}
              </p>
              
              <div className="bg-white/50 rounded-xl p-4 mb-6">
                <div className="flex items-center gap-3 mb-3">
                  <Clock className="w-5 h-5 text-blue-900" />
                  <span className="font-bold text-blue-900">Sales Cycle Advantage</span>
                </div>
                <p className="text-slate-700">{pipelineData.salesCycleAdvantage}</p>
              </div>

              <div className="space-y-4">
                {pipelineData.warmLeads.map((lead, idx) => (
                  <div key={idx} className="bg-white border border-slate-200 rounded-xl p-5">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <Building2 className="w-6 h-6 text-emerald-600" />
                        <h3 className="font-bold text-slate-900">{lead.name}</h3>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="px-3 py-1 bg-slate-100 text-emerald-600 rounded-full text-sm font-semibold">
                          {lead.status}
                        </span>
                        <span className="px-3 py-1 bg-cyan-500/20 text-blue-900 rounded-full text-sm font-semibold">
                          {lead.probability} Close Rate
                        </span>
                      </div>
                    </div>
                    <div className="grid md:grid-cols-3 gap-4 text-sm">
                      <div>
                        <p className="text-slate-500">Relationship</p>
                        <p className="text-slate-700">{lead.relationship}</p>
                      </div>
                      <div>
                        <p className="text-slate-500">Existing Software</p>
                        <p className="text-slate-700">{lead.software}</p>
                      </div>
                      <div>
                        <p className="text-slate-500">Why We'll Win</p>
                        <p className="text-slate-700">{lead.notes}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Competitive Moat - Why They Can't Replicate */}
            <div className="bg-white border border-violet-500/40 rounded-2xl p-8">
              <h2 className="text-2xl font-bold mb-4 flex items-center gap-3">
                <Shield className="w-6 h-6 text-blue-900" />
                Why Competitors Can&apos;t Replicate This
              </h2>
              <p className="text-slate-700 mb-6">
                Software can be copied. Trust cannot. Here&apos;s what protects our position:
              </p>
              
              <div className="grid md:grid-cols-2 gap-4">
                {competitiveMoat.map((item, idx) => (
                  <div key={idx} className="bg-white border border-slate-200 rounded-xl p-5">
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="font-bold text-slate-900">{item.moat}</h3>
                      <span className={`px-2 py-1 rounded text-xs font-semibold ${
                        item.defensibility === 'Unmatched' ? 'bg-slate-100 text-slate-800' :
                        item.defensibility === 'Very High' ? 'bg-slate-100 text-emerald-600' :
                        'bg-blue-100 text-blue-900'
                      }`}>
                        {item.defensibility} Defensibility
                      </span>
                    </div>
                    <p className="text-slate-600 text-sm">{item.description}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Customer Pipeline */}
            <div className="bg-white/50 border border-slate-200 rounded-2xl p-8">
              <h2 className="text-2xl font-bold mb-6 flex items-center gap-3">
                <Target className="w-6 h-6 text-emerald-600" />
                Active Pipeline & Partnerships
              </h2>
              <div className="space-y-4">
                {[
                  { 
                    name: "Major Public Health System", 
                    status: "LIVE BETA", 
                    value: "Platform + Water Contracts",
                    stage: "Active",
                    color: "emerald",
                    description: "One of the largest public health systems in the US"
                  },
                  { 
                    name: "National VA Healthcare System", 
                    status: "ACTIVE PIPELINE", 
                    value: "Water Purification",
                    stage: "Nationwide",
                    color: "blue",
                    description: "Expanding water purification projects across VA facilities"
                  },
                  { 
                    name: "Automated Backorder Sourcing (AskIsaac)", 
                    status: "FLAGSHIP", 
                    value: "$1.2M → $5.0M",
                    stage: "6-yr benchmark, now automated",
                    color: "amber",
                    description: "Sterile Mate's 6-year door-opener into every Texas hospital system — now automated for regional/statewide scale"
                  },
                  { 
                    name: "Water Purification Solutions", 
                    status: "EXCLUSIVE", 
                    value: "Water + Bleu Flux",
                    stage: "Since 2022",
                    color: "cyan",
                    description: "Exclusive healthcare representation + Bleu Flux brand launch"
                  },
                  { 
                    name: "State of Texas", 
                    status: "AWARDED", 
                    value: "RO System",
                    stage: "Completed",
                    color: "amber",
                    description: "Government contract award for water purification"
                  }
                ].map((item, idx) => (
                  <div key={idx} className={`bg-${item.color}-500/10 border border-${item.color}-500/30 rounded-xl p-5 flex items-center justify-between`}>
                    <div className="flex items-center gap-4">
                      <div className={`w-12 h-12 rounded-xl bg-${item.color}-500/20 flex items-center justify-center`}>
                        <Building2 className={`w-6 h-6 text-${item.color}-400`} />
                      </div>
                      <div>
                        <h3 className="font-bold text-slate-900">{item.name}</h3>
                        <p className="text-slate-600 text-sm">{item.description}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <span className={`px-3 py-1 bg-${item.color}-500/20 text-${item.color}-400 rounded-full text-sm font-semibold`}>
                        {item.status}
                      </span>
                      <p className="text-slate-600 text-sm mt-1">{item.value}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Key Wins */}
            <div className="bg-slate-50 border border-slate-200 rounded-2xl p-8">
              <h2 className="text-2xl font-bold mb-6 flex items-center gap-3">
                <Award className="w-6 h-6 text-slate-800" />
                Key Wins & Proof Points
              </h2>
              <div className="grid md:grid-cols-2 gap-4">
                {[
                  { title: "Water Purification Expertise", description: "Locked in 2022 - specialized water purification solutions for healthcare", icon: Lock },
                  { title: "Automated Backorder Sourcing (6-yr benchmark)", description: "Sterile Mate's door-opener into every TX hospital system since 2020 — now fully automated via AskIsaac for regional/statewide scale", icon: Zap },
                  { title: "$1M+ Saved Per Department", description: "Proven ROI through data-driven right-sizing of water systems vs over-specced competitors", icon: DollarSign },
                  { title: "Federal Healthcare Access", description: "Active VA and government healthcare system relationships through SDVOSB partnerships", icon: Shield }
                ].map((win, idx) => (
                  <div key={idx} className="bg-white/50 rounded-xl p-5 flex items-start gap-4">
                    <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center flex-shrink-0">
                      <win.icon className="w-5 h-5 text-slate-800" />
                    </div>
                    <div>
                      <h3 className="font-bold text-slate-900 mb-1">{win.title}</h3>
                      <p className="text-slate-600 text-sm">{win.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Leadership Section */}
        {activeSection === "management" && (
          <div className="space-y-10 animate-fade-in">
            <div className="text-center mb-12">
              <h1 className="text-4xl font-bold mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Leadership
              </h1>
              <p className="text-xl text-slate-600">Meet the founder driving AskIsaac's vision</p>
            </div>

            {/* Founder Card */}
            <div className="bg-white border border-slate-200 rounded-3xl overflow-hidden">
              <div className="grid md:grid-cols-2 gap-0">
                {/* Image */}
                <div className="relative h-[400px] md:h-auto">
                  <img 
                    src={GEORGE_IMAGE} 
                    alt="George Broughton III - Founder, AskIsaac Inc"
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-white/80 via-transparent to-transparent" />
                  <div className="absolute bottom-6 left-6 right-6">
                    <span className="px-3 py-1 bg-blue-100 border border-slate-200 rounded-full text-blue-900 text-sm">
                      Founder, AskIsaac Inc
                    </span>
                  </div>
                </div>

                {/* Content */}
                <div className="p-8 flex flex-col justify-center">
                  <h2 className="text-3xl font-bold mb-2">George Broughton III</h2>
                  <p className="text-blue-900 font-semibold mb-6">President &amp; Strategic Director, Sterile Mate LLC &bull; Creator of AskIsaac.io</p>
                  
                  <div className="space-y-4 text-slate-700">
                    <p>
                      George Broughton III founded Sterile Mate LLC during COVID-19, stepping in as a <span className="text-blue-900 font-semibold">frontline triage supplier</span> for Houston hospitals in crisis. What started as an emergency response became a full-service healthcare infrastructure company with <span className="text-emerald-600 font-semibold">$3.9M+ in cumulative revenue</span>.
                    </p>
                    <p>
                      He launched <span className="text-blue-900 font-semibold">water purification solutions</span> in 2022, securing $1M+ in installations across Harris Health System. Today, George is leading the company&apos;s most ambitious move yet: acquiring {scenario.id === "humble" ? <>a <span className="text-blue-900 font-semibold">10,766 SF commercial headquarters</span> to house B2B operations, a retail medical supply storefront (The Supply Doctor), and a DME depot.</> : scenario.id === "ronan" ? <>a <span className="text-blue-900 font-semibold">22,750 SF Class-A 3-story corporate HQ on 5.08 acres</span> at 16651 Ronan Rd in Greenspoint to house Sterile Mate&apos;s executive HQ, an expanded backorder-sourcing operation, water-purification and healthcare-construction sales, a 3,500 SF short-stock warehouse, and the Texas-metro expansion sales office (San Antonio, Dallas, Austin, El Paso, Fort Worth, Galveston, Waco).</> : <>the <span className="text-blue-900 font-semibold">87,282 SF &ldquo;2SIXTY3&rdquo; Class-B campus</span> at 263 N Sam Houston Pkwy E to house B2B operations, a retail medical supply storefront (The Supply Doctor), and a DME depot.</>}
                    </p>
                    <p className="bg-blue-500/10 border border-slate-200 rounded-xl p-4 italic">
                      <span className="text-blue-900 font-semibold">&quot;I built this company because I saw hospitals struggling to get what they needed, when they needed it. This building gives us a permanent home to serve them even better — under one roof.&quot;</span>
                    </p>
                    <p>
                      George also created <span className="text-blue-900 font-semibold">AskIsaac.io</span>, a proprietary AI-powered project management platform that powers all of Sterile Mate&apos;s operations — from procurement and compliance tracking to project execution and client reporting.
                    </p>
                  </div>

                  <div className="mt-8 grid grid-cols-3 gap-4">
                    <div className="bg-white/50 rounded-xl p-4 text-center">
                      <p className="text-2xl font-bold text-blue-900">$3.9M+</p>
                      <p className="text-sm text-slate-600">Revenue Generated</p>
                    </div>
                    <div className="bg-white/50 rounded-xl p-4 text-center">
                      <p className="text-2xl font-bold text-emerald-600">HUB</p>
                      <p className="text-sm text-slate-600">Certified | SAM.gov</p>
                    </div>
                    <div className="bg-white/50 rounded-xl p-4 text-center">
                      <p className="text-2xl font-bold text-slate-800">$1.182M</p>
                      <p className="text-sm text-slate-600">2026 Pipeline</p>
                    </div>
                  </div>

                  <a 
                    href="mailto:george.broughton@sterilemate.com"
                    className="mt-6 inline-flex items-center gap-2 text-blue-900 hover:text-blue-900 font-medium"
                  >
                    <Mail className="w-4 h-4" />
                    george.broughton@sterilemate.com
                  </a>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Market Opportunity Section */}
        {activeSection === "market" && (
          <div className="space-y-10 animate-fade-in">
            <div className="text-center mb-12">
              <h1 className="text-4xl font-bold mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Market Opportunity
              </h1>
              <p className="text-xl text-slate-600">$67B+ combined market — Healthcare Infrastructure + DME/Mobility</p>
            </div>

            {/* ============================================================ */}
            {/* THE HEADLINE BULLETIN — Backorder Sourcing Gold Mine        */}
            {/* ============================================================ */}
            <div id="backorder-bulletin" className="relative overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm scroll-mt-24">
              {/* Corner marker — subtle blue dot, not pulsing red */}
              <div className="absolute top-4 right-4 flex items-center gap-2 z-10">
                <span className="inline-flex rounded-full h-2 w-2 bg-blue-900" />
                <span className="text-[10px] font-semibold text-slate-500 uppercase tracking-widest">Market Signal</span>
              </div>

              <div className="p-8 md:p-12">
                {/* Clean tag */}
                <div className="inline-flex items-center gap-2 px-3 py-1 bg-blue-900 text-white text-[11px] font-bold rounded mb-6 uppercase tracking-widest">
                  Headline Market Opportunity
                </div>

                <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4 leading-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  &ldquo;Backorder&rdquo; is Healthcare&rsquo;s All-Hands-on-Deck Signal.
                </h2>
                <p className="text-lg text-slate-700 mb-8 max-w-4xl">
                  We&rsquo;ve been the answer to that signal for <span className="text-slate-900 font-semibold">six years</span>. AskIsaac just made it a national business.
                </p>

                <div className="grid md:grid-cols-2 gap-6 mb-8">
                  {/* The Problem */}
                  <div className="bg-slate-50 border border-slate-200 rounded-lg p-6">
                    <div className="flex items-center gap-2 mb-3">
                      <span className="text-[10px] font-black text-slate-700 uppercase tracking-widest">The Everyday Crisis</span>
                    </div>
                    <ul className="space-y-3 text-slate-700">
                      <li className="flex items-start gap-2">
                        <span className="text-slate-400 mt-1">·</span>
                        <span><strong className="text-slate-900">Backorders happen every single day</strong> across every US hospital — surgical trays paused, ORs delayed, ED carts empty.</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-slate-400 mt-1">·</span>
                        <span><strong className="text-slate-900">Severe-disaster surges</strong> (hurricanes, wildfires, pandemics, supply-chain shocks) flood us with SOS requests from multiple regions of the country <em>at the same time</em>.</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-slate-400 mt-1">·</span>
                        <span>Materials Managers stop everything to hunt substitutes — the word &ldquo;backorder&rdquo; is a <strong className="text-slate-900">Code Red for procurement teams</strong>.</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-slate-400 mt-1">·</span>
                        <span>We are already tracking to <strong className="text-slate-900">top the backorder-sourcing volume we last saw during COVID-19</strong> — the market is hotter than 2020.</span>
                      </li>
                    </ul>
                  </div>

                  {/* Our Answer */}
                  <div className="bg-slate-50 border border-slate-200 rounded-lg p-6">
                    <div className="flex items-center gap-2 mb-3">
                      <span className="text-[10px] font-black text-blue-900 uppercase tracking-widest">Why We Own This</span>
                    </div>
                    <ul className="space-y-3 text-slate-700">
                      <li className="flex items-start gap-2">
                        <CheckCircle2 className="w-4 h-4 text-blue-900 mt-1 flex-shrink-0" />
                        <span><strong className="text-slate-900">6-year benchmark:</strong> Backorder sourcing has been our door-opener into every Texas hospital system since 2020.</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle2 className="w-4 h-4 text-blue-900 mt-1 flex-shrink-0" />
                        <span><strong className="text-slate-900">National supplier network in every region</strong> — we can match SKUs, ETAs, and pricing in minutes instead of days.</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle2 className="w-4 h-4 text-blue-900 mt-1 flex-shrink-0" />
                        <span><strong className="text-slate-900">AskIsaac RFQ automation:</strong> instant multi-supplier broadcast, ETA tracking, hospital-alert engine — lets one operator do the work of ten.</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle2 className="w-4 h-4 text-blue-900 mt-1 flex-shrink-0" />
                        <span><strong className="text-slate-900">Inside-sales + healthcare-outreach automation</strong> can 5–10x the volume without adding proportional headcount.</span>
                      </li>
                    </ul>
                  </div>
                </div>

                {/* Founder Constraint Callout — clean navy admonition */}
                <div className="bg-slate-50 border-l-4 border-blue-900 rounded-r p-5 mb-8">
                  <p className="text-slate-800 text-base leading-relaxed">
                    <span className="text-blue-900 font-bold uppercase tracking-wider text-xs block mb-2">Where the Opportunity Sits Today</span>
                    Every backorder request in the pipeline right now is being handled <span className="font-semibold text-slate-900">personally by our CEO, George Broughton</span>. That&rsquo;s the ceiling. Deploy an inside-sales team plugged into AskIsaac, and this single line item alone becomes a national procurement business.
                  </p>
                </div>

                {/* Competitor Comparison */}
                <div>
                  <h3 className="text-xl font-bold text-slate-900 mb-4">Competitor Landscape — They Try. They Can&rsquo;t Respond Like We Do.</h3>
                  <div className="overflow-x-auto rounded-xl border border-slate-200/60">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-slate-100">
                          <th className="text-left py-3 px-4 text-slate-700 font-semibold">Player</th>
                          <th className="text-left py-3 px-4 text-slate-700 font-semibold">Model</th>
                          <th className="text-center py-3 px-4 text-slate-700 font-semibold">Regional Speed</th>
                          <th className="text-center py-3 px-4 text-slate-700 font-semibold">National Response</th>
                          <th className="text-center py-3 px-4 text-slate-700 font-semibold">Automated RFQ</th>
                        </tr>
                      </thead>
                      <tbody className="text-slate-700">
                        {[
                          { name: "Sterile Mate + AskIsaac", model: "Automated national procurement w/ 6-yr hospital relationships", regional: "\u2705 Minutes", national: "\u2705 Every Region", auto: "\u2705 Full RFQ Engine", highlight: true },
                          { name: "CIA Medical", model: "Catalog-first distributor — online product search", regional: "\u26A0\uFE0F Hours–Days", national: "\u26A0\uFE0F Limited", auto: "\u274C Manual" },
                          { name: "Grayline Medical", model: "Discount medical & DME reseller", regional: "\u26A0\uFE0F Hours–Days", national: "\u26A0\uFE0F Limited", auto: "\u274C Manual" },
                          { name: "Vitality Medical", model: "Consumer/DME e-commerce with B2B add-on", regional: "\u26A0\uFE0F Slow", national: "\u274C Retail-first", auto: "\u274C None" },
                        ].map((c, idx) => (
                          <tr key={idx} className={`border-t border-slate-200 ${c.highlight ? "bg-blue-50" : idx % 2 ? "bg-slate-50" : "bg-white"}`}>
                            <td className={`py-3 px-4 ${c.highlight ? "text-blue-900 font-bold" : "text-slate-900 font-semibold"}`}>{c.name}</td>
                            <td className="py-3 px-4 text-slate-700">{c.model}</td>
                            <td className="py-3 px-4 text-center">{c.regional}</td>
                            <td className="py-3 px-4 text-center">{c.national}</td>
                            <td className="py-3 px-4 text-center">{c.auto}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <p className="text-xs text-slate-600 mt-3 italic">Competitors have taken partial advantage of hospital backorder demand as catalog distributors, but none combine a national supplier network with automated RFQ dispatch and 6 years of embedded hospital relationships.</p>
                </div>

                {/* Backorder Scaling Capacity Model — shows headroom, not misleading live counts */}
                <div className="mt-8 bg-slate-100 rounded-2xl border border-slate-200 p-6">
                  <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
                    <div>
                      <h3 className="text-lg font-bold text-slate-900">Scaling Capacity Model — From Founder-Led to National Team</h3>
                      <p className="text-xs text-slate-600 mt-1">Illustrative capacity per staffing tier · AskIsaac RFQ automation constant across all tiers.</p>
                    </div>
                    <span className="text-[10px] font-black text-slate-800 uppercase tracking-widest bg-slate-100 border border-slate-200 rounded-full px-3 py-1">Projection Model</span>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-left text-[11px] uppercase tracking-wider text-slate-600 border-b border-slate-200/60">
                          <th className="py-2 pr-3 font-semibold">Tier</th>
                          <th className="py-2 pr-3 font-semibold">Sourcing Team</th>
                          <th className="py-2 pr-3 font-semibold">RFQ Capacity / Mo</th>
                          <th className="py-2 pr-3 font-semibold">Avg Deal Size</th>
                          <th className="py-2 pr-3 font-semibold">Monthly Revenue Ceiling</th>
                          <th className="py-2 pr-3 font-semibold">Coverage</th>
                        </tr>
                      </thead>
                      <tbody className="text-slate-700">
                        {[
                          { tier: "Today", team: "CEO only, manual + AskIsaac", rfq: "~40", deal: "$2,500", rev: "~$100K", cover: "TX hospital systems", tone: "slate" },
                          { tier: "Tier 1", team: "+1 Inside-Sales Rep", rfq: "~120", deal: "$2,500", rev: "~$300K", cover: "TX + neighboring states", tone: "amber" },
                          { tier: "Tier 2", team: "+3 Inside-Sales Reps + 1 SDR", rfq: "~350", deal: "$2,750", rev: "~$960K", cover: "South + Southeast US", tone: "orange" },
                          { tier: "Tier 3", team: "+8 Reps + 2 SDRs + Ops Lead", rfq: "~900", deal: "$3,000", rev: "~$2.7M", cover: "National (multi-region)", tone: "emerald" },
                        ].map((r, idx) => (
                          <tr key={idx} className={`border-b border-slate-800/60 ${r.tone === "emerald" ? "bg-emerald-500/5" : ""}`}>
                            <td className={`py-3 pr-3 font-bold ${r.tone === "emerald" ? "text-emerald-700" : r.tone === "orange" ? "text-slate-800" : r.tone === "amber" ? "text-slate-800" : "text-slate-700"}`}>{r.tier}</td>
                            <td className="py-3 pr-3 text-slate-700">{r.team}</td>
                            <td className="py-3 pr-3 text-slate-900 font-semibold">{r.rfq}</td>
                            <td className="py-3 pr-3 text-slate-700">{r.deal}</td>
                            <td className={`py-3 pr-3 font-bold ${r.tone === "emerald" ? "text-emerald-700" : "text-slate-900"}`}>{r.rev}</td>
                            <td className="py-3 pr-3 text-slate-600 text-xs">{r.cover}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <p className="text-[11px] text-slate-500 mt-4 italic leading-relaxed">
                    Model assumes AskIsaac automation stays constant (dispatch, ETA tracking, hospital alerts) while each incremental rep handles the human relationship + urgency triage. Deal size grows modestly with regional expansion (higher-volume health systems). Coverage jumps meaningfully at Tier 3 as we cross state lines with existing supplier partners. <span className="text-slate-800 font-semibold">The Y1 P&amp;L target ($1.2M backorder revenue) sits between Tier 1 and Tier 2 — meaning we hit plan with modest hiring, not aggressive.</span>
                  </p>
                </div>
              </div>
            </div>

            {/* ============================================================ */}
            {/* GPO MARKET THESIS — 20% off-contract sweet spot + Growth      */}
            {/* Executive-scan panel: market size · growth · our share       */}
            {/* ============================================================ */}
            <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-sm">
              <div className="px-6 sm:px-10 py-4 bg-slate-50 border-b border-slate-200 flex items-center justify-between flex-wrap gap-2">
                <span className="text-xs font-bold text-blue-900 uppercase tracking-[0.25em]">Where We Play · GPO Off-Contract Thesis</span>
                <span className="text-xs text-slate-500">Executive Skim · 20-Second Read</span>
              </div>
              <div className="p-6 sm:p-10">
                <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-3 leading-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  20% of hospital spend lives <span className="text-blue-900">outside</span> the GPO. That&rsquo;s our sweet spot.
                </h2>
                <p className="text-slate-700 text-lg max-w-4xl mb-8">
                  ~80% of US hospital procurement flows through Group Purchasing Organizations (Vizient, Premier, HealthTrust, HPG). The remaining <span className="text-blue-900 font-semibold">~20% is off-contract, discretionary, urgent spend</span> — backorders, one-off capital projects, disaster-surge sourcing, specialty consumables. GPOs don&rsquo;t serve it well; we do.
                </p>

                {/* The 80/20 visual bar */}
                <div className="mb-8">
                  <div className="flex items-center justify-between text-xs text-slate-500 mb-2">
                    <span>US Hospital Procurement Spend Distribution</span>
                    <span>Source: industry-standard GPO market data</span>
                  </div>
                  <div className="flex h-10 rounded overflow-hidden border border-slate-200">
                    <div className="bg-slate-200 flex items-center justify-center text-slate-800 text-sm font-semibold" style={{ width: '80%' }}>
                      80% · GPO Contracted (Vizient, Premier, HealthTrust)
                    </div>
                    <div className="bg-blue-900 flex items-center justify-center text-white text-sm font-bold" style={{ width: '20%' }}>
                      20% · Off-Contract
                    </div>
                  </div>
                  <p className="text-xs text-slate-600 mt-2">
                    That 20% national average = urgent backorders, specialty items, disaster-driven demand, ad-hoc capital. <span className="text-blue-900 font-semibold">Our operating home base.</span>
                  </p>
                </div>

                {/* Market Size / Growth / Our Share — three-panel scan strip */}
                <div className="grid md:grid-cols-3 gap-4 mb-8">
                  <div className="bg-slate-50 rounded-lg p-5 border border-slate-200">
                    <div className="flex items-center gap-2 mb-3">
                      <Globe className="w-4 h-4 text-slate-500" />
                      <span className="text-[10px] font-black text-slate-700 uppercase tracking-widest">Market Size</span>
                    </div>
                    <p className="text-3xl font-bold text-slate-900 mb-1">$260B</p>
                    <p className="text-sm text-slate-600 leading-relaxed">
                      US hospital supply spend (2026). <span className="text-slate-900 font-semibold">~$52B lives off-contract</span> — the addressable slice for procurement players like us.
                    </p>
                  </div>
                  <div className="bg-slate-50 rounded-lg p-5 border border-slate-200">
                    <div className="flex items-center gap-2 mb-3">
                      <TrendingUp className="w-4 h-4 text-slate-500" />
                      <span className="text-[10px] font-black text-slate-700 uppercase tracking-widest">Expected Growth</span>
                    </div>
                    <p className="text-3xl font-bold text-slate-900 mb-1">6.5% CAGR</p>
                    <p className="text-sm text-slate-600 leading-relaxed">
                      Off-contract spend growing faster than GPO-contract spend as backorders, specialty items, and disaster demand rise post-pandemic. Aging infrastructure accelerates capital demand.
                    </p>
                  </div>
                  <div className="bg-blue-900 rounded-lg p-5">
                    <div className="flex items-center gap-2 mb-3">
                      <Target className="w-4 h-4 text-white" />
                      <span className="text-[10px] font-black text-white uppercase tracking-widest">Our Potential Share (Y5)</span>
                    </div>
                    <p className="text-3xl font-bold text-white mb-1">$180M</p>
                    <p className="text-sm text-blue-100 leading-relaxed">
                      Conservative 0.35% share of the <em>off-contract</em> slice by Y5. Every 0.1% we take beyond that is pure upside — no GPO gatekeeper required.
                    </p>
                  </div>
                </div>

                {/* Dual-Path Strategy: Off-Contract + Branded Consumables INTO GPO */}
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="bg-white rounded-lg p-6 border border-slate-200">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-8 h-8 rounded bg-blue-900 flex items-center justify-center text-white font-bold text-sm">A</div>
                      <h3 className="text-lg font-bold text-slate-900">Path A — Off-Contract Sweet Spot</h3>
                    </div>
                    <p className="text-slate-600 text-sm leading-relaxed mb-3">Own the urgent 20% GPOs can&rsquo;t serve.</p>
                    <ul className="space-y-2 text-sm text-slate-700">
                      <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-blue-900 mt-0.5 flex-shrink-0" /><span>Backorder sourcing (AskIsaac RFQ engine)</span></li>
                      <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-blue-900 mt-0.5 flex-shrink-0" /><span>Disaster-surge multi-region response</span></li>
                      <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-blue-900 mt-0.5 flex-shrink-0" /><span>Specialty items GPOs don&rsquo;t stock</span></li>
                      <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-blue-900 mt-0.5 flex-shrink-0" /><span>Capital projects (water systems, construction)</span></li>
                    </ul>
                  </div>
                  <div className="bg-white rounded-lg p-6 border border-slate-200">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-8 h-8 rounded bg-blue-900 flex items-center justify-center text-white font-bold text-sm">B</div>
                      <h3 className="text-lg font-bold text-slate-900">Path B — Branded Consumables <em>Into</em> GPO</h3>
                    </div>
                    <p className="text-slate-600 text-sm leading-relaxed mb-3">Cross-sell our private-label into contracted channels.</p>
                    <ul className="space-y-2 text-sm text-slate-700">
                      <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-blue-900 mt-0.5 flex-shrink-0" /><span>Sterile Mate exam gloves (Intco direct-import)</span></li>
                      <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-blue-900 mt-0.5 flex-shrink-0" /><span>Sterile Mate microfiber (Texas MF, branded 50-ct pop-up)</span></li>
                      <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-blue-900 mt-0.5 flex-shrink-0" /><span>Target: GPO-priced distribution contracts (Vizient / Premier / HealthTrust)</span></li>
                      <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-blue-900 mt-0.5 flex-shrink-0" /><span>Turns branded SKUs into a recurring, GPO-covered revenue tail</span></li>
                    </ul>
                  </div>
                </div>

                <p className="text-xs text-slate-500 mt-6 italic leading-relaxed">
                  <span className="text-slate-900 font-semibold">The two paths compound:</span> Path A opens the door with urgent, high-margin backorder sourcing. Once we&rsquo;re inside the hospital, Path B places our branded consumables on the shelf under a GPO contract for recurring, forecastable volume. We don&rsquo;t need to disrupt GPOs — we ride alongside them where they don&rsquo;t serve, and inside them where they do.
                </p>
              </div>
            </div>

            {/* Market Segments + Tailwinds */}
            <div className="bg-white border border-slate-200 rounded-3xl p-8">
              <h2 className="text-2xl font-bold mb-6 flex items-center gap-3">
                <Globe className="w-6 h-6 text-blue-900" />
                Segment Detail &amp; Tailwinds
              </h2>
              <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-white/50 rounded-xl p-5">
                  <h3 className="font-bold text-slate-900 mb-4">Market Segments We Serve</h3>
                  <div className="space-y-3">
                    {[
                      { segment: "Water Purification Systems", size: "$2.1B", growth: "8% CAGR" },
                      { segment: "Durable Medical Equipment (DME)", size: "$20B", growth: "6% CAGR" },
                      { segment: "Hospital EVS & Janitorial", size: "$3.4B", growth: "5% CAGR" },
                      { segment: "Mobility & Rehabilitation Equipment", size: "$8.5B", growth: "7% CAGR" },
                      { segment: "Compliance & Sterile Processing", size: "$0.9B", growth: "12% CAGR" }
                    ].map((item, idx) => (
                      <div key={idx} className="flex items-center justify-between py-2 border-b border-slate-200 last:border-0">
                        <span className="text-slate-700">{item.segment}</span>
                        <div className="text-right">
                          <span className="text-slate-900 font-semibold">{item.size}</span>
                          <span className="text-emerald-600 text-sm ml-2">{item.growth}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="bg-white/50 rounded-xl p-5">
                  <h3 className="font-bold text-slate-900 mb-4">Why Now? Market Tailwinds</h3>
                  <div className="space-y-3">
                    {[
                      { trend: "Post-COVID Infrastructure Investment", impact: "Hospitals upgrading aging systems" },
                      { trend: "Labor Shortage in Healthcare", impact: "Need for efficient contractor deployment" },
                      { trend: "Compliance Tightening", impact: "AAMI ST108, Joint Commission requirements" },
                      { trend: "Digital Transformation Push", impact: "Hospitals seeking SaaS solutions" }
                    ].map((item, idx) => (
                      <div key={idx} className="flex items-start gap-3 py-2 border-b border-slate-200 last:border-0">
                        <TrendingUp className="w-4 h-4 text-emerald-600 mt-1 flex-shrink-0" />
                        <div>
                          <p className="text-slate-900 font-medium">{item.trend}</p>
                          <p className="text-slate-600 text-sm">{item.impact}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Problem Statement */}
            <div className="bg-gradient-to-br from-red-50 to-orange-50 border border-red-500/20 rounded-2xl p-8">
              <h2 className="text-2xl font-bold mb-4 flex items-center gap-3 text-red-600">
                <AlertTriangle className="w-6 h-6" />
                The Problem
              </h2>
              <p className="text-slate-700 text-lg leading-relaxed mb-4">
                Healthcare service providers consistently <span className="text-red-600 font-semibold">over-spec systems where not needed</span>. From water purification to HVAC, sterilization to plumbing, vendors push unnecessary FDA 510K-compliant equipment and over-engineered solutions that cost hospitals <span className="text-red-600 font-semibold">$500K+ more than necessary</span> per department.
              </p>
              <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                <div className="bg-red-50 border border-red-500/20 rounded-lg p-3">
                  <span className="text-red-700">Water Systems:</span> <span className="text-slate-600">510K equipment where basic purification suffices</span>
                </div>
                <div className="bg-red-50 border border-red-500/20 rounded-lg p-3">
                  <span className="text-red-700">HVAC/Mechanical:</span> <span className="text-slate-600">Over-sized units for compliance theater</span>
                </div>
                <div className="bg-amber-900/30 border border-slate-200 rounded-lg p-3">
                  <span className="text-slate-800">Maintenance:</span> <span className="text-slate-600">Expand contractor options beyond single-source contracts</span>
                </div>
                <div className="bg-red-50 border border-red-500/20 rounded-lg p-3">
                  <span className="text-red-700">Procurement:</span> <span className="text-slate-600">3-quote process takes weeks, delays care</span>
                </div>
              </div>
            </div>

            {/* Solution */}
            <div className="bg-white border border-emerald-500/20 rounded-2xl p-8">
              <h2 className="text-2xl font-bold mb-4 flex items-center gap-3 text-emerald-600">
                <Lightbulb className="w-6 h-6" />
                Our Solution: Cost Rectification Through Data
              </h2>
              <p className="text-slate-700 text-lg leading-relaxed mb-6">
                We use <span className="text-emerald-600 font-semibold">software-backed validation</span> instead of hardware-heavy legacy quotes. AskIsaac&apos;s data-driven approach right-sizes every specification — saving hospitals over <span className="text-emerald-600 font-semibold">$1M per department</span> while maintaining full regulatory compliance.
              </p>
              <div className="grid md:grid-cols-2 gap-4">
                {competitiveAdvantages.slice(0, 6).map((adv, idx) => (
                  <div key={idx} className="bg-white/50 border border-slate-200 rounded-xl p-5">
                    <div className="flex items-center gap-3 mb-3">
                      <adv.icon className="w-6 h-6 text-blue-900" />
                      <h3 className="font-semibold">{adv.title}</h3>
                    </div>
                    <p className="text-slate-600 text-sm mb-3">{adv.description}</p>
                    <span className="inline-block px-3 py-1 bg-emerald-500/10 text-emerald-600 rounded-full text-sm font-medium">
                      {adv.savings}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Market Segments */}
            <div>
              <h2 className="text-2xl font-bold mb-6 flex items-center gap-3">
                <Globe className="w-6 h-6 text-blue-900" />
                Target Markets
              </h2>
              <div className="grid md:grid-cols-4 gap-4">
                {[
                  { 
                    title: "Hospital Systems (B2B)", 
                    description: "Water purification, EVS, medical device infrastructure for public health systems and regional hospitals",
                    icon: Hospital,
                    color: "from-blue-900 to-blue-900"
                  },
                  { 
                    title: "Government Contracts", 
                    description: "VA, Harris County, State of Texas, FEMA, GSA — HUB Certified with active contracts",
                    icon: Building2,
                    color: "from-blue-900 to-blue-900"
                  },
                  { 
                    title: "Retail Medical Supply", 
                    description: "The Supply Doctor — mobility, rehab, home health for consumers in NE Houston&apos;s $1.3B spending corridor",
                    icon: Store,
                    color: "from-blue-900 to-blue-900"
                  },
                  { 
                    title: "DME / Insurance", 
                    description: "Medicare and private insurance billing for durable medical equipment — wheelchairs, scooters, braces",
                    icon: Home,
                    color: "from-blue-900 to-blue-900"
                  }
                ].map((market, idx) => (
                  <div key={idx} className="bg-white/50 border border-slate-200 rounded-2xl p-6 text-center">
                    <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${market.color} flex items-center justify-center mx-auto mb-4`}>
                      <market.icon className="w-7 h-7 text-slate-900" />
                    </div>
                    <h3 className="font-semibold text-lg mb-2">{market.title}</h3>
                    <p className="text-slate-600 text-sm">{market.description}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Digital Innovation */}
            <div className="bg-white border border-violet-500/20 rounded-2xl p-8">
              <h2 className="text-2xl font-bold mb-4 flex items-center gap-3 text-blue-900">
                <Code className="w-6 h-6" />
                Digital Innovation
              </h2>
              <p className="text-slate-700 text-lg leading-relaxed">
                We've introduced <span className="text-blue-900 font-semibold">infection prevention validation digital tools</span> that change the game when it comes to clean validation and system reporting. Direct to VA Sterile Processing departments.
              </p>
            </div>
          </div>
        )}

        {/* GO-TO-MARKET STRATEGY SECTION */}
        {activeSection === "market" && (
          <div className="space-y-10 animate-fade-in">
            <div className="text-center mb-12">
              <h1 className="text-4xl font-bold mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Go-to-Market Strategy
              </h1>
              <p className="text-xl text-slate-600">How we win and retain healthcare customers</p>
            </div>

            {/* Sales Motion Overview */}
            <div className="bg-white border border-slate-200 rounded-3xl p-8">
              <h2 className="text-2xl font-bold mb-6 flex items-center gap-3">
                <Target className="w-6 h-6 text-blue-900" />
                Sales Motion: Relationship-First, Platform-Second
              </h2>
              <p className="text-slate-700 mb-6">
                AskIsaac&apos;s GTM leverages Sterile Mate&apos;s existing hospital relationships. We enter through <span className="text-blue-900 font-semibold">service contracts</span> (water, maintenance), then expand to <span className="text-blue-900 font-semibold">platform adoption</span> once trust is established.
              </p>
              
              <div className="grid md:grid-cols-4 gap-4">
                {[
                  { step: "1", title: "Initial Contact", description: "Sterile Mate service contract or referral", icon: Phone, time: "Week 1" },
                  { step: "2", title: "Pain Discovery", description: "Identify facility pain points and compliance gaps", icon: Target, time: "Week 2-3" },
                  { step: "3", title: "Pilot Project", description: "Small water or maintenance project ($25-100K)", icon: Wrench, time: "Week 4-8" },
                  { step: "4", title: "Platform Upsell", description: "Introduce AskIsaac for ongoing operations", icon: Layers, time: "Month 3+" }
                ].map((item, idx) => (
                  <div key={idx} className="bg-white/50 border border-slate-200 rounded-xl p-5 text-center">
                    <div className="w-12 h-12 rounded-full bg-blue-900 flex items-center justify-center mx-auto mb-3">
                      <span className="text-slate-900 font-bold text-lg">{item.step}</span>
                    </div>
                    <h3 className="font-bold text-slate-900 mb-1">{item.title}</h3>
                    <p className="text-slate-600 text-sm mb-2">{item.description}</p>
                    <span className="text-xs text-blue-900">{item.time}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Target Customer Profile */}
            <div className="bg-white/50 border border-slate-200 rounded-2xl p-8">
              <h2 className="text-2xl font-bold mb-6 flex items-center gap-3">
                <Users className="w-6 h-6 text-emerald-600" />
                Ideal Customer Profile (ICP)
              </h2>
              
              <div className="grid md:grid-cols-3 gap-6">
                <div className="bg-emerald-500/10 border border-slate-200 rounded-xl p-6">
                  <Hospital className="w-10 h-10 text-emerald-600 mb-4" />
                  <h3 className="font-bold text-lg text-emerald-600 mb-2">Primary: Health Systems</h3>
                  <ul className="text-slate-700 text-sm space-y-2">
                    <li>• 3-50+ facilities</li>
                    <li>• $5M+ annual facility spend</li>
                    <li>• Sterile Processing Departments</li>
                    <li>• Compliance-focused leadership</li>
                  </ul>
                  <div className="mt-4 pt-4 border-t border-emerald-500/20">
                    <p className="text-emerald-600 font-semibold">ACV: $150K - $500K</p>
                  </div>
                </div>
                
                <div className="bg-blue-500/10 border border-slate-200 rounded-xl p-6">
                  <Shield className="w-10 h-10 text-blue-900 mb-4" />
                  <h3 className="font-bold text-lg text-blue-900 mb-2">Secondary: VA/Federal</h3>
                  <ul className="text-slate-700 text-sm space-y-2">
                    <li>• VA Medical Centers</li>
                    <li>• Federal healthcare facilities</li>
                    <li>• SDVOSB pathway access</li>
                    <li>• Long procurement cycles</li>
                  </ul>
                  <div className="mt-4 pt-4 border-t border-blue-500/20">
                    <p className="text-blue-900 font-semibold">ACV: $100K - $300K</p>
                  </div>
                </div>
                
                <div className="bg-violet-500/10 border border-slate-200 rounded-xl p-6">
                  <Building2 className="w-10 h-10 text-blue-900 mb-4" />
                  <h3 className="font-bold text-lg text-blue-900 mb-2">Tertiary: Regional Hospitals</h3>
                  <ul className="text-slate-700 text-sm space-y-2">
                    <li>• Single facility or small network</li>
                    <li>• $1-5M annual facility spend</li>
                    <li>• Limited procurement staff</li>
                    <li>• Need turnkey solutions</li>
                  </ul>
                  <div className="mt-4 pt-4 border-t border-violet-500/20">
                    <p className="text-blue-900 font-semibold">ACV: $50K - $150K</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Sales Channels */}
            <div className="bg-white/50 border border-slate-200 rounded-2xl p-8">
              <h2 className="text-2xl font-bold mb-6 flex items-center gap-3">
                <Layers className="w-6 h-6 text-blue-900" />
                Sales Channels
              </h2>
              
              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <h3 className="font-bold text-lg text-blue-900">Direct Sales (Primary)</h3>
                  {[
                    { channel: "Founder-Led Sales", description: "George leverages existing relationships in Texas Medical Center", conversion: "40% close rate" },
                    { channel: "Inbound from Demo", description: "Hospital staff discover AskIsaac via trade shows or referrals", conversion: "25% close rate" },
                    { channel: "Service-to-Platform", description: "Sterile Mate service contracts convert to platform deals", conversion: "60% upsell rate" }
                  ].map((item, idx) => (
                    <div key={idx} className="bg-slate-50 rounded-lg p-4 flex items-center justify-between">
                      <div>
                        <p className="font-semibold text-slate-900">{item.channel}</p>
                        <p className="text-slate-600 text-sm">{item.description}</p>
                      </div>
                      <span className="text-emerald-600 font-semibold text-sm whitespace-nowrap ml-4">{item.conversion}</span>
                    </div>
                  ))}
                </div>
                
                <div className="space-y-4">
                  <h3 className="font-bold text-lg text-blue-900">Partner Channels (Secondary)</h3>
                  {[
                    { channel: "Water Project Referrals", description: "Water projects lead to platform introductions", conversion: "Growing" },
                    { channel: "Backorder-to-Full-Line Upsell", description: "6-yr backorder clients converted to Water/Devices/Construction accounts", conversion: "Established" },
                    { channel: "SDVOSB Network", description: "Federal contractor partnerships for VA access", conversion: "Established" }
                  ].map((item, idx) => (
                    <div key={idx} className="bg-slate-50 rounded-lg p-4 flex items-center justify-between">
                      <div>
                        <p className="font-semibold text-slate-900">{item.channel}</p>
                        <p className="text-slate-600 text-sm">{item.description}</p>
                      </div>
                      <span className="text-blue-900 font-semibold text-sm whitespace-nowrap ml-4">{item.conversion}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Sales Cycle & Process */}
            <div className="bg-slate-50 border border-slate-200 rounded-2xl p-8">
              <h2 className="text-2xl font-bold mb-6 flex items-center gap-3">
                <Clock className="w-6 h-6 text-slate-800" />
                Sales Cycle & Key Metrics
              </h2>
              
              <div className="grid md:grid-cols-4 gap-4 mb-6">
                {[
                  { label: "Avg Sales Cycle", value: "90 days", note: "Existing relationships" },
                  { label: "Avg Deal Size", value: "$125K", note: "Platform + services" },
                  { label: "Win Rate", value: "35%", note: "Qualified pipeline" },
                  { label: "Expansion Rate", value: "140%", note: "Net revenue retention" }
                ].map((metric, idx) => (
                  <div key={idx} className="bg-white/50 rounded-xl p-5 text-center">
                    <p className="text-2xl font-bold text-slate-800">{metric.value}</p>
                    <p className="text-slate-900 font-medium">{metric.label}</p>
                    <p className="text-slate-500 text-sm">{metric.note}</p>
                  </div>
                ))}
              </div>
              
              <div className="bg-white/50 rounded-xl p-5">
                <h3 className="font-bold text-slate-900 mb-3">Key Buying Triggers</h3>
                <div className="grid md:grid-cols-2 gap-4">
                  {[
                    "Water system failure or compliance audit finding",
                    "Joint Commission inspection coming up",
                    "New facility director seeking modernization",
                    "Budget allocation for digital transformation",
                    "Frustration with Big Box distributor delays",
                    "AAMI ST108 compliance deadline"
                  ].map((trigger, idx) => (
                    <div key={idx} className="flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4 text-slate-800 flex-shrink-0" />
                      <span className="text-slate-700 text-sm">{trigger}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Competitive Positioning */}
            <div className="bg-white/50 border border-slate-200 rounded-2xl p-8">
              <h2 className="text-2xl font-bold mb-6 flex items-center gap-3">
                <Award className="w-6 h-6 text-emerald-600" />
                Competitive Positioning & Win Themes
              </h2>
              
              <div className="grid md:grid-cols-3 gap-4">
                {[
                  { 
                    competitor: "vs. Big Box Distributors", 
                    theme: "Local + Compliant + Fast",
                    pitch: "We deploy pre-vetted local contractors in hours, not days. Same compliance, better service, lower cost."
                  },
                  { 
                    competitor: "vs. Legacy ERPs", 
                    theme: "Mobile-First, Not Desktop-Trapped",
                    pitch: "Your staff texts from the floor. Our platform meets them there. No desktop required."
                  },
                  { 
                    competitor: "vs. In-House Teams", 
                    theme: "Extend, Don&apos;t Replace",
                    pitch: "AskIsaac amplifies your team with on-demand expertise when you need surge capacity."
                  }
                ].map((item, idx) => (
                  <div key={idx} className="bg-slate-50 rounded-xl p-5">
                    <p className="text-sm text-slate-600 mb-2">{item.competitor}</p>
                    <h3 className="font-bold text-emerald-600 mb-2">{item.theme}</h3>
                    <p className="text-slate-700 text-sm">{item.pitch}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* UNIT ECONOMICS SECTION */}
        {activeSection === "financials" && (
          <div className="space-y-10 animate-fade-in">
            <div className="text-center mb-12">
              <h1 className="text-4xl font-bold mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Unit Economics
              </h1>
              <p className="text-xl text-slate-600">How we make money on every customer</p>
            </div>

            {/* Key Unit Economics */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-slate-50 border border-slate-200 rounded-2xl p-6 text-center">
                <p className="text-sm text-emerald-600 font-semibold mb-2">Customer Lifetime Value</p>
                <p className="text-3xl font-bold text-slate-900">$750K</p>
                <p className="text-slate-600 text-sm mt-2">Over 6-year avg relationship</p>
              </div>
              <div className="bg-white border border-slate-200 rounded-2xl p-6 text-center">
                <p className="text-sm text-blue-900 font-semibold mb-2">Customer Acquisition Cost</p>
                <p className="text-3xl font-bold text-slate-900">$22K</p>
                <p className="text-slate-600 text-sm mt-2">Blended across channels</p>
              </div>
              <div className="bg-white border border-slate-200 rounded-2xl p-6 text-center">
                <p className="text-sm text-blue-900 font-semibold mb-2">LTV:CAC Ratio</p>
                <p className="text-3xl font-bold text-emerald-600">34:1</p>
                <p className="text-slate-600 text-sm mt-2">Exceptional efficiency</p>
              </div>
              <div className="bg-slate-50 border border-slate-200 rounded-2xl p-6 text-center">
                <p className="text-sm text-slate-800 font-semibold mb-2">Payback Period</p>
                <p className="text-3xl font-bold text-slate-900">4 mo</p>
                <p className="text-slate-600 text-sm mt-2">Time to recoup CAC</p>
              </div>
            </div>

            {/* Revenue Model Breakdown */}
            <div className="bg-white/50 border border-slate-200 rounded-2xl p-8">
              <h2 className="text-2xl font-bold mb-6 flex items-center gap-3">
                <DollarSign className="w-6 h-6 text-emerald-600" />
                Revenue Model & Margin Structure
              </h2>
              
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-slate-200">
                      <th className="text-left py-4 px-4 text-slate-600 font-medium">Revenue Stream</th>
                      <th className="text-center py-4 px-4 text-slate-600 font-medium">Gross Margin</th>
                      <th className="text-center py-4 px-4 text-slate-600 font-medium">% of Revenue (Y3)</th>
                      <th className="text-left py-4 px-4 text-slate-600 font-medium">Description</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      { stream: "Platform Subscription (SaaS)", margin: "85-90%", pct: "40%", description: "Monthly per-location fees for AskIsaac platform access" },
                      { stream: "Managed Services", margin: "44%", pct: "25%", description: "Sterile Mate-executed projects (water, maintenance)" },
                      { stream: "Supply Brokerage", margin: "35%", pct: "15%", description: "Medical supplies and equipment procurement" },
                      { stream: "Transaction Fees", margin: "100%", pct: "10%", description: "8% surcharge on QuickBid enterprise transactions" },
                      { stream: "Professional Services", margin: "60%", pct: "10%", description: "Implementation, training, custom integrations" }
                    ].map((item, idx) => (
                      <tr key={idx} className="border-b border-slate-800 hover:bg-white/30">
                        <td className="py-4 px-4 font-semibold text-slate-900">{item.stream}</td>
                        <td className="py-4 px-4 text-center">
                          <span className={`px-3 py-1 rounded-full text-sm font-semibold ${
                            item.margin.includes('85') || item.margin === '100%' ? 'bg-slate-100 text-emerald-600' :
                            item.margin.includes('44') || item.margin.includes('60') ? 'bg-blue-100 text-blue-900' :
                            'bg-slate-100 text-slate-800'
                          }`}>
                            {item.margin}
                          </span>
                        </td>
                        <td className="py-4 px-4 text-center text-slate-700">{item.pct}</td>
                        <td className="py-4 px-4 text-slate-600 text-sm">{item.description}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              
              <div className="mt-6 bg-emerald-500/10 border border-slate-200 rounded-xl p-4">
                <p className="text-center text-emerald-700 font-medium">
                  <TrendingUp className="w-4 h-4 inline mr-2" />
                  Blended Gross Margin: <span className="text-slate-900 font-bold">65-70%</span> — improving as SaaS revenue grows
                </p>
              </div>
            </div>

            {/* LTV Calculation Detail */}
            <div className="bg-white border border-slate-200 rounded-2xl p-8">
              <h2 className="text-2xl font-bold mb-6 flex items-center gap-3">
                <Calculator className="w-6 h-6 text-blue-900" />
                Customer Lifetime Value Breakdown
              </h2>
              
              <div className="grid md:grid-cols-2 gap-8">
                <div>
                  <h3 className="font-bold text-lg text-blue-900 mb-4">LTV Calculation</h3>
                  <div className="space-y-3">
                    {[
                      { label: "Average Contract Value (ACV)", value: "$125,000", note: "Platform + services" },
                      { label: "Average Customer Lifespan", value: "6 years", note: "Healthcare stickiness" },
                      { label: "Annual Expansion Rate", value: "+15%", note: "Additional facilities/services" },
                      { label: "Annual Churn Rate", value: "5%", note: "Very low in healthcare" },
                      { label: "Gross Margin", value: "65%", note: "Blended across streams" }
                    ].map((item, idx) => (
                      <div key={idx} className="flex items-center justify-between py-2 border-b border-slate-200 last:border-0">
                        <div>
                          <span className="text-slate-900">{item.label}</span>
                          <span className="text-slate-500 text-sm ml-2">({item.note})</span>
                        </div>
                        <span className="text-blue-900 font-semibold">{item.value}</span>
                      </div>
                    ))}
                  </div>
                  <div className="mt-4 bg-white/50 rounded-xl p-4">
                    <p className="text-slate-600 text-sm mb-2">LTV Formula:</p>
                    <p className="text-slate-900 font-mono text-sm">LTV = (ACV × Lifespan × (1 + Expansion)^n × Margin) / (1 + Churn)</p>
                    <p className="text-2xl font-bold text-emerald-600 mt-3">= $750,000</p>
                  </div>
                </div>
                
                <div>
                  <h3 className="font-bold text-lg text-emerald-600 mb-4">CAC Breakdown</h3>
                  <div className="space-y-3">
                    {[
                      { label: "Sales Compensation", value: "$8,000", pct: "36%" },
                      { label: "Marketing Spend", value: "$5,000", pct: "23%" },
                      { label: "Trade Shows/Events", value: "$4,000", pct: "18%" },
                      { label: "Demo/Pilot Costs", value: "$3,000", pct: "14%" },
                      { label: "Sales Tools/Overhead", value: "$2,000", pct: "9%" }
                    ].map((item, idx) => (
                      <div key={idx} className="flex items-center justify-between py-2 border-b border-slate-200 last:border-0">
                        <span className="text-slate-900">{item.label}</span>
                        <div className="text-right">
                          <span className="text-emerald-600 font-semibold">{item.value}</span>
                          <span className="text-slate-500 text-sm ml-2">{item.pct}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="mt-4 bg-white/50 rounded-xl p-4">
                    <p className="text-slate-600 text-sm mb-2">Total CAC:</p>
                    <p className="text-2xl font-bold text-slate-900">$22,000</p>
                    <p className="text-emerald-600 text-sm mt-1">LTV:CAC = 34:1 (target &gt;3:1)</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Cohort Economics */}
            <div className="bg-white/50 border border-slate-200 rounded-2xl p-8">
              <h2 className="text-2xl font-bold mb-6 flex items-center gap-3">
                <BarChart3 className="w-6 h-6 text-blue-900" />
                Customer Economics by Tier
              </h2>
              
              <div className="grid md:grid-cols-3 gap-6">
                {[
                  { 
                    tier: "Tier 1: Managed Compliance",
                    acv: "$200K - $500K",
                    margin: "44% (Services) + 35% (Supplies)",
                    ltv: "$1.2M",
                    cac: "$35K",
                    ratio: "34:1",
                    example: "Major health system water + maintenance"
                  },
                  { 
                    tier: "Tier 2: Platform + Services",
                    acv: "$100K - $200K",
                    margin: "85% (Platform) + 44% (Services)",
                    ltv: "$750K",
                    cac: "$22K",
                    ratio: "34:1",
                    example: "Regional hospital network"
                  },
                  { 
                    tier: "Tier 3: White-Label SaaS",
                    acv: "$50K - $100K",
                    margin: "90% (Pure SaaS)",
                    ltv: "$400K",
                    cac: "$12K",
                    ratio: "33:1",
                    example: "Self-service hospital IT team"
                  }
                ].map((tier, idx) => (
                  <div key={idx} className={`rounded-xl p-6 border ${
                    idx === 0 ? 'bg-emerald-500/10 border-slate-200' :
                    idx === 1 ? 'bg-blue-500/10 border-slate-200' :
                    'bg-violet-500/10 border-slate-200'
                  }`}>
                    <h3 className={`font-bold text-lg mb-4 ${
                      idx === 0 ? 'text-emerald-600' : idx === 1 ? 'text-blue-900' : 'text-blue-900'
                    }`}>{tier.tier}</h3>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-slate-600">ACV Range:</span>
                        <span className="text-slate-900 font-semibold">{tier.acv}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-600">Gross Margin:</span>
                        <span className="text-slate-900">{tier.margin}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-600">LTV:</span>
                        <span className="text-emerald-600 font-semibold">{tier.ltv}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-600">CAC:</span>
                        <span className="text-slate-900">{tier.cac}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-600">LTV:CAC:</span>
                        <span className="text-emerald-600 font-bold">{tier.ratio}</span>
                      </div>
                    </div>
                    <div className="mt-4 pt-4 border-t border-slate-200">
                      <p className="text-slate-500 text-xs">Example: {tier.example}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Path to Profitability */}
            <div className="bg-white border border-slate-200 rounded-2xl p-8">
              <h2 className="text-2xl font-bold mb-6 flex items-center gap-3">
                <TrendingUp className="w-6 h-6 text-emerald-600" />
                Path to Profitability
              </h2>
              
              <div className="grid md:grid-cols-3 gap-6 mb-6">
                {[
                  { year: "Year 1", revenue: "$745K", profit: "+$263K", margin: "35%", status: "Profitable" },
                  { year: "Year 2", revenue: "$2.86M", profit: "+$1.21M", margin: "42%", status: "Profitable" },
                  { year: "Year 3", revenue: "$8.01M", profit: "+$4.08M", margin: "51%", status: "Profitable" }
                ].map((year, idx) => (
                  <div key={idx} className="bg-white/50 rounded-xl p-6 text-center">
                    <p className="text-slate-600 text-sm mb-2">{year.year}</p>
                    <p className="text-2xl font-bold text-slate-900">{year.revenue}</p>
                    <p className="text-emerald-600 font-bold text-lg">{year.profit}</p>
                    <div className="mt-3">
                      <span className="px-3 py-1 bg-slate-100 text-emerald-600 rounded-full text-sm font-semibold">
                        {year.margin} margin
                      </span>
                    </div>
                  </div>
                ))}
              </div>
              
              <div className="bg-white/50 rounded-xl p-5">
                <p className="text-center text-emerald-700">
                  <CheckCircle2 className="w-5 h-5 inline mr-2" />
                  <span className="font-bold">Bootstrap-First Model:</span> Profitable from Year 1 — external funding accelerates growth but isn&apos;t required for survival
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Roadmap Section */}
        {activeSection === "financials" && (
          <div className="space-y-10 animate-fade-in">
            <div className="text-center mb-12">
              <h1 className="text-4xl font-bold mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Growth Roadmap
              </h1>
              <p className="text-xl text-slate-600">From startup to national healthcare solutions leader</p>
            </div>

            {/* Timeline */}
            <div className="relative">
              {/* Timeline Line */}
              <div className="absolute left-8 top-0 bottom-0 w-0.5 bg-gradient-to-b from-blue-500 via-emerald-500 to-orange-500 hidden md:block" />

              <div className="space-y-8">
                {roadmapData.map((milestone, idx) => (
                  <div 
                    key={idx} 
                    className={`relative flex gap-6 ${milestone.future ? 'opacity-70' : ''}`}
                  >
                    {/* Timeline Dot */}
                    <div className={`relative z-10 w-16 h-16 rounded-2xl bg-gradient-to-br ${milestone.color} flex items-center justify-center flex-shrink-0 shadow-lg ${milestone.current ? 'ring-4 ring-yellow-400/50 animate-pulse' : ''}`}>
                      <milestone.icon className="w-8 h-8 text-slate-900" />
                    </div>

                    {/* Content */}
                    <div className={`flex-1 bg-white/50 border border-slate-200 rounded-2xl p-6 ${milestone.current ? 'border-yellow-500/50 bg-white' : ''}`}>
                      <div className="flex flex-wrap items-center gap-4 mb-3">
                        <span className={`px-3 py-1 rounded-full text-sm font-bold ${milestone.current ? 'bg-yellow-500/20 text-yellow-400' : milestone.future ? 'bg-slate-700 text-slate-600' : 'bg-blue-100 text-blue-900'}`}>
                          {milestone.year}
                        </span>
                        <h3 className="text-xl font-bold">{milestone.title}</h3>
                        {milestone.current && (
                          <span className="px-2 py-1 bg-yellow-500/20 text-yellow-400 text-xs rounded-full">Current Year</span>
                        )}
                        {milestone.forecast && (
                          <span className="px-2 py-1 bg-violet-500/20 text-blue-900 text-xs rounded-full">Forecast</span>
                        )}
                      </div>
                      <p className="text-slate-600 mb-4">{milestone.description}</p>
                      <div className="flex items-center gap-2">
                        <DollarSign className="w-5 h-5 text-emerald-600" />
                        <span className="text-2xl font-bold text-emerald-600">{milestone.revenue}</span>
                        <span className="text-slate-500">{milestone.forecast ? 'Projected Revenue' : 'Revenue'}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Financials Section */}
        {activeSection === "financials" && (
          <div className="space-y-10 animate-fade-in">
            <div className="text-center mb-12">
              <h1 className="text-4xl font-bold mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Financial Projections
              </h1>
              <p className="text-xl text-slate-600">SBA Expansion Model &mdash; B2B + Retail + DME Revenue Streams</p>
              
              {/* Link to Detailed Projections */}
              <button 
                onClick={() => navigate("/investor/financials")}
                className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-emerald-600/20 border border-slate-200 text-emerald-600 hover:bg-emerald-600/30 rounded-lg text-sm font-medium transition-all"
              >
                <BarChart3 className="w-4 h-4" />
                View Detailed 3-Year Financial Model
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>

            {/* Projection Table */}
            <div className="bg-white/50 border border-slate-200 rounded-2xl overflow-hidden">
              <div className="grid grid-cols-4 bg-white p-4 font-semibold text-slate-700">
                <div>Year</div>
                <div className="text-right">Revenue</div>
                <div className="text-right">Expenses</div>
                <div className="text-right">Net Income</div>
              </div>
              {financialProjections.map((row, idx) => (
                <div key={idx} className="grid grid-cols-4 p-4 border-t border-slate-200 hover:bg-slate-50 transition-colors">
                  <div className="font-semibold">{row.year}</div>
                  <div className="text-right text-blue-900 font-bold">{row.revenue}</div>
                  <div className="text-right text-slate-600">{row.expenses}</div>
                  <div className="text-right text-emerald-600 font-bold">{row.netIncome}</div>
                </div>
              ))}
            </div>

            {/* Year 1 Personnel Budget */}
            <div className="bg-white border border-slate-200 rounded-2xl p-8">
              <h2 className="text-2xl font-bold mb-4 flex items-center gap-3">
                <Users className="w-6 h-6 text-blue-900" />
                Year 1 Personnel Budget
              </h2>
              <p className="text-slate-700 mb-6">
                8 full-time positions plus 1 outsourced developer supporting B2B operations, retail, and technology development. Sales team earns 15% commission on products sold in addition to base salary.
              </p>

              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="personnel-budget-table">
                  <thead>
                    <tr className="border-b border-slate-200">
                      <th className="text-left py-3 px-3 text-slate-600">Role</th>
                      <th className="text-right py-3 px-3 text-slate-600">Annual Cost</th>
                      <th className="text-left py-3 px-3 text-slate-600">Department</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      { role: "CEO", name: "George Broughton III", salary: "$125,000", dept: "Executive" },
                      { role: "Full Stack Developer (Outsourced)", name: "$5,000/mo contractor", salary: "$60,000", dept: "Technology", contractor: true },
                      { role: "Office Manager / Executive Assistant", name: "", salary: "$55,000", dept: "Administration" },
                      { role: "Bookkeeper", name: "", salary: "$50,000", dept: "Finance" },
                      { role: "Warehouse Logistics Coordinator", name: "", salary: "$46,000", dept: "Operations" },
                      { role: "Sales Assistant", name: "", salary: "$40,000", dept: "Sales" },
                      { role: "Sales Rep — Water Purification", name: "", salary: "$42,500", dept: "Sales", commission: true },
                      { role: "Sales Rep — Janitorial Sales", name: "", salary: "$42,500", dept: "Sales", commission: true },
                      { role: "Sales Rep — Medical Device Sales", name: "", salary: "$42,500", dept: "Sales", commission: true },
                    ].map((row, idx) => (
                      <tr key={idx} className="border-b border-slate-200 hover:bg-slate-700/20 transition-colors">
                        <td className="py-3 px-3">
                          <span className="text-slate-900 font-medium">{row.role}</span>
                          {row.name && <span className="text-slate-500 text-xs block">{row.name}</span>}
                          {row.commission && <span className="text-slate-800 text-xs">+ 15% commission</span>}
                          {row.contractor && <span className="text-blue-900 text-xs block">No benefits — outsourced</span>}
                        </td>
                        <td className="text-right py-3 px-3 text-blue-900 font-semibold">{row.salary}</td>
                        <td className="py-3 px-3 text-slate-600">{row.dept}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="border-t-2 border-slate-600">
                      <td className="py-3 px-3 font-bold text-slate-900">Base Salaries (8 FTE)</td>
                      <td className="text-right py-3 px-3 text-slate-900 font-bold">$443,500</td>
                      <td className="py-3 px-3"></td>
                    </tr>
                    <tr className="border-t border-slate-200">
                      <td className="py-3 px-3 text-slate-700">Benefits &amp; Insurance (15% of FTE)</td>
                      <td className="text-right py-3 px-3 text-slate-700">$66,525</td>
                      <td className="py-3 px-3"></td>
                    </tr>
                    <tr className="border-t border-slate-200">
                      <td className="py-3 px-3 text-slate-700">Outsourced Developer</td>
                      <td className="text-right py-3 px-3 text-slate-700">$60,000</td>
                      <td className="py-3 px-3"></td>
                    </tr>
                    <tr className="border-t-2 border-blue-500/50 bg-blue-500/10">
                      <td className="py-4 px-3 font-bold text-lg text-blue-900">Total Personnel Budget</td>
                      <td className="text-right py-4 px-3 text-blue-900 font-bold text-lg">$570,025</td>
                      <td className="py-4 px-3"></td>
                    </tr>
                  </tfoot>
                </table>
              </div>

              <div className="mt-6 grid md:grid-cols-3 gap-4">
                <div className="bg-white/50 rounded-xl p-4 text-center">
                  <p className="text-slate-600 text-sm">Headcount</p>
                  <p className="text-2xl font-bold text-blue-900">8 FTE + 1</p>
                  <p className="text-xs text-slate-500">8 full-time + 1 contractor</p>
                </div>
                <div className="bg-white/50 rounded-xl p-4 text-center">
                  <p className="text-slate-600 text-sm">Sales Commission</p>
                  <p className="text-2xl font-bold text-slate-800">15%</p>
                  <p className="text-xs text-slate-500">On products sold (3 reps)</p>
                </div>
                <div className="bg-white/50 rounded-xl p-4 text-center">
                  <p className="text-slate-600 text-sm">Working Capital Allocation</p>
                  <p className="text-2xl font-bold text-emerald-600">$950K</p>
                  <p className="text-xs text-slate-500">Covers personnel + marketing + ops</p>
                </div>
              </div>
            </div>

            {/* Year 1 Month-by-Month Cash Flow */}
            <div className="bg-white border border-slate-200 rounded-2xl p-8">
              <h2 className="text-2xl font-bold text-slate-900 mb-4 flex items-center gap-3">
                <Calculator className="w-6 h-6 text-blue-900" />
                Year 1: Monthly Cash Flow (SBA Expansion Model)
              </h2>
              <p className="text-slate-700 mb-6">
                Revenue ramp from B2B pipeline ($791K within 90 days) plus retail launch and operational scaling. Working capital covers payroll, marketing ($10K/mo), debt service ($27.8K/mo), and operations.
              </p>
              
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-200">
                      <th className="text-left py-3 px-2 text-slate-600">Month</th>
                      <th className="text-right py-3 px-2 text-slate-600">Revenue</th>
                      <th className="text-right py-3 px-2 text-slate-600">Expenses</th>
                      <th className="text-right py-3 px-2 text-slate-600">Net</th>
                      <th className="text-right py-3 px-2 text-slate-600">Cash Balance</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      { month: "Jan", revenue: 40000, expenses: 155000, note: "Renovation begins, B2B pipeline active" },
                      { month: "Feb", revenue: 65000, expenses: 155000, note: "Buildout continues, initial B2B installs" },
                      { month: "Mar", revenue: 180000, expenses: 165000, note: "First B2B contract revenue ($791K pipeline)" },
                      { month: "Apr", revenue: 220000, expenses: 170000, note: "Retail soft open (The Supply Doctor)" },
                      { month: "May", revenue: 230000, expenses: 175000, note: "Retail ramp + B2B recurring" },
                      { month: "Jun", revenue: 240000, expenses: 178000, note: "Full retail + B2B operations" },
                      { month: "Jul", revenue: 245000, expenses: 180000, note: "Marketing driving B2B leads" },
                      { month: "Aug", revenue: 250000, expenses: 180000, note: "Showroom generating conversions" },
                      { month: "Sep", revenue: 260000, expenses: 182000, note: "Trade show season + new contracts" },
                      { month: "Oct", revenue: 270000, expenses: 185000, note: "DME Phase II planning begins" },
                      { month: "Nov", revenue: 280000, expenses: 185000, note: "Holiday retail boost" },
                      { month: "Dec", revenue: 290000, expenses: 188000, note: "Year-end B2B contract signings" }
                    ].reduce((acc, row, idx) => {
                      const prevBalance = idx === 0 ? 950000 : acc[idx - 1].endBalance;
                      const net = row.revenue - row.expenses;
                      const endBalance = prevBalance + net;
                      acc.push({ ...row, net, endBalance });
                      return acc;
                    }, []).map((row, idx) => (
                      <tr key={idx} className={`border-b border-slate-200 ${row.endBalance < 300000 ? 'bg-slate-100' : ''}`}>
                        <td className="py-3 px-2">
                          <span className="font-semibold text-slate-900">{row.month}</span>
                          <span className="text-slate-500 text-xs block">{row.note}</span>
                        </td>
                        <td className="text-right py-3 px-2 text-blue-900">${row.revenue.toLocaleString()}</td>
                        <td className="text-right py-3 px-2 text-red-600">${row.expenses.toLocaleString()}</td>
                        <td className={`text-right py-3 px-2 font-semibold ${row.net >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                          {row.net >= 0 ? '+' : ''}${row.net.toLocaleString()}
                        </td>
                        <td className={`text-right py-3 px-2 font-bold ${row.endBalance < 400000 ? 'text-slate-800' : 'text-emerald-600'}`}>
                          ${row.endBalance.toLocaleString()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              
              <div className="mt-6 grid md:grid-cols-3 gap-4">
                <div className="bg-white/50 rounded-xl p-4 text-center">
                  <p className="text-slate-600 text-sm">Working Capital</p>
                  <p className="text-2xl font-bold text-blue-900">$950,000</p>
                  <p className="text-xs text-slate-500">From SBA loan proceeds</p>
                </div>
                <div className="bg-white/50 rounded-xl p-4 text-center">
                  <p className="text-slate-600 text-sm">Year 1 Revenue</p>
                  <p className="text-2xl font-bold text-emerald-600">$2.57M</p>
                  <p className="text-xs text-slate-500">B2B + Retail combined</p>
                </div>
                <div className="bg-white/50 rounded-xl p-4 text-center">
                  <p className="text-slate-600 text-sm">Monthly Debt Service</p>
                  <p className="text-2xl font-bold text-slate-800">{scenario.monthlyDebtTotal.replace("~", "")}</p>
                  <p className="text-xs text-slate-500">SBA + existing debt</p>
                </div>
              </div>
            </div>

            {/* Revenue Breakdown */}
            <div className="bg-white border border-slate-200 rounded-2xl p-8">
              <h2 className="text-2xl font-bold mb-6 flex items-center gap-3">
                <BarChart3 className="w-6 h-6 text-blue-900" />
                Year 2 (2028) Projected Revenue Mix &mdash; $7.59M
              </h2>
              <div className="space-y-4">
                {[
                  { label: "Backorder Sourcing (AskIsaac-automated \u00b7 flagship)", value: 27.7, amount: "$2.10M", color: "from-blue-900 to-blue-900" },
                  { label: "Private-Label Exam Gloves (Intco direct-import)", value: 20.4, amount: "$1.54M", color: "from-blue-900 to-blue-900" },
                  { label: "Water Purification & Healthcare Capital Projects", value: 18.5, amount: "$1.40M", color: "from-blue-900 to-blue-900" },
                  { label: "Medical Device Sales", value: 14.5, amount: "$1.10M", color: "from-blue-900 to-blue-900" },
                  { label: "Healthcare Construction (GC / Renovation)", value: 11.9, amount: "$0.90M", color: "from-blue-900 to-blue-900" },
                  { label: "Specialized BioBurden Cleaning", value: 4.6, amount: "$0.35M", color: "from-blue-900 to-blue-900" },
                  { label: "Private-Label Microfiber (Texas MF)", value: 2.5, amount: "$0.19M", color: "from-blue-900 to-blue-900" }
                ].map((item, idx) => (
                  <div key={idx}>
                    <div className="flex justify-between mb-2">
                      <span className="text-slate-700">{item.label}</span>
                      <span className="text-slate-600">{item.amount} ({item.value}%)</span>
                    </div>
                    <div className="h-3 bg-slate-100 rounded-full overflow-hidden">
                      <div 
                        className={`h-full bg-gradient-to-r ${item.color} rounded-full transition-all duration-1000`}
                        style={{ width: `${item.value}%` }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Gross Profit Summary */}
            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-white/50 border border-slate-200 rounded-2xl p-6">
                <h3 className="font-semibold mb-4 text-lg">Year 3 Financial Target (2029)</h3>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-slate-600">Total Revenue</span>
                    <span className="font-bold text-blue-900">$11,641,336</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-600">Backorder Sourcing (Flagship)</span>
                    <span className="font-bold text-slate-800">$3,200,000</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-600">Private-Label Gloves (Intco)</span>
                    <span className="font-bold text-slate-700">$2,786,736</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-600">Water + Medical Devices + Construction</span>
                    <span className="font-bold text-slate-700">$4,800,000</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-600">BioBurden + Microfiber</span>
                    <span className="font-bold text-slate-700">$854,600</span>
                  </div>
                  <div className="h-px bg-slate-700" />
                  <div className="flex justify-between">
                    <span className="text-slate-700 font-semibold">Net Operating Income</span>
                    <span className="font-bold text-emerald-600">$4,254,596</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-600">DSCR (Year 2+)</span>
                    <span className="font-bold text-emerald-600">{scenario.dscrRangeY2}</span>
                  </div>
                </div>
              </div>

              <div className="bg-white/50 border border-slate-200 rounded-2xl p-6">
                <h3 className="font-semibold mb-4 text-lg">Proof of Performance (Completed Years)</h3>
                <div className="space-y-3 text-sm">
                  <div className="flex justify-between">
                    <span className="text-slate-600">2021</span>
                    <span className="text-slate-700">$800K &mdash; Initial contracts</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-600">2022</span>
                    <span className="text-slate-700">$700K &mdash; Water purification launch</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-600">2023</span>
                    <span className="text-slate-700">$600K &mdash; Federal contracts</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-600">2024</span>
                    <span className="text-slate-700">$1.1M &mdash; Major installations</span>
                  </div>
                  <div className="flex justify-between font-semibold">
                    <span className="text-emerald-600">2025</span>
                    <span className="text-emerald-600">$1.5M+ &mdash; Expansion Year</span>
                  </div>
                  <div className="h-px bg-slate-700" />
                  <div className="flex justify-between font-semibold">
                    <span className="text-blue-900">Pipeline (90 days)</span>
                    <span className="text-blue-900">$791K &mdash; Harris Health</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Customers Section */}
        {activeSection === "management" && (
          <div className="space-y-10 animate-fade-in">
            <div className="text-center mb-12">
              <h1 className="text-4xl font-bold mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Customer Success
              </h1>
              <p className="text-xl text-slate-600">Proven track record with leading healthcare institutions</p>
            </div>

            {/* Case Studies */}
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {caseStudies.map((study, idx) => (
                <div 
                  key={idx} 
                  className="bg-white/50 border border-slate-200 rounded-2xl p-6 hover:border-slate-600 transition-all"
                >
                  <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${study.color} flex items-center justify-center mb-4`}>
                    <Hospital className="w-6 h-6 text-slate-900" />
                  </div>
                  <span className="px-2 py-1 bg-blue-500/10 text-blue-900 text-xs rounded-full font-medium">
                    {study.highlight}
                  </span>
                  <h3 className="text-xl font-bold mt-3 mb-2">{study.name}</h3>
                  <p className="text-slate-600 text-sm mb-4">{study.description}</p>
                  <div className="flex items-center gap-2 text-emerald-600 text-sm font-medium">
                    <CheckCircle2 className="w-4 h-4" />
                    {study.contracts}
                  </div>
                </div>
              ))}
            </div>

            {/* Testimonial-style highlight */}
            <div className="bg-white border border-blue-500/20 rounded-2xl p-8 text-center">
              <Star className="w-12 h-12 text-yellow-400 mx-auto mb-4" />
              <p className="text-2xl font-semibold text-slate-900 mb-4">
                &quot;Enterprise validated with one of the largest public health systems in the US&quot;
              </p>
              <p className="text-slate-600">
                Live beta deployment + 3 water purification contracts and counting
              </p>
            </div>

            {/* Key Wins */}
            <div className="bg-white/50 border border-slate-200 rounded-2xl p-8">
              <h2 className="text-2xl font-bold mb-6 flex items-center gap-3">
                <Award className="w-6 h-6 text-slate-800" />
                Key Contract Wins
              </h2>
              <div className="grid md:grid-cols-2 gap-4">
                {[
                  "3 Water Purification Contracts at Major Public Health System",
                  "State of Texas Reverse Osmosis System (Awarded Bid)",
                  "National Integrated Healthcare System Partnership",
                  "Regional Federal Medical Center Partnership",
                  "Leading Pediatric Institution Partnership",
                  "Federal Healthcare Nationwide Expansion Pipeline"
                ].map((win, idx) => (
                  <div key={idx} className="flex items-center gap-3 p-3 bg-white/50 rounded-xl">
                    <CheckCircle2 className="w-5 h-5 text-emerald-600 flex-shrink-0" />
                    <span className="text-slate-700">{win}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Investment Section */}
        {activeSection === "loan" && (
          <div className="space-y-10 animate-fade-in">
            <div className="text-center mb-12">
              <h1 className="text-4xl font-bold mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Investment Opportunity
              </h1>
              <p className="text-xl text-slate-600">Sterile Mate LLC — Powered by AskIsaac Technology</p>
            </div>

            {/* The Ask */}
            <div className="bg-white border border-slate-200 rounded-3xl p-10 text-center">
              <Landmark className="w-16 h-16 text-blue-900 mx-auto mb-6" />
              <h2 className="text-3xl font-bold mb-2" data-testid="loan-request-amount">SBA {scenario.loanType} Loan Request: {scenario.askAmount}</h2>
              <p className="text-4xl font-bold text-blue-900 mb-4">SBA {scenario.loanType} Loan</p>
              <p className="text-slate-600 max-w-2xl mx-auto mb-4">
                Structure: <span className="text-slate-900 font-semibold">SBA {scenario.loanType} — {scenario.id === "ronan" ? "Real Estate Acquisition + Renovation (fixed-rate, 25-yr)" : "Real Estate + Working Capital"}</span>
              </p>
              <p className="text-slate-700 max-w-3xl mx-auto">
                This financing will establish Sterile Mate&apos;s first <span className="text-blue-900 font-semibold">fully integrated B2B Healthcare Solutions Headquarters</span> — combining showroom, training, logistics, {scenario.id === "northsam" ? "AskIsaac engineering, and multi-tenant rental income" : scenario.id === "ronan" ? "expanded backorder sourcing, water purification systems, healthcare construction, a 3,500 SF short-stock warehouse, and a Texas-metro expansion sales office" : "and retail"} under one roof at {scenario.address}.
              </p>
            </div>

            {/* SBA Debt Structure */}
            <div className="bg-white border border-slate-200 rounded-2xl p-8">
              <h2 className="text-2xl font-bold mb-6 flex items-center gap-3">
                <Calculator className="w-6 h-6 text-emerald-600" />
                SBA Debt Structure &amp; Coverage
              </h2>
              <div className="grid md:grid-cols-2 gap-8">
                <div>
                  <p className="text-slate-600 mb-4">Monthly debt service analysis based on SBA {scenario.loanType} terms:</p>
                  <div className="space-y-4">
                    <div className="bg-white rounded-xl p-4 border border-slate-200">
                      <div className="flex justify-between items-center">
                        <span className="text-slate-600">SBA Loan Amount</span>
                        <span className="text-2xl font-bold text-slate-900">{scenario.sbaLoanLabel}</span>
                      </div>
                      <div className="text-xs text-slate-500 mt-1">90% of total project cost, 25-year real estate term</div>
                    </div>
                    <div className="bg-white rounded-xl p-4 border border-slate-200">
                      <div className="flex justify-between items-center">
                        <span className="text-slate-600">Monthly Payment (SBA)</span>
                        <span className="text-2xl font-bold text-blue-900">{scenario.monthlyDebtSBA}</span>
                      </div>
                      <div className="text-xs text-blue-900/70 mt-1">Includes principal + interest</div>
                    </div>
                    <div className="bg-white rounded-xl p-4 border border-slate-200">
                      <div className="flex justify-between items-center">
                        <span className="text-slate-600">Total Monthly Debt Service</span>
                        <span className="text-2xl font-bold text-slate-800">{scenario.monthlyDebtTotal}</span>
                      </div>
                      <div className="text-xs text-slate-800/70 mt-1">SBA + existing debt ($2,300/mo)</div>
                    </div>
                  </div>
                </div>
                <div className="flex flex-col justify-center">
                  <div className="bg-slate-50 rounded-2xl p-8 text-center border border-slate-200">
                    <p className="text-slate-600 mb-2">Year 2 DSCR (Debt Service Coverage Ratio)</p>
                    <p className="text-5xl font-bold text-emerald-600 mb-2">{scenario.dscrRangeY2}</p>
                    <p className="text-lg font-semibold text-slate-900 mb-4">Strong Coverage</p>
                    <div className="pt-4 border-t border-slate-200">
                      <p className="text-xs text-slate-500">SBA typically requires 1.15&ndash;1.25x minimum</p>
                      <p className="text-sm text-emerald-600 font-semibold mt-2">Exceeds SBA requirements by Year 2</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Capital Deployment Strategy */}
            <div className="bg-white/50 border border-slate-200 rounded-2xl p-8">
              <h2 className="text-2xl font-bold mb-6 flex items-center gap-3">
                <Target className="w-6 h-6 text-blue-900" />
                1. Capital Deployment Strategy
              </h2>
              
              {/* Infrastructure & Scale */}
              <div className="mb-6 bg-white border border-blue-500/20 rounded-xl p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-bold text-lg flex items-center gap-2 text-blue-900">
                    <Building2 className="w-5 h-5" />
                    A. Infrastructure & Scale
                  </h3>
                  <span className="px-3 py-1 bg-blue-100 text-blue-900 rounded-full text-sm font-semibold">40% — $280K</span>
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="bg-white/50 rounded-lg p-4">
                    <h4 className="font-semibold mb-2">Marketplace Architecture</h4>
                    <p className="text-slate-600 text-sm">Developing the "Double-Sided" platform where healthcare systems can "discover" local, pre-vetted contractors and suppliers that are currently invisible to them.</p>
                  </div>
                  <div className="bg-white/50 rounded-lg p-4">
                    <h4 className="font-semibold mb-2">API & QuickBid Development</h4>
                    <p className="text-slate-600 text-sm">Building the engine that allows hospitals to post emergency requirements and receive 3+ instant, compliant bids from local providers in their "backyard."</p>
                  </div>
                </div>
              </div>

              {/* Network Expansion */}
              <div className="mb-6 bg-white border border-emerald-500/20 rounded-xl p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-bold text-lg flex items-center gap-2 text-emerald-600">
                    <Globe className="w-5 h-5" />
                    B. Network Expansion & Onboarding
                  </h3>
                  <span className="px-3 py-1 bg-slate-100 text-emerald-600 rounded-full text-sm font-semibold">30% — $210K</span>
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="bg-white/50 rounded-lg p-4">
                    <h4 className="font-semibold mb-2">The Global Exchange</h4>
                    <p className="text-slate-600 text-sm">Scaling the team responsible for pre-vetting and credentialing the first 1,000+ regional contractors to ensure they are "healthcare-ready."</p>
                  </div>
                  <div className="bg-white/50 rounded-lg p-4">
                    <h4 className="font-semibold mb-2">Partner Subscription Model</h4>
                    <p className="text-slate-600 text-sm">Onboarding local service firms into the AskIsaac ecosystem, giving them the digital "credentials" to win business within major healthcare networks previously locked by "Big Box" monopolies.</p>
                  </div>
                </div>
              </div>

              {/* Operational Moat */}
              <div className="bg-white border border-slate-200 rounded-xl p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-bold text-lg flex items-center gap-2 text-slate-800">
                    <Shield className="w-5 h-5" />
                    C. Operational "Moat" Construction
                  </h3>
                  <span className="px-3 py-1 bg-slate-100 text-slate-800 rounded-full text-sm font-semibold">30% — $210K</span>
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="bg-white/50 rounded-lg p-4">
                    <h4 className="font-semibold mb-2">Compliance & Validation</h4>
                    <p className="text-slate-600 text-sm">Enhancing AI tools for AAMI ST108 digital validation, effectively making the software the "Standard of Truth" for hospital water and sterile processing.</p>
                  </div>
                  <div className="bg-white/50 rounded-lg p-4">
                    <h4 className="font-semibold mb-2">VA/Government Channel Support</h4>
                    <p className="text-slate-600 text-sm">Expanding administrative capacity to handle the high volume of incoming federal and VA requests through existing SDVOSB partnerships.</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Key Investment Triggers */}
            <div className="bg-white border border-violet-500/20 rounded-2xl p-8">
              <h2 className="text-2xl font-bold mb-6 flex items-center gap-3">
                <Zap className="w-6 h-6 text-blue-900" />
                2. Key Investment Triggers
              </h2>
              <div className="grid md:grid-cols-3 gap-6">
                <div className="bg-white/50 rounded-xl p-6 border border-slate-200">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-900 to-blue-900 flex items-center justify-center mb-4">
                    <Users className="w-6 h-6 text-slate-900" />
                  </div>
                  <h3 className="font-bold text-lg mb-2">The Connectivity Gap</h3>
                  <p className="text-slate-600 text-sm">Hospitals are over-paying because they aren't connected to the local talent in their own backyard. AskIsaac solves this by surfacing high-quality, pre-vetted local alternatives to failing "Big Box" distributors.</p>
                </div>
                <div className="bg-white/50 rounded-xl p-6 border border-slate-200">
                  <div className="w-12 h-12 rounded-xl bg-emerald-700 flex items-center justify-center mb-4">
                    <DollarSign className="w-6 h-6 text-slate-900" />
                  </div>
                  <h3 className="font-bold text-lg mb-2">Proven ROI</h3>
                  <p className="text-slate-600 text-sm">We've demonstrated that our data-driven approach can save a single hospital department over $1M in unnecessary equipment "over-speccing."</p>
                </div>
                <div className="bg-white/50 rounded-xl p-6 border border-slate-200">
                  <div className="w-12 h-12 rounded-xl bg-blue-900 flex items-center justify-center mb-4">
                    <TrendingUp className="w-6 h-6 text-slate-900" />
                  </div>
                  <h3 className="font-bold text-lg mb-2">High-Margin Scalability</h3>
                  <p className="text-slate-600 text-sm">AskIsaac provides the "Brains" (SaaS) and the "Marketplace" (The Network) while partners provide the physical labor, allowing rapid geographic expansion without massive overhead.</p>
                </div>
              </div>
            </div>

            {/* 24-Month Roadmap */}
            <div className="bg-white/50 border border-slate-200 rounded-2xl p-8">
              <h2 className="text-2xl font-bold mb-6 flex items-center gap-3">
                <Calendar className="w-6 h-6 text-blue-900" />
                3. The 24-Month Roadmap
              </h2>
              <div className="space-y-4">
                <div className="flex items-start gap-4 p-5 bg-gradient-to-r from-blue-500/10 to-transparent border-l-4 border-blue-500 rounded-r-xl">
                  <div className="w-20 text-center flex-shrink-0">
                    <span className="text-blue-900 font-bold text-lg">Q1-Q2</span>
                    <p className="text-slate-500 text-sm">2026</p>
                  </div>
                  <div>
                    <h3 className="font-bold text-lg mb-1">Launch "Discovery" Portal</h3>
                    <p className="text-slate-600">Onboard first 100 regional contractors in Texas and the Southeast.</p>
                  </div>
                </div>
                <div className="flex items-start gap-4 p-5 bg-gradient-to-r from-emerald-500/10 to-transparent border-l-4 border-emerald-500 rounded-r-xl">
                  <div className="w-20 text-center flex-shrink-0">
                    <span className="text-emerald-600 font-bold text-lg">Q3-Q4</span>
                    <p className="text-slate-500 text-sm">2026</p>
                  </div>
                  <div>
                    <h3 className="font-bold text-lg mb-1">Roll Out "QuickBid" Nationwide</h3>
                    <p className="text-slate-600">Deploy to VA Medical Centers; hit 500+ active supplier nodes.</p>
                  </div>
                </div>
                <div className="flex items-start gap-4 p-5 bg-gradient-to-r from-violet-500/10 to-transparent border-l-4 border-violet-500 rounded-r-xl">
                  <div className="w-20 text-center flex-shrink-0">
                    <span className="text-blue-900 font-bold text-lg">2027</span>
                    <p className="text-slate-500 text-sm">Target</p>
                  </div>
                  <div>
                    <h3 className="font-bold text-lg mb-1">Standard of Truth & Series A</h3>
                    <p className="text-slate-600">Achieve "Standard of Truth" status for AAMI ST108 compliance; target acquisition/Series A at $30M+ valuation.</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Use of Funds Visual */}
            <div>
              <h2 className="text-2xl font-bold mb-6 flex items-center gap-3">
                <PieChart className="w-6 h-6 text-emerald-600" />
                Capital Allocation Overview
              </h2>
              <div className="space-y-4">
                {(scenario.id === "northsam" ? useOfFundsPlanB : scenario.id === "ronan" ? useOfFundsPlanC : useOfFunds).map((item, idx) => (
                  <div key={idx} className="bg-white/50 border border-slate-200 rounded-xl p-5">
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <h3 className="font-semibold text-lg">{item.category}</h3>
                        <p className="text-slate-600 text-sm">{item.description}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-xl font-bold text-emerald-600">{item.amount}</p>
                        <p className="text-slate-500 text-sm">{item.percentage}%</p>
                      </div>
                    </div>
                    <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                      <div 
                        className={`h-full rounded-full ${idx === 0 ? 'bg-gradient-to-r from-blue-900 to-blue-900' : idx === 1 ? 'bg-emerald-700' : 'bg-gradient-to-r from-blue-900 to-blue-900'}`}
                        style={{ width: `${item.percentage}%` }}
                      />
                    </div>
                    {Array.isArray(item.detail) && item.detail.length > 0 && (
                      <ul className="mt-3 pl-4 space-y-1 text-xs text-slate-600 list-disc list-outside marker:text-emerald-500" data-testid={`capital-alloc-detail-${idx}`}>
                        {item.detail.map((line, di) => (
                          <li key={di}>{line}</li>
                        ))}
                      </ul>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Investor Rights */}
            <div className="bg-white/30 border border-slate-200 rounded-2xl p-8">
              <h2 className="text-2xl font-bold mb-6 flex items-center gap-3">
                <Shield className="w-6 h-6 text-blue-900" />
                Investor Rights & Reporting
              </h2>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {[
                  { title: "Quarterly Financials", description: "Full P&L, balance sheet, and cash flow statements" },
                  { title: "Monthly Updates", description: "Revenue, pipeline, and key metrics summary" },
                  { title: "Board Observer Seat", description: "For investments of $250K+ with voting consultation" },
                  { title: "Annual Meeting", description: "In-person or virtual investor presentation" },
                  { title: "Direct Access", description: "Direct line to CEO for questions and concerns" },
                  { title: "Pro-Rata Rights", description: "Option to maintain ownership in future rounds" }
                ].map((right, idx) => (
                  <div key={idx} className="flex items-start gap-3 p-4 bg-white/50 rounded-xl">
                    <CheckCircle2 className="w-5 h-5 text-blue-900 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-semibold">{right.title}</p>
                      <p className="text-slate-600 text-sm">{right.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Signature */}
            <div className="bg-white border border-slate-200 rounded-2xl p-8 text-center">
              <img src={GEORGE_IMAGE} alt="George Broughton III" className="w-24 h-24 rounded-full mx-auto mb-4 border-4 border-slate-200 object-cover" />
              <h3 className="text-2xl font-bold">George Broughton III</h3>
              <p className="text-blue-900 font-medium mb-4">Founder, AskIsaac Inc</p>
              <p className="text-sm text-slate-500">Creator of AskIsaac.io • Founder of Sterile Mate LLC</p>
              <div className="flex flex-wrap justify-center gap-4 text-slate-600">
                <a href="tel:8009883892" className="flex items-center gap-2 hover:text-slate-900 transition-colors">
                  <Phone className="w-4 h-4" />
                  800.988.3892
                </a>
                <a href="mailto:george.broughton@sterilemate.com" className="flex items-center gap-2 hover:text-slate-900 transition-colors">
                  <Mail className="w-4 h-4" />
                  george.broughton@sterilemate.com
                </a>
              </div>
            </div>
          </div>
        )}

        {/* LEGAL & CAP TABLE SECTION */}
        {activeSection === "loan" && (
          <div className="space-y-10 animate-fade-in">
            <div className="text-center mb-12">
              <h1 className="text-4xl font-bold mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Legal Structure & Cap Table
              </h1>
              <p className="text-xl text-slate-600">Corporate structure and ownership transparency</p>
            </div>

            {/* Entity Structure */}
            <div className="bg-white/50 border border-slate-200 rounded-2xl p-8">
              <h2 className="text-2xl font-bold mb-6 flex items-center gap-3">
                <Building2 className="w-6 h-6 text-blue-900" />
                Corporate Entity Structure
              </h2>
              
              <div className="grid md:grid-cols-2 gap-8">
                <div className="space-y-4">
                  <div className="bg-blue-500/10 border border-slate-200 rounded-xl p-5">
                    <h3 className="font-bold text-blue-900 mb-3">AskIsaac Inc.</h3>
                    <ul className="space-y-2 text-slate-700 text-sm">
                      <li className="flex items-start gap-2">
                        <CheckCircle2 className="w-4 h-4 text-blue-900 flex-shrink-0 mt-0.5" />
                        <span><strong>Entity Type:</strong> Texas Corporation</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle2 className="w-4 h-4 text-blue-900 flex-shrink-0 mt-0.5" />
                        <span><strong>Incorporated:</strong> 2026 (spun from Sterile Mate LLC)</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle2 className="w-4 h-4 text-blue-900 flex-shrink-0 mt-0.5" />
                        <span><strong>Primary Business:</strong> Healthcare SaaS platform & marketplace</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle2 className="w-4 h-4 text-blue-900 flex-shrink-0 mt-0.5" />
                        <span><strong>State:</strong> Texas</span>
                      </li>
                    </ul>
                  </div>
                  
                  <div className="bg-emerald-500/10 border border-slate-200 rounded-xl p-5">
                    <h3 className="font-bold text-emerald-600 mb-3">Sterile Mate LLC (Operating Company)</h3>
                    <ul className="space-y-2 text-slate-700 text-sm">
                      <li className="flex items-start gap-2">
                        <CheckCircle2 className="w-4 h-4 text-emerald-600 flex-shrink-0 mt-0.5" />
                        <span><strong>Entity Type:</strong> Texas Limited Liability Company</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle2 className="w-4 h-4 text-emerald-600 flex-shrink-0 mt-0.5" />
                        <span><strong>Founded:</strong> 2020</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle2 className="w-4 h-4 text-emerald-600 flex-shrink-0 mt-0.5" />
                        <span><strong>Relationship:</strong> Wholly-owned subsidiary / service arm</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle2 className="w-4 h-4 text-emerald-600 flex-shrink-0 mt-0.5" />
                        <span><strong>Revenue History:</strong> $3.9M+ cumulative (2020-2025)</span>
                      </li>
                    </ul>
                  </div>
                </div>
                
                <div className="space-y-4">
                  <div className="bg-slate-50 rounded-xl p-5">
                    <h3 className="font-bold text-slate-900 mb-4">Registered Agent & HQ</h3>
                    <div className="space-y-3 text-slate-700 text-sm">
                      <div className="flex items-start gap-2">
                        <MapPin className="w-4 h-4 text-slate-600 mt-0.5" />
                        <div>
                          <p className="font-semibold">Corporate Address</p>
                          <p className="text-slate-600">396 W Greens Road, Suite 1139</p>
                          <p className="text-slate-600">Houston, TX 77067</p>
                        </div>
                      </div>
                      <div className="flex items-start gap-2">
                        <Shield className="w-4 h-4 text-slate-600 mt-0.5" />
                        <div>
                          <p className="font-semibold">Texas Registered Agent</p>
                          <p className="text-slate-600">On file with TX Secretary of State</p>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="bg-slate-100 border border-slate-200 rounded-xl p-5">
                    <h3 className="font-bold text-slate-800 mb-3">Certifications & Compliance</h3>
                    <ul className="space-y-2 text-slate-700 text-sm">
                      <li className="flex items-center gap-2">
                        <FileCheck className="w-4 h-4 text-slate-800" />
                        <span>HIPAA-compliant infrastructure</span>
                      </li>
                      <li className="flex items-center gap-2">
                        <FileCheck className="w-4 h-4 text-slate-800" />
                        <span>SOC 2 Type II (planned 2026)</span>
                      </li>
                      <li className="flex items-center gap-2">
                        <FileCheck className="w-4 h-4 text-slate-800" />
                        <span>Texas contractor licensing</span>
                      </li>
                      <li className="flex items-center gap-2">
                        <FileCheck className="w-4 h-4 text-slate-800" />
                        <span>General liability & E&O insurance</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            {/* Cap Table */}
            <div className="bg-white border border-slate-200 rounded-2xl p-8">
              <h2 className="text-2xl font-bold mb-6 flex items-center gap-3">
                <PieChart className="w-6 h-6 text-blue-900" />
                Cap Table (Pre-Investment)
              </h2>
              
              <div className="grid md:grid-cols-2 gap-8">
                <div>
                  <h3 className="font-bold text-lg text-blue-900 mb-4">Current Ownership</h3>
                  <div className="bg-white/50 rounded-xl p-5">
                    <div className="space-y-4">
                      <div className="flex items-center justify-between p-3 bg-violet-500/10 border border-slate-200 rounded-lg">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-violet-500 flex items-center justify-center">
                            <Users className="w-5 h-5 text-slate-900" />
                          </div>
                          <div>
                            <p className="font-semibold text-slate-900">George Broughton III</p>
                            <p className="text-slate-600 text-sm">Founder & CEO</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-2xl font-bold text-blue-900">100%</p>
                          <p className="text-slate-500 text-sm">Common Stock</p>
                        </div>
                      </div>
                    </div>
                    
                    <div className="mt-4 pt-4 border-t border-slate-200">
                      <div className="flex justify-between text-sm">
                        <span className="text-slate-600">Total Authorized Shares:</span>
                        <span className="text-slate-900">10,000,000</span>
                      </div>
                      <div className="flex justify-between text-sm mt-2">
                        <span className="text-slate-600">Shares Outstanding:</span>
                        <span className="text-slate-900">8,000,000</span>
                      </div>
                      <div className="flex justify-between text-sm mt-2">
                        <span className="text-slate-600">Option Pool Reserved:</span>
                        <span className="text-slate-900">2,000,000 (20%)</span>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div>
                  <h3 className="font-bold text-lg text-emerald-600 mb-4">Ownership Structure</h3>
                  <div className="bg-white/50 rounded-xl p-5">
                    <p className="text-slate-600 text-sm mb-4">SBA 7(a) does not dilute equity &mdash; owner retains full ownership:</p>
                    <div className="space-y-3">
                      <div className="flex items-center justify-between py-2 border-b border-slate-200">
                        <span className="text-slate-900">George Broughton III (Founder)</span>
                        <span className="text-blue-900 font-semibold">100%</span>
                      </div>
                    </div>
                    
                    <div className="mt-4 pt-4 border-t border-slate-200">
                      <div className="flex justify-between text-sm">
                        <span className="text-slate-600">Total Project Cost:</span>
                        <span className="text-emerald-600 font-semibold">{scenario.askAmount}</span>
                      </div>
                      <div className="flex justify-between text-sm mt-2">
                        <span className="text-slate-600">Equity Injection (~10%):</span>
                        <span className="text-emerald-600 font-semibold">{scenario.equityInjectionAmount}</span>
                      </div>
                      <div className="flex justify-between text-sm mt-2">
                        <span className="text-slate-600">SBA Loan (~90%):</span>
                        <span className="text-blue-900 font-semibold">{scenario.sbaLoanAmount}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Key Agreements & IP */}
            <div className="bg-white/50 border border-slate-200 rounded-2xl p-8">
              <h2 className="text-2xl font-bold mb-6 flex items-center gap-3">
                <FileText className="w-6 h-6 text-blue-900" />
                Key Agreements & Intellectual Property
              </h2>
              
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <h3 className="font-bold text-lg text-blue-900 mb-4">Material Contracts</h3>
                  <div className="space-y-3">
                    {[
                      { 
                        name: "Water Purification Solutions", 
                        type: "Exclusive Distribution",
                        status: "Active since 2022",
                        term: "Multi-year"
                      },
                      { 
                        name: "Automated Backorder Sourcing (AskIsaac)", 
                        type: "Flagship Revenue Line",
                        status: "6-yr benchmark, now automated",
                        term: "Scaling regional → statewide"
                      },
                      { 
                        name: "Major Public Health System Beta", 
                        type: "Platform License",
                        status: "Live Beta",
                        term: "Converting to paid"
                      },
                      { 
                        name: "VA Healthcare Contracts", 
                        type: "Service Contracts",
                        status: "Active pipeline",
                        term: "Project-based"
                      }
                    ].map((contract, idx) => (
                      <div key={idx} className="bg-slate-50 rounded-lg p-4">
                        <div className="flex items-center justify-between mb-2">
                          <h4 className="font-semibold text-slate-900">{contract.name}</h4>
                          <span className="px-2 py-1 bg-slate-100 text-emerald-600 rounded text-xs">{contract.status}</span>
                        </div>
                        <div className="flex items-center gap-4 text-sm">
                          <span className="text-slate-600">{contract.type}</span>
                          <span className="text-slate-500">|</span>
                          <span className="text-slate-600">{contract.term}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                
                <div>
                  <h3 className="font-bold text-lg text-slate-800 mb-4">Intellectual Property</h3>
                  <div className="space-y-3">
                    {[
                      { 
                        name: "AskIsaac Platform", 
                        type: "Proprietary Software",
                        protection: "Trade Secret + Copyright",
                        status: "Protected"
                      },
                      { 
                        name: "AskIsaac Trademark", 
                        type: "Brand Name",
                        protection: "Trademark Application",
                        status: "Pending"
                      },
                      { 
                        name: "Isaac Bot", 
                        type: "AI/Chat Interface",
                        protection: "Trade Secret",
                        status: "Protected"
                      },
                      { 
                        name: "QuickBid Algorithm", 
                        type: "Bidding Engine",
                        protection: "Trade Secret",
                        status: "Protected"
                      },
                      { 
                        name: "Bleu Flux Brand", 
                        type: "Product Brand",
                        protection: "Trademark Application",
                        status: "Pending"
                      }
                    ].map((ip, idx) => (
                      <div key={idx} className="bg-slate-50 rounded-lg p-4">
                        <div className="flex items-center justify-between mb-2">
                          <h4 className="font-semibold text-slate-900">{ip.name}</h4>
                          <span className={`px-2 py-1 rounded text-xs ${
                            ip.status === 'Protected' ? 'bg-slate-100 text-emerald-600' : 'bg-slate-100 text-slate-800'
                          }`}>{ip.status}</span>
                        </div>
                        <div className="flex items-center gap-4 text-sm">
                          <span className="text-slate-600">{ip.type}</span>
                          <span className="text-slate-500">|</span>
                          <span className="text-slate-600">{ip.protection}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Existing Debt & Liabilities */}
            <div className="bg-white border border-slate-200 rounded-2xl p-8">
              <h2 className="text-2xl font-bold mb-6 flex items-center gap-3">
                <Shield className="w-6 h-6 text-emerald-600" />
                Debt & Liabilities
              </h2>
              
              <div className="bg-emerald-500/10 border border-slate-200 rounded-xl p-6 text-center">
                <CheckCircle2 className="w-12 h-12 text-emerald-600 mx-auto mb-4" />
                <h3 className="text-xl font-bold text-emerald-600 mb-2">Clean Balance Sheet</h3>
                <p className="text-slate-700">No outstanding debt, convertible notes, or prior investor obligations</p>
                <p className="text-slate-600 text-sm mt-2">Sterile Mate LLC has operated profitably without external financing</p>
              </div>
              
              <div className="grid md:grid-cols-3 gap-4 mt-6">
                <div className="bg-slate-50 rounded-lg p-4 text-center">
                  <p className="text-slate-600 text-sm">Long-term Debt</p>
                  <p className="text-2xl font-bold text-emerald-600">$0</p>
                </div>
                <div className="bg-slate-50 rounded-lg p-4 text-center">
                  <p className="text-slate-600 text-sm">Convertible Notes</p>
                  <p className="text-2xl font-bold text-emerald-600">$0</p>
                </div>
                <div className="bg-slate-50 rounded-lg p-4 text-center">
                  <p className="text-slate-600 text-sm">Prior Investors</p>
                  <p className="text-2xl font-bold text-emerald-600">None</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Risks Section */}
        {activeSection === "loan" && (
          <div className="space-y-10 animate-fade-in">
            <div className="text-center mb-12">
              <h1 className="text-4xl font-bold mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Risk Factors
              </h1>
              <p className="text-xl text-slate-600">Transparency builds trust — here's what we're watching</p>
            </div>

            {/* Risk Cards */}
            <div className="space-y-4">
              {riskFactors.map((item, idx) => (
                <div key={idx} className="bg-white/50 border border-slate-200 rounded-2xl p-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-xl bg-slate-100 flex items-center justify-center flex-shrink-0">
                      <AlertTriangle className="w-6 h-6 text-slate-800" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-bold text-lg mb-2">{item.risk}</h3>
                      <p className="text-slate-600 mb-4">{item.description}</p>
                      <div className="flex items-center gap-2 p-3 bg-emerald-500/10 rounded-lg border border-emerald-500/20">
                        <Shield className="w-5 h-5 text-emerald-600" />
                        <div>
                          <p className="text-sm text-emerald-600 font-medium">Mitigation Strategy</p>
                          <p className="text-slate-700 text-sm">{item.mitigation}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* General Disclaimer */}
            <div className="bg-white/30 border border-slate-200 rounded-2xl p-6 text-center">
              <p className="text-slate-600 text-sm">
                <strong className="text-slate-700">Investment Disclaimer:</strong> This investment involves risk. Past performance does not guarantee future results. 
                Investors should conduct their own due diligence and consult with financial advisors before making investment decisions.
              </p>
            </div>
          </div>
        )}


        {/* FAQ Section */}
        {activeSection === "faq" && (
          <div className="space-y-10 animate-fade-in">
            <div className="text-center mb-12">
              <h1 className="text-4xl font-bold mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Frequently Asked Questions
              </h1>
              <p className="text-xl text-slate-600">Common questions from investors</p>
            </div>

            {/* FAQ Accordion */}
            <div className="space-y-4 max-w-3xl mx-auto">
              {faqData.map((faq, idx) => {
                // First FAQ entry — investment amount/structure — switches with active scenario
                const dynamicAnswer =
                  idx === 0
                    ? scenario.id === "humble"
                      ? "The total project cost is $3,415,000 via SBA 7(a) financing, covering building acquisition ($2.1M), renovations ($150K), equipment ($115K), inventory ($100K), and 12-month working capital ($950K including $120K marketing budget). Equity injection is approximately $340K (10%)."
                      : scenario.id === "ronan"
                        ? "The total project cost is $4,490,000 — financed as $3.456M via SBA 504 plus a $650K revolving LOC that funds both operating capital and the initial consumables inventory ($150K to seed 1 container of 4-mil gloves, 1 container of 3.5-mil gloves, and 3,000 Sterile Mate microfiber boxes). The SBA 504 loan covers the 22,750 SF Class-A 3-story HQ on 5.08 acres at 16651 Ronan Rd, Houston ($3.5M acquisition), $160K renovation & capital improvements for the 2nd-level BWS/MEP/RAS build-out, and $180K for Sterile Mate\u2019s first statewide B2B marketing launch. Equity injection is approximately $384K (10% of the SBA $3.456M). Plan C is owner-occupied — seven diversified operating revenue streams anchored by Backorder Sourcing (Sterile Mate\u2019s 6-year benchmark and door-opener into every Texas hospital system, now automated by AskIsaac and scaling from $1.2M Y1 to $3.2M Y3), plus Water Purification & Healthcare Capital Projects, Medical Device Sales, Healthcare Construction, Private-Label Exam Gloves direct-import, Private-Label Microfiber, and Specialized BioBurden Cleaning — drive $4.54M Y1 → $7.59M Y2 → $11.64M Y3, with a Y2 DSCR of 6.71x — substantially above SBA 504 1.15x–1.25x minimums."
                        : "The total project cost is $4,700,000 via SBA 7(a) financing, covering acquisition of the 87,282 SF \u201C2SIXTY3\u201D Class-B campus at 263 N Sam Houston Pkwy E ($3.4M), renovation & capital improvements ($800K), and operating capital / working reserves ($500K). Equity injection is approximately $470K (10%). Plan B underwrites $300K–$450K of multi-tenant rental income on the surplus floor area."
                    : faq.answer;
                return (
                  <div
                    key={idx}
                    className="bg-white/50 border border-slate-200 rounded-xl overflow-hidden"
                  >
                    <button
                      onClick={() => setExpandedFaq(expandedFaq === idx ? null : idx)}
                      className="w-full flex items-center justify-between p-5 text-left hover:bg-slate-50 transition-colors"
                    >
                      <span className="font-semibold pr-4">{faq.question}</span>
                      <ChevronRight className={`w-5 h-5 text-slate-600 flex-shrink-0 transition-transform ${expandedFaq === idx ? 'rotate-90' : ''}`} />
                    </button>
                    {expandedFaq === idx && (
                      <div className="px-5 pb-5 pt-0">
                        <p className="text-slate-600 leading-relaxed">{dynamicAnswer}</p>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* More Questions CTA */}
            <div className="text-center bg-white/30 border border-slate-200 rounded-2xl p-8">
              <HelpCircle className="w-12 h-12 text-blue-900 mx-auto mb-4" />
              <h3 className="text-xl font-bold mb-2">Have More Questions?</h3>
              <p className="text-slate-600 mb-4">We're happy to answer any questions about the investment opportunity.</p>
              <button
                onClick={() => setActiveSection('contact')}
                className="inline-flex items-center gap-2 px-6 py-3 bg-blue-900 text-white font-semibold rounded-xl hover:from-blue-700 hover:to-cyan-600 transition-all"
              >
                <MessageSquare className="w-4 h-4" />
                Contact Us
              </button>
            </div>
          </div>
        )}

        {/* Contact Section */}
        {activeSection === "contact" && (
          <div className="space-y-10 animate-fade-in">
            <div className="text-center mb-12">
              <h1 className="text-4xl font-bold mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Let&apos;s Connect
              </h1>
              <p className="text-xl text-slate-600">Schedule a call or send us a message</p>
            </div>

            <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
              {/* Contact Form */}
              <div className="bg-white/50 border border-slate-200 rounded-2xl p-8">
                <h2 className="text-xl font-bold mb-6 flex items-center gap-2">
                  <MessageSquare className="w-5 h-5 text-blue-900" />
                  Send a Message
                </h2>
                <form onSubmit={handleContactSubmit} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Your Name</label>
                    <Input
                      type="text"
                      value={contactForm.name}
                      onChange={(e) => setContactForm({...contactForm, name: e.target.value})}
                      className="w-full bg-white border-slate-200 text-slate-900"
                      placeholder="John Smith"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Email Address</label>
                    <Input
                      type="email"
                      value={contactForm.email}
                      onChange={(e) => setContactForm({...contactForm, email: e.target.value})}
                      className="w-full bg-white border-slate-200 text-slate-900"
                      placeholder="john@company.com"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Company / Fund</label>
                    <Input
                      type="text"
                      value={contactForm.company}
                      onChange={(e) => setContactForm({...contactForm, company: e.target.value})}
                      className="w-full bg-white border-slate-200 text-slate-900"
                      placeholder="Acme Ventures"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Message</label>
                    <Textarea
                      value={contactForm.message}
                      onChange={(e) => setContactForm({...contactForm, message: e.target.value})}
                      className="w-full bg-white border-slate-200 text-slate-900 min-h-[120px]"
                      placeholder="I&apos;m interested in learning more about the investment opportunity..."
                      required
                    />
                  </div>
                  <Button
                    type="submit"
                    disabled={formSubmitting}
                    className="w-full py-3 bg-blue-900 hover:from-blue-700 hover:to-cyan-600 text-white font-semibold rounded-xl"
                  >
                    {formSubmitting ? (
                      <span className="flex items-center gap-2">
                        <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        Sending...
                      </span>
                    ) : (
                      <span className="flex items-center gap-2">
                        <Send className="w-4 h-4" />
                        Send Message
                      </span>
                    )}
                  </Button>
                </form>
              </div>

              {/* Direct Contact Info */}
              <div className="space-y-6">
                <div className="bg-white/50 border border-slate-200 rounded-2xl p-8">
                  <h2 className="text-xl font-bold mb-6 flex items-center gap-2">
                    <Users className="w-5 h-5 text-emerald-600" />
                    Direct Contact
                  </h2>
                  <div className="flex items-center gap-4 mb-6">
                    <img 
                      src={GEORGE_IMAGE} 
                      alt="George Broughton III"
                      className="w-16 h-16 rounded-xl object-cover"
                    />
                    <div>
                      <h3 className="font-bold text-lg">George Broughton III</h3>
                      <p className="text-slate-600">Founder, AskIsaac Inc</p>
                    </div>
                  </div>
                  <div className="space-y-3">
                    <a 
                      href="mailto:george.broughton@sterilemate.com"
                      className="flex items-center gap-3 p-3 bg-white rounded-lg hover:bg-slate-100 transition-colors"
                    >
                      <Mail className="w-5 h-5 text-blue-900" />
                      <span>george.broughton@sterilemate.com</span>
                    </a>
                    <a 
                      href="tel:+18009883892"
                      className="flex items-center gap-3 p-3 bg-white rounded-lg hover:bg-slate-100 transition-colors"
                    >
                      <Phone className="w-5 h-5 text-emerald-600" />
                      <span>800.988.3892</span>
                    </a>
                    <div className="flex items-center gap-3 p-3 bg-white rounded-lg">
                      <MapPin className="w-5 h-5 text-slate-800" />
                      <span className="text-slate-600">396 W Greens Road, Suite 1139<br/>Houston, TX 77067</span>
                    </div>
                  </div>
                </div>

                {/* Testimonials */}
                <div className="bg-white border border-blue-500/20 rounded-2xl p-6">
                  <Quote className="w-8 h-8 text-blue-900 mb-4" />
                  <p className="text-slate-700 italic mb-4">
                    "{testimonials[0].quote}"
                  </p>
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full bg-blue-400" />
                    <span className="font-semibold text-sm">{testimonials[0].author}</span>
                    <span className="text-slate-500 text-sm">— {testimonials[0].role}</span>
                  </div>
                </div>

                {/* Download Pitch Deck */}
                <div className="bg-white/50 border border-slate-200 rounded-2xl p-6">
                  <h3 className="font-bold mb-3 flex items-center gap-2">
                    <FileText className="w-5 h-5 text-blue-900" />
                    Pitch Deck & Materials
                  </h3>
                  <p className="text-slate-600 text-sm mb-4">
                    Download our investor pitch deck and sales materials.
                  </p>
                  <div className="space-y-3">
                    <a
                      href="/pitch-deck"
                      target="_blank"
                      className="w-full inline-flex items-center justify-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-900 to-blue-900 text-slate-900 font-semibold rounded-xl hover:from-emerald-700 hover:to-teal-700 transition-all"
                      data-testid="view-pitch-deck"
                    >
                      <Eye className="w-4 h-4" />
                      View Pitch Deck
                    </a>
                    <a
                      href="/sales-one-pager"
                      target="_blank"
                      className="w-full inline-flex items-center justify-center gap-2 px-4 py-2 bg-blue-900 text-white font-semibold rounded-xl hover:from-cyan-700 hover:to-blue-700 transition-all"
                    >
                      <Download className="w-4 h-4" />
                      Sales One-Pager (PDF)
                    </a>
                    <a
                      href="https://customer-assets.emergentagent.com/job_service-widgets/artifacts/std19ze8_2025%20Capabilities%20Brochure%201.12.2025.pdf"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-full inline-flex items-center justify-center gap-2 px-4 py-2 bg-blue-900 text-white font-semibold rounded-xl hover:from-violet-700 hover:to-purple-700 transition-all"
                    >
                      <Download className="w-4 h-4" />
                      Capabilities Brochure (PDF)
                    </a>
                    <a
                      href="/executive-letter"
                      target="_blank"
                      className="w-full inline-flex items-center justify-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-900 to-blue-900 text-slate-900 font-semibold rounded-xl hover:from-amber-700 hover:to-orange-700 transition-all"
                      data-testid="executive-letter-link"
                    >
                      <FileText className="w-4 h-4" />
                      Hospital Executive Letter
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Capabilities Section */}
        {activeSection === "contact" && (
          <div className="space-y-10 animate-fade-in">
            <div className="text-center mb-12">
              <h1 className="text-4xl font-bold mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Capabilities & Resources
              </h1>
              <p className="text-xl text-slate-600">Download our detailed capabilities brochures</p>
            </div>

            {/* Download Cards */}
            <div className="grid md:grid-cols-3 gap-6">
              {capabilitiesDocs.map((doc, idx) => (
                <a
                  key={idx}
                  href={doc.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group bg-white/50 border border-slate-200 rounded-2xl p-6 hover:border-blue-500/50 hover:bg-white transition-all"
                  data-testid={`capability-doc-${idx}`}
                >
                  <div className="w-16 h-16 rounded-2xl bg-white border border-slate-200 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                    <FileText className="w-8 h-8 text-blue-900" />
                  </div>
                  <h3 className="font-semibold text-lg mb-2 group-hover:text-blue-900 transition-colors">{doc.name}</h3>
                  <p className="text-slate-600 text-sm mb-4">{doc.description}</p>
                  <div className="flex items-center gap-2 text-blue-900 font-medium">
                    <Download className="w-4 h-4" />
                    Download PDF
                    <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </div>
                </a>
              ))}
            </div>

            {/* Certifications */}
            <div className="bg-white/50 border border-slate-200 rounded-2xl p-8">
              <h2 className="text-2xl font-bold mb-6 flex items-center gap-3">
                <ShieldCheck className="w-6 h-6 text-emerald-600" />
                Certifications & Accreditations
              </h2>
              <div className="grid md:grid-cols-3 gap-4">
                {[
                  { title: "Minority-Owned Business", description: "Certified MBE status" },
                  { title: "ISO/IEC 17025:2017", description: "Laboratory accreditation" },
                  { title: "TCEQ Approved", description: "Texas Commission on Environmental Quality" }
                ].map((cert, idx) => (
                  <div key={idx} className="text-center p-4 bg-white/50 rounded-xl border border-slate-200">
                    <Award className="w-8 h-8 text-slate-800 mx-auto mb-2" />
                    <p className="font-semibold">{cert.title}</p>
                    <p className="text-xs text-slate-600">{cert.description}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Strategic Recommendations */}
            <div className="bg-white border border-slate-200 rounded-2xl p-8">
              <h2 className="text-2xl font-bold mb-6 flex items-center gap-3">
                <Target className="w-6 h-6 text-blue-900" />
                Strategic Priorities
              </h2>
              <div className="grid md:grid-cols-2 gap-4">
                {[
                  "Close SBA 7(a) financing for HQ acquisition",
                  "Complete facility buildout (showroom, training, warehouse)",
                  "Launch The Supply Doctor retail storefront",
                  "Execute $791K Harris Health pipeline within 90 days",
                  "Activate $10K/mo marketing engine (Google Ads, billboards, trade shows)",
                  "Phase II: DME depot + Medicare/insurance billing",
                  "Scale AskIsaac SaaS platform licensing"
                ].map((rec, idx) => (
                  <div key={idx} className="flex items-start gap-3 p-3 bg-white/50 rounded-xl">
                    <div className="w-6 h-6 rounded-full bg-slate-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                    </div>
                    <span className="text-slate-700">{rec}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </main>

      {/* Footer */}
      <footer className="relative z-10 border-t border-slate-200 bg-slate-100 backdrop-blur-lg mt-20">
        <div className="max-w-7xl mx-auto px-6 py-8 text-center text-slate-500 text-sm">
          <p>© 2026 AskIsaac Inc. All rights reserved. Confidential Investor Materials.</p>
          <p className="text-xs text-slate-500 mt-1">Created by George Broughton III • © 2026 AskIsaac Inc.</p>
          <p className="mt-2">396 W Greens Road, Suite 1139, Houston, TX 77067</p>
        </div>
      </footer>

      {/* Demo Presentation Modal */}
      <EnhancedDemoPresentation open={showDemo} onClose={() => setShowDemo(false)} />
    </div>
  );
}
